import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import {
  RefreshCw, Trash2, Clock, AlertTriangle, Copy, Archive,
  ChevronDown, ChevronRight, Eye, ExternalLink, Shield,
  FileQuestion, Film, Image as ImageIcon, X,
  Unlink, ScatterChart, Search, LinkIcon, ShieldAlert, Activity,
  Download, Play, CheckCircle, XCircle, SkipForward, AlertOctagon,
  FileText, Loader, Database, ArrowRightLeft, Info,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Risk Colors ──────────────────────────────────────────────────────────

const RISK_COLORS = {
  low: { bg: '#052e16', border: '#166534', text: '#4ade80', label: 'LOW' },
  medium: { bg: '#1c1917', border: '#854d0e', text: '#fbbf24', label: 'MED' },
  high: { bg: '#1c0a0a', border: '#991b1b', text: '#f87171', label: 'HIGH' },
  blocked: { bg: '#0a0a0a', border: '#333', text: '#525252', label: 'BLOCKED' },
};

const CATEGORY_META = {
  stale_uploads: {
    label: 'Stale Uploads',
    icon: Clock,
    color: '#f59e0b',
    description: 'Upload requests that were never confirmed (past stale threshold)',
  },
  expired_renders: {
    label: 'Expired Renders',
    icon: Film,
    color: '#ec4899',
    description: 'TTL-expired render outputs that were not explicitly invalidated',
  },
  invalidated_renders: {
    label: 'Invalidated Renders',
    icon: Film,
    color: '#e879f9',
    description: 'Render outputs invalidated by slide/carousel changes, including those also past TTL',
  },
  orphaned_assets: {
    label: 'Orphaned Assets',
    icon: AlertTriangle,
    color: '#ef4444',
    description: 'Confirmed assets with orphaned_at set — may have zero references',
  },
  soft_deleted_with_s3: {
    label: 'Soft-Deleted with S3',
    icon: Archive,
    color: '#8b5cf6',
    description: 'Logically deleted assets whose S3 objects may still exist',
  },
  duplicate_groups: {
    label: 'Duplicate Groups',
    icon: Copy,
    color: '#06b6d4',
    description: 'Same-user, same-content groups with multiple copies',
  },
  unused_confirmed_assets: {
    label: 'Unused Confirmed',
    icon: Unlink,
    color: '#f97316',
    description: 'Confirmed & tracked, but not found in any live carousel. Reachability audit only.',
  },
  possible_duplicate_size_groups: {
    label: 'Possible Duplicates (Size)',
    icon: ScatterChart,
    color: '#a855f7',
    description: 'Same owner/kind/type/byte-size groups — UNVERIFIED, requires hash comparison.',
  },
  stale_asset_references: {
    label: 'Stale References',
    icon: LinkIcon,
    color: '#fb923c',
    description: 'asset_references rows exist but carousel JSONB no longer contains the asset. Suspected stale.',
  },
  missing_asset_references: {
    label: 'Missing References',
    icon: Search,
    color: '#38bdf8',
    description: 'Carousel JSONB contains asset but no asset_references row exists. Reference mismatch.',
  },
  referenced_deleted_assets: {
    label: 'Deleted but Live',
    icon: ShieldAlert,
    color: '#ef4444',
    description: 'LIVE DEPENDENCY: Asset has deleted_at set but is still used in current carousel JSONB. NOT a cleanup candidate.',
  },
  verified_duplicates: {
    label: 'Verified Duplicates',
    icon: Copy,
    color: '#c084fc',
    description: 'Hash-verified excess copies. Only unreferenced duplicates are eligible for deletion.',
  },
  legacy_unused_confirmed: {
    label: 'Legacy Unused Confirmed',
    icon: AlertOctagon,
    color: '#dc2626',
    description: 'Confirmed and tracked, but not referenced by any live carousel, asset_references, render_jobs, or backups. Legacy/manual/irreversible cleanup.',
    isLegacy: true,
  },
};

// ── Main Component ───────────────────────────────────────────────────────

export default function AdminCleanup() {
  const [password, setPassword] = useState(() => sessionStorage.getItem('admin_password') || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [previewKey, setPreviewKey] = useState(null);
  const [usageModal, setUsageModal] = useState(null); // { asset_id, storage_key }
  const [dryRun, setDryRun] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);

  // ── PR 8A.6a: Manifest/Staged Cleanup State ──
  const [activeTab, setActiveTab] = useState('diagnostics'); // diagnostics | staged
  const [manifest, setManifest] = useState(null);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [manifestError, setManifestError] = useState(null);
  const [manifestCategory, setManifestCategory] = useState('all');
  const [manifestMinAge, setManifestMinAge] = useState(0);
  const [manifestIncludeHigh, setManifestIncludeHigh] = useState(true);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [confirmModal, setConfirmModal] = useState(null); // { expectedConfirmation, items }
  const [confirmText, setConfirmText] = useState('');
  const [executing, setExecuting] = useState(false);
  const [execResult, setExecResult] = useState(null);
  const [manifestHistory, setManifestHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [manifestDetail, setManifestDetail] = useState(null);
  const [manifestDetailLoading, setManifestDetailLoading] = useState(false);
  const [dupVerification, setDupVerification] = useState(null);
  const [dupVerifyLoading, setDupVerifyLoading] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);

  // ── Anonymous Cleanup State ──
  const [anonData, setAnonData] = useState(null);
  const [anonLoading, setAnonLoading] = useState(false);
  const [anonError, setAnonError] = useState(null);
  // Empty cleanup
  const [anonEmptyResult, setAnonEmptyResult] = useState(null);
  const [anonEmptyModal, setAnonEmptyModal] = useState(false);
  const [anonEmptyText, setAnonEmptyText] = useState('');
  const [anonEmptyApplying, setAnonEmptyApplying] = useState(false);
  // Demo-only cleanup
  const [anonDemoResult, setAnonDemoResult] = useState(null);
  const [anonDemoModal, setAnonDemoModal] = useState(false);
  const [anonDemoText, setAnonDemoText] = useState('');
  const [anonDemoApplying, setAnonDemoApplying] = useState(false);
  // Content cleanup (separate)
  const [anonContentResult, setAnonContentResult] = useState(null);
  const [anonContentModal, setAnonContentModal] = useState(false);
  const [anonContentText, setAnonContentText] = useState('');
  const [anonContentApplying, setAnonContentApplying] = useState(false);

  const headers = useCallback(() => ({
    'X-Admin-Password': password,
    'Content-Type': 'application/json',
  }), [password]);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/cleanup/candidates`, {
        headers: headers(),
      });
      if (resp.status === 401) {
        setAuthenticated(false);
        throw new Error('Invalid password');
      }
      if (resp.status === 503) {
        const d = await resp.json();
        if (d.error === 'schema_missing') {
          setError('Database schema v010 not applied. Go to Admin > Analytics > Migration Gate.');
          setAuthenticated(true);
          return;
        }
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const d = await resp.json();
      setData(d);
      setAuthenticated(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const fetchCoverage = useCallback(async () => {
    setCoverageLoading(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/cleanup/coverage`, { headers: headers() });
      if (resp.ok) {
        const d = await resp.json();
        setCoverage(d);
      }
    } catch (_) { /* ignore */ }
    setCoverageLoading(false);
  }, [headers]);

  const fetchDryRun = useCallback(async () => {
    setDryRunLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/storage/reference-rebuild-dryrun`, { headers: headers() });
      if (res.ok) setDryRun(await res.json());
    } catch (_) { /* ignore */ }
    setDryRunLoading(false);
  }, [headers]);

  // ── Anonymous Cleanup Functions ──
  const fetchAnonDryRun = useCallback(async () => {
    setAnonLoading(true);
    setAnonError(null);
    try {
      const url = `${BACKEND_URL}/api/admin/cleanup/anonymous/dry-run`;
      const resp = await fetch(url, { headers: headers() });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const d = await resp.json();
      setAnonData(d);
    } catch (e) {
      setAnonError(e.message);
    }
    setAnonLoading(false);
  }, [headers]);

  const applyAnonEmpty = useCallback(async () => {
    setAnonEmptyApplying(true);
    setAnonError(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/cleanup/anonymous/apply-empty`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          confirmation: 'APPLY ANONYMOUS CLEANUP',
          limit: 50,
        }),
      });
      if (!resp.ok) {
        const d = await resp.json();
        throw new Error(d.detail || `HTTP ${resp.status}`);
      }
      const d = await resp.json();
      setAnonEmptyResult(d);
      setAnonEmptyModal(false);
      setAnonEmptyText('');
      fetchAnonDryRun();
    } catch (e) {
      setAnonError(e.message);
    }
    setAnonEmptyApplying(false);
  }, [headers, fetchAnonDryRun]);

  const applyAnonDemo = useCallback(async () => {
    setAnonDemoApplying(true);
    setAnonError(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/cleanup/anonymous/apply-demo`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          confirmation: 'APPLY DEMO CLEANUP',
          limit: 50,
        }),
      });
      if (!resp.ok) {
        const d = await resp.json();
        throw new Error(d.detail || `HTTP ${resp.status}`);
      }
      const d = await resp.json();
      setAnonDemoResult(d);
      setAnonDemoModal(false);
      setAnonDemoText('');
      fetchAnonDryRun();
    } catch (e) {
      setAnonError(e.message);
    }
    setAnonDemoApplying(false);
  }, [headers, fetchAnonDryRun]);

  const applyAnonContent = useCallback(async () => {
    setAnonContentApplying(true);
    setAnonError(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/cleanup/anonymous/apply-content`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          confirmation: 'APPLY ANONYMOUS CONTENT CLEANUP',
          limit: 20,
        }),
      });
      if (!resp.ok) {
        const d = await resp.json();
        throw new Error(d.detail || `HTTP ${resp.status}`);
      }
      const d = await resp.json();
      setAnonContentResult(d);
      setAnonContentModal(false);
      setAnonContentText('');
      fetchAnonDryRun();
    } catch (e) {
      setAnonError(e.message);
    }
    setAnonContentApplying(false);
  }, [headers, fetchAnonDryRun]);

  const handleLogin = async (e) => {
    e.preventDefault();
    sessionStorage.setItem('admin_password', password);
    await fetchCandidates();
    fetchCoverage();
  };

  useEffect(() => {
    if (password) {
      fetchCandidates();
      fetchCoverage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('admin_password');
    setPassword('');
    setAuthenticated(false);
    setData(null);
  };

  // ── PR 8A.6a: Manifest API Functions ──

  const generateManifest = useCallback(async () => {
    setManifestLoading(true);
    setManifestError(null);
    setManifest(null);
    setSelectedItems(new Set());
    setExecResult(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/cleanup/manifest`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          category: manifestCategory,
          min_age_days: manifestMinAge,
          include_high_risk: manifestIncludeHigh,
        }),
      });
      // Defensive body read — proxy layers can consume the stream before JS
      let text = '';
      try { text = await resp.clone().text(); } catch { try { text = await resp.text(); } catch { /* body unavailable */ } }
      let data = {};
      try { data = JSON.parse(text); } catch { /* non-JSON response */ }

      if (resp.status === 401) throw new Error('Invalid admin password');
      if (resp.status === 503) throw new Error(data.detail || data.error || 'Schema v012 not applied. Run migration first.');
      if (!resp.ok) throw new Error(data.detail || `Server error (HTTP ${resp.status})`);
      if (!data.manifest_id) throw new Error('Invalid manifest response');
      setManifest(data);
    } catch (e) {
      setManifestError(e.message);
    } finally {
      setManifestLoading(false);
    }
  }, [headers, manifestCategory, manifestMinAge, manifestIncludeHigh]);

  const executeManifest = useCallback(async (confirmation) => {
    if (!manifest || selectedItems.size === 0) return;
    setExecuting(true);
    setExecResult(null);
    setManifestError(null);
    try {
      const selectedIds = [...selectedItems];
      const payload = {
        manifest_id: manifest.manifest_id,
        selected_item_ids: selectedIds,
        confirmation,
      };
      const resp = await fetch(`${BACKEND_URL}/api/admin/cleanup/execute`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(payload),
      });
      let text = '';
      try { text = await resp.clone().text(); } catch { try { text = await resp.text(); } catch { /* body unavailable */ } }
      let data = {};
      try { data = JSON.parse(text); } catch { /* non-JSON response */ }

      if (!resp.ok) {
        // Build detailed error message from backend response
        const detail = data.detail || '';
        const errorParts = [`Execution failed (HTTP ${resp.status})`];
        if (detail) errorParts.push(detail);
        // If detail is missing, add local diagnostic context
        if (!detail) {
          const selItems = manifest.items?.filter(i => selectedIds.includes(i.item_id)) || [];
          const selCats = [...new Set(selItems.map(i => i.category))];
          errorParts.push(
            `Confirmation sent: "${confirmation}". ` +
            `Selected categories: [${selCats.join(', ')}]. ` +
            `Items: ${selectedIds.length}. ` +
            `Manifest: ${manifest.manifest_id} (filter: ${manifest.category_filter || '?'}). ` +
            `Response body: ${text?.substring(0, 200) || '(empty)'}`
          );
        }
        throw new Error(errorParts.join(' — '));
      }
      setExecResult(data);
      setSelectedItems(new Set());
      setConfirmModal(null);
      setConfirmText('');
    } catch (e) {
      setManifestError(e.message);
      setConfirmModal(null);
      setConfirmText('');
    } finally {
      setExecuting(false);
    }
  }, [headers, manifest, selectedItems]);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/cleanup/history`, { headers: headers() });
      if (resp.ok) {
        let text = '';
        try { text = await resp.clone().text(); } catch { try { text = await resp.text(); } catch { /* */ } }
        try { setManifestHistory(JSON.parse(text)); } catch { /* ignore parse error */ }
      } else {
        // 503 = schema not applied yet — show empty history instead of stuck Loading
        setManifestHistory({ manifests: [] });
      }
    } catch (_) {
      setManifestHistory({ manifests: [] });
    }
    setHistoryLoading(false);
  }, [headers]);

  const exportManifest = useCallback(async (format) => {
    if (!manifest) return;
    try {
      const resp = await fetch(
        `${BACKEND_URL}/api/admin/cleanup/manifest/${manifest.manifest_id}/export?format=${format}`,
        { headers: headers() }
      );
      if (!resp.ok) {
        let errText = '';
        try { errText = await resp.clone().text(); } catch { try { errText = await resp.text(); } catch { /* */ } }
        throw new Error(`Export failed (HTTP ${resp.status})${errText ? ': ' + errText.slice(0, 200) : ''}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `manifest-${manifest.manifest_id}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setManifestError(`Export failed: ${e.message}`);
    }
  }, [headers, manifest]);

  const fetchManifestDetail = useCallback(async (manifestId) => {
    setManifestDetailLoading(true);
    setManifestDetail(null);
    try {
      const resp = await fetch(
        `${BACKEND_URL}/api/admin/cleanup/manifest/${manifestId}/detail`,
        { headers: headers() }
      );
      let text = '';
      try { text = await resp.clone().text(); } catch { try { text = await resp.text(); } catch { /* */ } }
      let data = {};
      try { data = JSON.parse(text); } catch { /* */ }
      if (resp.ok && data.manifest_id) {
        setManifestDetail(data);
      } else {
        setManifestError(data.detail || `Failed to load manifest detail (HTTP ${resp.status})`);
      }
    } catch (e) {
      setManifestError(`Detail fetch failed: ${e.message}`);
    }
    setManifestDetailLoading(false);
  }, [headers]);

  const verifyDuplicates = useCallback(async () => {
    setDupVerifyLoading(true);
    setDupVerification(null);
    try {
      const resp = await fetch(
        `${BACKEND_URL}/api/admin/cleanup/verify-duplicates?max_groups=30&max_hashes=50`,
        { method: 'POST', headers: headers() }
      );
      let data = {};
      try { data = await resp.json(); } catch { /* */ }
      if (resp.ok) {
        setDupVerification(data);
      } else {
        setManifestError(data.detail || `Verify duplicates failed (HTTP ${resp.status})`);
      }
    } catch (e) {
      setManifestError(`Verify duplicates failed: ${e.message}`);
    }
    setDupVerifyLoading(false);
  }, [headers]);

  const toggleItemSelection = useCallback((itemId) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }, []);

  // Per-category selection — no mixed-category "Select All"
  const selectByCategory = useCallback((category) => {
    if (!manifest) return;
    const eligible = manifest.items
      .filter(i => i.cleanup_eligible && i.category === category)
      .map(i => i.item_id);
    setSelectedItems(new Set(eligible));
  }, [manifest]);

  // Compute eligible categories from manifest for per-category buttons
  const eligibleByCategory = React.useMemo(() => {
    if (!manifest) return {};
    const groups = {};
    manifest.items.forEach(i => {
      if (i.cleanup_eligible) {
        if (!groups[i.category]) groups[i.category] = 0;
        groups[i.category]++;
      }
    });
    return groups;
  }, [manifest]);

  // Check if current selection is mixed-category
  const selectionCategories = React.useMemo(() => {
    if (!manifest || selectedItems.size === 0) return new Set();
    return new Set(
      manifest.items
        .filter(i => selectedItems.has(i.item_id))
        .map(i => i.category)
    );
  }, [manifest, selectedItems]);

  const isMixedSelection = selectionCategories.size > 1;

  const clearSelection = useCallback(() => {
    setSelectedItems(new Set());
  }, []);

  const openConfirmModal = useCallback(() => {
    if (!manifest || selectedItems.size === 0) return;
    const selItems = manifest.items.filter(i => selectedItems.has(i.item_id));
    const cats = new Set(selItems.map(i => i.category));

    // BLOCK mixed-category execution
    if (cats.size > 1) {
      setManifestError(
        'Mixed-category execution is not allowed. ' +
        'Select items from only one category at a time. ' +
        'Categories selected: ' + [...cats].map(c => CATEGORY_META[c]?.label || c).join(', ')
      );
      return;
    }

    const cat = [...cats][0];
    // Use server-provided confirmations from manifest response when available,
    // fall back to hardcoded map for all deletable categories.
    const fallbackMap = {
      expired_renders: 'DELETE EXPIRED RENDERS',
      invalidated_renders: 'DELETE INVALIDATED RENDERS',
      soft_deleted_with_s3: 'DELETE SOFT DELETED WITH S3',
      orphaned_assets: 'DELETE ORPHANED ASSETS',
      verified_duplicates: 'DELETE VERIFIED DUPLICATES',
      legacy_unused_confirmed: 'DELETE LEGACY UNUSED CONFIRMED',
    };
    const expectedConfirmation =
      manifest?.expected_confirmations?.[cat] || fallbackMap[cat] || 'DELETE SELECTED CLEANUP MANIFEST';
    setConfirmModal({ expectedConfirmation, items: selItems, category: cat });
    setConfirmText('');
  }, [manifest, selectedItems]);

  const toggleExpand = (cat) => {
    setExpanded(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  // ── Login ──────────────────────────────────────────────────────────────

  if (!authenticated) {
    return (
      <div style={styles.loginContainer}>
        <div style={styles.loginCard}>
          <Trash2 size={32} color="#f59e0b" />
          <h2 style={styles.loginTitle}>Cleanup Visibility</h2>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Admin password"
              style={styles.loginInput}
              autoFocus
            />
            <button type="submit" style={styles.loginButton} disabled={loading}>
              {loading ? 'Loading…' : 'View Candidates'}
            </button>
          </form>
          {error && <div style={styles.errorBanner}>{error}</div>}
        </div>
      </div>
    );
  }

  // ── Main Layout ────────────────────────────────────────────────────────

  return (
    <AdminLayout activePage="cleanup" onLogout={handleLogout}>
      <div style={styles.page}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Cleanup Candidates</h1>
            {data && (
              <span style={styles.subtitle}>
                Generated: {_timeAgo(data.generated_at)}
                {' · '}GC: {data.gc_enabled ? 'ENABLED' : 'disabled'}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Tab buttons */}
            <button
              onClick={() => setActiveTab('diagnostics')}
              style={{
                ...styles.tabBtn,
                ...(activeTab === 'diagnostics' ? styles.tabBtnActive : {}),
              }}
            >
              <Eye size={13} /> Diagnostics
            </button>
            <button
              onClick={() => { setActiveTab('staged'); if (!manifestHistory) fetchHistory(); }}
              style={{
                ...styles.tabBtn,
                ...(activeTab === 'staged' ? styles.tabBtnActive : {}),
              }}
            >
              <Trash2 size={13} /> Staged Cleanup
            </button>
            <button
              onClick={() => { setActiveTab('anonymous'); if (!anonData) fetchAnonDryRun(); }}
              style={{
                ...styles.tabBtn,
                ...(activeTab === 'anonymous' ? styles.tabBtnActive : {}),
              }}
            >
              <Activity size={13} /> Anonymous
            </button>
            {activeTab === 'diagnostics' && (
              <button
                onClick={() => { fetchCandidates(); fetchCoverage(); }}
                disabled={loading || coverageLoading}
                style={styles.refreshBtn}
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                {loading ? 'Scanning…' : 'Refresh'}
              </button>
            )}
          </div>
        </div>

        {/* ── DIAGNOSTICS TAB ── */}
        {activeTab === 'diagnostics' && (
          <>
        {/* Read-only banner */}
        <div style={styles.readOnlyBanner}>
          <Shield size={16} color="#f59e0b" />
          <span>
            <strong>Review only</strong> — no cleanup actions are available in PR 8A.
            Inspect candidates below. No S3 operations, no DB writes, no lifecycle changes.
          </span>
        </div>

        {error && <div style={styles.errorBanner}>{error}</div>}

        {/* ── S3 Coverage Summary ── */}
        {coverage && (() => {
          const s = coverage.summary;
          const keepCats = coverage.keep_categories;
          const attnCats = coverage.attention_categories;
          const cleanCats = coverage.cleanup_categories;
          const dbDiag = coverage.db_diagnostics;
          return (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Database size={16} color="#60a5fa" />
                <span style={{ fontSize: 14, fontWeight: 700, color: '#e5e5e5' }}>
                  S3 Coverage Analysis
                </span>
                {coverageLoading && <RefreshCw size={12} className="animate-spin" color="#525252" />}
              </div>

              {/* Top-level summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 14 }}>
                {/* Total S3 */}
                <div style={{ ...styles.totalCard, borderColor: '#3b82f622' }}>
                  <div style={{ fontSize: 10, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Total S3 Objects</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 22, fontWeight: 700, color: '#e5e5e5' }}>{s.total_s3_objects}</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: '#525252' }}>{s.total_s3_size_human}</div>
                </div>
                {/* Verified / Keep */}
                <div style={{ ...styles.totalCard, borderColor: '#22c55e22' }}>
                  <div style={{ fontSize: 10, color: '#22c55e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Verified / Keep</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 22, fontWeight: 700, color: '#22c55e' }}>{s.keep_objects}</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: '#525252' }}>{s.keep_size_human}</div>
                  <div style={{ fontSize: 9, color: '#3a3a3a', marginTop: 4 }}>
                    {Object.entries(keepCats).filter(([,v]) => v.count > 0).map(([k,v]) =>
                      `${k.replace('keep_','').replace(/_/g,' ')}: ${v.count}`
                    ).join(' · ')}
                  </div>
                </div>
                {/* Needs Attention */}
                <div style={{ ...styles.totalCard, borderColor: s.attention_objects > 0 ? '#f59e0b33' : '#1a1a1b' }}>
                  <div style={{ fontSize: 10, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Needs Attention</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 22, fontWeight: 700, color: s.attention_objects > 0 ? '#f59e0b' : '#3a3a3a' }}>{s.attention_objects}</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: '#525252' }}>{s.attention_size_human}</div>
                  <div style={{ fontSize: 9, color: '#3a3a3a', marginTop: 4 }}>
                    {Object.entries(attnCats).filter(([,v]) => v.count > 0).map(([k,v]) => `${k.replace(/_/g,' ')}: ${v.count}`).join(' · ')}
                    {dbDiag.db_points_to_missing_s3.count > 0 && ` · db→missing: ${dbDiag.db_points_to_missing_s3.count}`}
                  </div>
                </div>
                {/* Cleanup Candidates */}
                <div style={{ ...styles.totalCard, borderColor: s.cleanup_candidate_objects > 0 ? '#ef444433' : '#1a1a1b' }}>
                  <div style={{ fontSize: 10, color: '#ef4444', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Cleanup Candidates</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 22, fontWeight: 700, color: s.cleanup_candidate_objects > 0 ? '#ef4444' : '#3a3a3a' }}>{s.cleanup_candidate_objects}</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: '#525252' }}>{s.cleanup_candidate_size_human}</div>
                  <div style={{ fontSize: 9, color: '#3a3a3a', marginTop: 4 }}>
                    {Object.entries(cleanCats).filter(([,v]) => v.count > 0).map(([k,v]) => `${k.replace(/_/g,' ')}: ${v.count}`).join(' · ')}
                  </div>
                </div>
                {/* Unclassified */}
                <div style={{ ...styles.totalCard, borderColor: s.unclassified_objects > 0 ? '#dc262644' : '#1a1a1b' }}>
                  <div style={{ fontSize: 10, color: s.unclassified_objects > 0 ? '#dc2626' : '#525252', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Unclassified</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 22, fontWeight: 700, color: s.unclassified_objects > 0 ? '#dc2626' : '#3a3a3a' }}>{s.unclassified_objects}</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: '#525252' }}>{s.unclassified_size_human}</div>
                </div>
                {/* Coverage */}
                <div style={{ ...styles.totalCard, borderColor: s.coverage_ok ? '#22c55e22' : '#dc262644' }}>
                  <div style={{ fontSize: 10, color: s.coverage_ok ? '#22c55e' : '#dc2626', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Coverage</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 22, fontWeight: 700, color: s.coverage_ok ? '#22c55e' : '#dc2626' }}>{s.classification_coverage_percent}%</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: s.coverage_ok ? '#166534' : '#991b1b' }}>{s.coverage_ok ? '✓ All classified' : '✗ Gap detected'}</div>
                </div>
              </div>

              {/* Unclassified warning */}
              {s.unclassified_objects > 0 && (
                <div style={{ ...styles.readOnlyBanner, borderColor: '#dc262644', background: '#1c0a0a', marginBottom: 14 }}>
                  <AlertOctagon size={14} color="#dc2626" />
                  <span style={{ fontSize: 11, color: '#fca5a5' }}>
                    {s.unclassified_objects} S3 objects are not classified by the current diagnostics model.
                    Add/fix diagnostic categories before considering cleanup automation.
                  </span>
                </div>
              )}

              {/* DB → Missing S3 warning */}
              {dbDiag.db_points_to_missing_s3.count > 0 && (
                <div style={{ ...styles.readOnlyBanner, borderColor: '#f59e0b33', background: '#1a1405', marginBottom: 14 }}>
                  <AlertTriangle size={14} color="#f59e0b" />
                  <span style={{ fontSize: 11, color: '#fbbf24' }}>
                    {dbDiag.db_points_to_missing_s3.count} DB records point to missing S3 objects
                    ({dbDiag.db_points_to_missing_s3.size_human}).
                    These are confirmed assets whose storage_key has no corresponding S3 file.
                  </span>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── S3 Coverage Drill-Down Cards ── */}
        <CoverageDrillDown coverage={coverage} onPreview={setPreviewKey} headers={headers} onRefresh={() => { fetchCoverage(); fetchCandidates(); }} />

        {data && (
          <>
            {/* Totals Card */}
            <div style={styles.totalsRow}>
              <div style={styles.totalCard}>
                <div style={styles.totalLabel}>Total Candidates</div>
                <div style={styles.totalValue}>{data.totals.total_candidates}</div>
              </div>
              <div style={styles.totalCard}>
                <div style={styles.totalLabel}>Estimated Reclaimable</div>
                <div style={styles.totalValue}>{data.totals.total_estimated_bytes_human}</div>
              </div>
              <div style={styles.totalCard}>
                <div style={styles.totalLabel}>Categories</div>
                <div style={styles.totalValue}>
                  {Object.keys(data.categories).filter(k => data.categories[k].count > 0).length}
                  <span style={{ fontSize: 12, color: '#525252', fontWeight: 400 }}> / {Object.keys(data.categories).length}</span>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div style={styles.cardGrid}>
              {Object.entries(data.categories).map(([key, cat]) => {
                const meta = CATEGORY_META[key] || { label: key, icon: FileQuestion, color: '#737373' };
                const Icon = meta.icon;
                const hasItems = cat.count > 0;
                return (
                  <button
                    key={key}
                    onClick={() => hasItems && toggleExpand(key)}
                    style={{
                      ...styles.summaryCard,
                      borderColor: hasItems ? meta.color + '44' : '#1a1a1b',
                      cursor: hasItems ? 'pointer' : 'default',
                      opacity: hasItems ? 1 : 0.5,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <Icon size={16} color={meta.color} />
                      <span style={{ color: '#a3a3a3', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {meta.label}
                      </span>
                      {hasItems && (
                        expanded[key]
                          ? <ChevronDown size={14} color="#525252" style={{ marginLeft: 'auto' }} />
                          : <ChevronRight size={14} color="#525252" style={{ marginLeft: 'auto' }} />
                      )}
                    </div>
                    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 22, fontWeight: 700, color: hasItems ? '#e5e5e5' : '#3a3a3a' }}>
                      {cat.count}
                    </div>
                    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: '#525252', marginTop: 2 }}>
                      {cat.estimated_bytes_human}
                    </div>
                    {cat.error && (
                      <div style={{ fontSize: 10, color: '#ef4444', marginTop: 6 }}>Error: {cat.error}</div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Expired Renders — extra diagnostics */}
            {data.categories.expired_renders && data.categories.expired_renders.count > 0 && (
              <div style={styles.diagnosticRow}>
                <span style={{ color: '#737373', fontSize: 11 }}>Expired Renders:</span>
                <DiagBadge label="Safe to delete S3" value={data.categories.expired_renders.safe_to_delete_s3_count} color="#4ade80" />
                <DiagBadge label="Mark-only (shared key)" value={data.categories.expired_renders.mark_only_count} color="#f87171" />
                <DiagBadge label="Shared output_keys" value={data.categories.expired_renders.shared_output_key_count} color="#fbbf24" />
              </div>
            )}

            {/* Invalidated Renders — extra diagnostics */}
            {data.categories.invalidated_renders && data.categories.invalidated_renders.count > 0 && (
              <div style={styles.diagnosticRow}>
                <span style={{ color: '#737373', fontSize: 11 }}>Invalidated Renders:</span>
                <DiagBadge label="Total" value={data.categories.invalidated_renders.count} color="#e879f9" />
                {data.categories.invalidated_renders.by_reason && Object.entries(data.categories.invalidated_renders.by_reason).map(([reason, cnt]) => (
                  <DiagBadge key={reason} label={reason} value={cnt} color="#c084fc" />
                ))}
                {data.categories.invalidated_renders.has_invalidation_columns === false && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '3px 10px', background: '#1c1917', borderRadius: 4,
                    border: '1px solid #854d0e', color: '#fbbf24', fontSize: 10, fontWeight: 700,
                  }}>
                    COLUMNS MISSING
                  </span>
                )}
              </div>
            )}

            {/* Duplicate Groups — extra info */}
            {data.categories.duplicate_groups && data.categories.duplicate_groups.excess_copies > 0 && (
              <div style={styles.diagnosticRow}>
                <span style={{ color: '#737373', fontSize: 11 }}>Duplicates:</span>
                <DiagBadge label="Excess copies" value={data.categories.duplicate_groups.excess_copies} color="#06b6d4" />
                <DiagBadge label="Potential savings" value={data.categories.duplicate_groups.estimated_bytes_human} color="#06b6d4" />
              </div>
            )}

            {/* Unused Confirmed Assets — extra diagnostics (PR 8A.1) */}
            {data.categories.unused_confirmed_assets && data.categories.unused_confirmed_assets.count > 0 && (
              <div style={styles.diagnosticRow}>
                <span style={{ color: '#737373', fontSize: 11 }}>Unused Confirmed:</span>
                <DiagBadge label="Unreachable assets" value={data.categories.unused_confirmed_assets.count} color="#f97316" />
                <DiagBadge label="System presets (excl.)" value={data.categories.unused_confirmed_assets.system_preset_count} color="#525252" />
                <DiagBadge label="Fields checked" value={(data.categories.unused_confirmed_assets.carousel_asset_fields_checked || []).length} color="#737373" />
              </div>
            )}

            {/* Possible Duplicate Size Groups — extra diagnostics (PR 8A.1) */}
            {data.categories.possible_duplicate_size_groups && data.categories.possible_duplicate_size_groups.count > 0 && (
              <div style={styles.diagnosticRow}>
                <span style={{ color: '#737373', fontSize: 11 }}>Possible Dupes (Size):</span>
                <DiagBadge label="Excess copies" value={data.categories.possible_duplicate_size_groups.excess_copies} color="#a855f7" />
                <DiagBadge label="Potential (unverified)" value={data.categories.possible_duplicate_size_groups.estimated_bytes_human} color="#a855f7" />
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 10px', background: '#1c0a0a', borderRadius: 4,
                  border: '1px solid #991b1b', color: '#f87171', fontSize: 10, fontWeight: 700,
                }}>
                  UNVERIFIED
                </span>
              </div>
            )}

            {/* Stale Asset References — extra diagnostics (PR 8A.2) */}
            {data.categories.stale_asset_references && data.categories.stale_asset_references.count > 0 && (
              <div style={styles.diagnosticRow}>
                <span style={{ color: '#737373', fontSize: 11 }}>Stale Refs:</span>
                <DiagBadge label="Stale rows" value={data.categories.stale_asset_references.count} color="#fb923c" />
                <DiagBadge label="Affected bytes" value={data.categories.stale_asset_references.estimated_bytes_human} color="#fb923c" />
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 10px', background: '#1c1917', borderRadius: 4,
                  border: '1px solid #854d0e', color: '#fbbf24', fontSize: 10, fontWeight: 700,
                }}>
                  SUSPECTED STALE
                </span>
              </div>
            )}

            {/* Missing Asset References — extra diagnostics (PR 8A.2) */}
            {data.categories.missing_asset_references && data.categories.missing_asset_references.count > 0 && (
              <div style={styles.diagnosticRow}>
                <span style={{ color: '#737373', fontSize: 11 }}>Missing Refs:</span>
                <DiagBadge label="Missing rows" value={data.categories.missing_asset_references.count} color="#38bdf8" />
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 10px', background: '#0c1929', borderRadius: 4,
                  border: '1px solid #1e40af', color: '#60a5fa', fontSize: 10, fontWeight: 700,
                }}>
                  REFERENCE MISMATCH
                </span>
              </div>
            )}

            {/* Referenced Deleted Assets — data integrity warning (PR 8A.4) */}
            {data.categories.referenced_deleted_assets && data.categories.referenced_deleted_assets.count > 0 && (
              <div style={styles.diagnosticRow}>
                <span style={{ color: '#737373', fontSize: 11 }}>Deleted but Live:</span>
                <DiagBadge label="Live dependencies" value={data.categories.referenced_deleted_assets.count} color="#ef4444" />
                <DiagBadge label="Affected" value={data.categories.referenced_deleted_assets.estimated_bytes_human} color="#ef4444" />
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 10px', background: '#1c0505', borderRadius: 4,
                  border: '1px solid #dc2626', color: '#f87171', fontSize: 10, fontWeight: 700,
                }}>
                  DO NOT DELETE
                </span>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 10px', background: '#0a0a0a', borderRadius: 4,
                  border: '1px solid #1f1f1f', color: '#737373', fontSize: 10,
                }}>
                  NOT RECLAIMABLE
                </span>
              </div>
            )}

            {/* Reference Rebuild Dry-Run Card (PR 8A.4) */}
            <DryRunCard dryRun={dryRun} loading={dryRunLoading} onFetch={fetchDryRun} />

            {/* Reference Repair Card (PR 8A.5) */}
            <ReferenceRepairCard headers={headers()} />

            {/* Normalize Persisted Media URLs Card */}
            <NormalizeMediaUrlsCard headers={headers()} />

            {/* Normalize Deleted Asset Status Card */}
            <NormalizeDeletedStatusCard headers={headers()} />

            {/* Media Lifecycle Foundation (PR 1) */}
            <LifecycleFoundationCard headers={headers()} />

            {/* Expandable Tables */}
            {Object.entries(data.categories).map(([key, cat]) => {
              if (!expanded[key] || !cat.samples || cat.samples.length === 0) return null;
              const meta = CATEGORY_META[key] || { label: key, color: '#737373' };

              return (
                <div key={key} style={styles.tableCard}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <div style={{ width: 3, height: 18, borderRadius: 2, background: meta.color }} />
                    <span style={{ color: '#e5e5e5', fontSize: 14, fontWeight: 600 }}>{meta.label}</span>
                    <span style={{ color: '#525252', fontSize: 11, fontFamily: "'IBM Plex Mono', monospace" }}>
                      ({cat.count} total, showing {cat.samples.length})
                    </span>
                    {meta.description && (
                      <span style={{ color: '#3a3a3a', fontSize: 11, marginLeft: 'auto' }}>{meta.description}</span>
                    )}
                  </div>

                  {key === 'duplicate_groups'
                    ? <DuplicateGroupsTable samples={cat.samples} onPreview={setPreviewKey} headers={headers()} />
                    : key === 'expired_renders'
                    ? <ExpiredRendersTable samples={cat.samples} />
                    : key === 'invalidated_renders'
                    ? <InvalidatedRendersTable samples={cat.samples} />
                    : key === 'unused_confirmed_assets'
                    ? <UnusedAssetsTable samples={cat.samples} category={cat} onPreview={setPreviewKey} headers={headers()} onUsage={setUsageModal} />
                    : key === 'possible_duplicate_size_groups'
                    ? <PossibleDupeSizeTable samples={cat.samples} category={cat} onPreview={setPreviewKey} headers={headers()} onUsage={setUsageModal} />
                    : key === 'stale_asset_references'
                    ? <ReferenceConsistencyTable samples={cat.samples} category={cat} type="stale" onPreview={setPreviewKey} headers={headers()} onUsage={setUsageModal} />
                    : key === 'missing_asset_references'
                    ? <ReferenceConsistencyTable samples={cat.samples} category={cat} type="missing" onPreview={setPreviewKey} headers={headers()} onUsage={setUsageModal} />
                    : key === 'referenced_deleted_assets'
                    ? <ReferencedDeletedTable samples={cat.samples} category={cat} onPreview={setPreviewKey} headers={headers()} onUsage={setUsageModal} />
                    : <CandidateTable samples={cat.samples} category={key} onPreview={setPreviewKey} headers={headers()} onUsage={setUsageModal} />
                  }
                </div>
              );
            })}

            {/* Config */}
            <div style={styles.configCard}>
              <div style={{ color: '#525252', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                Configuration
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {Object.entries(data.config).map(([k, v]) => (
                  <div key={k} style={styles.configItem}>
                    <span style={{ color: '#525252' }}>{k.replace(/_/g, ' ')}</span>
                    <span style={{ color: typeof v === 'boolean' ? (v ? '#4ade80' : '#f87171') : '#a3a3a3', fontWeight: 600 }}>
                      {String(v)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
          </>
        )}

        {/* ── STAGED CLEANUP TAB ── */}
        {activeTab === 'staged' && (
          <StagedCleanupPanel
            headers={headers()}
            manifest={manifest}
            setManifest={setManifest}
            manifestLoading={manifestLoading}
            manifestError={manifestError}
            setManifestError={setManifestError}
            manifestCategory={manifestCategory}
            setManifestCategory={setManifestCategory}
            manifestMinAge={manifestMinAge}
            setManifestMinAge={setManifestMinAge}
            manifestIncludeHigh={manifestIncludeHigh}
            setManifestIncludeHigh={setManifestIncludeHigh}
            selectedItems={selectedItems}
            toggleItemSelection={toggleItemSelection}
            selectByCategory={selectByCategory}
            eligibleByCategory={eligibleByCategory}
            isMixedSelection={isMixedSelection}
            selectionCategories={selectionCategories}
            clearSelection={clearSelection}
            generateManifest={generateManifest}
            openConfirmModal={openConfirmModal}
            exportManifest={exportManifest}
            execResult={execResult}
            manifestHistory={manifestHistory}
            historyLoading={historyLoading}
            fetchHistory={fetchHistory}
            fetchCandidates={fetchCandidates}
            onPreview={setPreviewKey}
            fetchManifestDetail={fetchManifestDetail}
            manifestDetail={manifestDetail}
            manifestDetailLoading={manifestDetailLoading}
            setManifestDetail={setManifestDetail}
            verifyDuplicates={verifyDuplicates}
            dupVerification={dupVerification}
            dupVerifyLoading={dupVerifyLoading}
            setDupVerification={setDupVerification}
          />
        )}

        {/* Confirmation Modal */}
        {confirmModal && (
          <div style={styles.modalOverlay}>
            <div style={styles.confirmModalCard}>
              <button onClick={() => { setConfirmModal(null); setConfirmText(''); }}
                style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#737373', padding: 4 }}>
                <X size={18} />
              </button>
              <AlertOctagon size={28} color="#ef4444" />
              <h3 style={{ color: '#e5e5e5', fontSize: 16, margin: '12px 0 8px' }}>Confirm Cleanup Execution</h3>
              <p style={{ color: '#a3a3a3', fontSize: 12, margin: '0 0 16px', lineHeight: 1.5 }}>
                You are about to delete <strong style={{ color: '#f87171' }}>{confirmModal.items.length}</strong> S3 object(s).
                Each item will be revalidated before deletion. This action is <strong style={{ color: '#f87171' }}>irreversible</strong>.
              </p>
              {/* Category-specific warnings */}
              {confirmModal.category === 'orphaned_assets' && (
                <div style={{
                  ...styles.readOnlyBanner, borderColor: '#991b1b44', background: '#1c0a0a99', marginBottom: 12,
                }}>
                  <AlertTriangle size={14} color="#f87171" />
                  <span style={{ fontSize: 11, color: '#fca5a5' }}>
                    <strong>HIGH RISK</strong> — These are original user uploads, not generated cache.
                    Delete only after manual inspection of each item.
                  </span>
                </div>
              )}
              {confirmModal.category === 'verified_duplicates' && (
                <div style={{
                  ...styles.readOnlyBanner, borderColor: '#7e22ce44', background: '#1c0a2e99', marginBottom: 12,
                }}>
                  <Copy size={14} color="#c084fc" />
                  <span style={{ fontSize: 11, color: '#d8b4fe' }}>
                    Only hash-verified, unreferenced excess copies will be deleted.
                    Live-referenced duplicates are blocked. Canonical assets are preserved.
                  </span>
                </div>
              )}
              {confirmModal.category === 'legacy_unused_confirmed' && (
                <div style={{
                  ...styles.readOnlyBanner, borderColor: '#dc262644', background: '#1c0a0a',
                  marginBottom: 12, padding: '10px 12px',
                }}>
                  <AlertOctagon size={16} color="#dc2626" />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 12, color: '#fca5a5', fontWeight: 700 }}>
                      ⚠ LEGACY / MANUAL / IRREVERSIBLE
                    </span>
                    <span style={{ fontSize: 11, color: '#d4d4d4', lineHeight: 1.5 }}>
                      These assets are confirmed and tracked, but not currently referenced by live carousels,
                      asset references, render jobs, or backups/system objects.
                      They may include user-retained library/workspace assets from before lifecycle tracking existed.
                      <strong style={{ color: '#fca5a5' }}> Delete only if you accept that this legacy state
                      cannot be reliably classified.</strong>
                    </span>
                  </div>
                </div>
              )}
              <div style={{ background: '#0a0a0a', border: '1px solid #262626', borderRadius: 6, padding: '10px 14px', marginBottom: 16 }}>
                <div style={{ color: '#525252', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  Selected items by category
                </div>
                {Object.entries(
                  confirmModal.items.reduce((acc, i) => { acc[i.category] = (acc[i.category] || 0) + 1; return acc; }, {})
                ).map(([cat, cnt]) => (
                  <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#a3a3a3', padding: '2px 0' }}>
                    <span>{CATEGORY_META[cat]?.label || cat}</span>
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600 }}>{cnt}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ color: '#737373', fontSize: 11, display: 'block', marginBottom: 4 }}>
                  Type <span style={{ color: '#f87171', fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {confirmModal.expectedConfirmation}
                  </span> to proceed:
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder={confirmModal.expectedConfirmation}
                  style={styles.loginInput}
                  autoFocus
                />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setConfirmModal(null); setConfirmText(''); }}
                  style={{ ...styles.tabBtn, flex: 1 }}>
                  Cancel
                </button>
                <button
                  onClick={() => executeManifest(confirmText)}
                  disabled={confirmText !== confirmModal.expectedConfirmation || executing}
                  style={{
                    flex: 1, padding: '10px 16px', borderRadius: 6, fontWeight: 600, fontSize: 13,
                    border: '1px solid #991b1b', cursor: 'pointer',
                    background: confirmText === confirmModal.expectedConfirmation ? '#7f1d1d' : '#1c0a0a',
                    color: confirmText === confirmModal.expectedConfirmation ? '#fca5a5' : '#525252',
                    opacity: executing ? 0.6 : 1,
                  }}
                >
                  {executing ? 'Executing…' : 'Execute Cleanup'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {previewKey && (
          <PreviewModal
            s3Key={previewKey}
            headers={headers()}
            onClose={() => setPreviewKey(null)}
          />
        )}
        {usageModal && (
          <AssetUsageModal
            assetId={usageModal.asset_id}
            storageKey={usageModal.storage_key}
            headers={headers()}
            onClose={() => setUsageModal(null)}
          />
        )}

        {/* ── ANONYMOUS CLEANUP TAB ── */}
        {activeTab === 'anonymous' && (
          <div>
            {/* Banner */}
            <div style={{ ...styles.readOnlyBanner, borderColor: '#854d0e44' }}>
              <Activity size={16} color="#f59e0b" />
              <span>
                <strong>Anonymous User Cleanup</strong> — audit and clean expired anonymous users.
                Empty and content cleanup are <strong>separate actions</strong> with separate confirmations.
              </span>
            </div>

            {anonError && <div style={styles.errorBanner}>{anonError}</div>}

            {/* Scan Button */}
            <div style={{ display: 'flex', gap: 8, margin: '12px 0', alignItems: 'center' }}>
              <button onClick={fetchAnonDryRun} disabled={anonLoading} style={styles.refreshBtn}>
                <RefreshCw size={14} className={anonLoading ? 'animate-spin' : ''} />
                {anonLoading ? 'Scanning…' : 'Scan / Dry Run'}
              </button>
            </div>

            {/* Summary Cards */}
            {anonData && anonData.summary && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 16 }}>
                <div style={styles.summaryCard}>
                  <div style={{ ...styles.summaryCount, color: '#60a5fa' }}>{anonData.summary.total_anonymous_users}</div>
                  <div style={styles.summaryLabel}>Total Anonymous</div>
                </div>
                <div style={styles.summaryCard}>
                  <div style={{ ...styles.summaryCount, color: '#4ade80' }}>{anonData.summary.empty_anonymous_users}</div>
                  <div style={styles.summaryLabel}>Empty Users</div>
                </div>
                <div style={styles.summaryCard}>
                  <div style={{ ...styles.summaryCount, color: '#fbbf24' }}>{anonData.summary.demo_only_anonymous_users || 0}</div>
                  <div style={styles.summaryLabel}>Demo-Only</div>
                </div>
                <div style={styles.summaryCard}>
                  <div style={{ ...styles.summaryCount, color: '#f59e0b' }}>{anonData.summary.content_anonymous_users}</div>
                  <div style={styles.summaryLabel}>With Content</div>
                </div>
                <div style={styles.summaryCard}>
                  <div style={{ ...styles.summaryCount, color: '#a3a3a3' }}>{anonData.summary.total_registered_users}</div>
                  <div style={styles.summaryLabel}>Registered (excluded)</div>
                </div>
              </div>
            )}

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* SECTION 1: Empty Anonymous Cleanup                         */}
            {/* ═══════════════════════════════════════════════════════════ */}
            {anonData && (
              <div style={{ ...styles.configCard, marginTop: 0, marginBottom: 16, borderColor: '#16653444' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div>
                    <div style={{ color: '#4ade80', fontSize: 13, fontWeight: 700 }}>
                      Empty Anonymous Cleanup
                    </div>
                    <div style={{ color: '#525252', fontSize: 10, marginTop: 2 }}>
                      Deletes expired anonymous users with zero content. No S3 involvement. Safe for routine use.
                    </div>
                  </div>
                  <button
                    onClick={() => setAnonEmptyModal(true)}
                    disabled={!anonData || anonEmptyApplying || (anonData.summary?.expired_empty_candidates || 0) === 0}
                    style={{
                      ...styles.refreshBtn,
                      background: (anonData.summary?.expired_empty_candidates || 0) > 0 ? '#14532d' : '#262626',
                      borderColor: (anonData.summary?.expired_empty_candidates || 0) > 0 ? '#166534' : '#333',
                      color: (anonData.summary?.expired_empty_candidates || 0) > 0 ? '#4ade80' : '#525252',
                      opacity: (!anonData || anonEmptyApplying || (anonData.summary?.expired_empty_candidates || 0) === 0) ? 0.4 : 1,
                    }}
                  >
                    <Trash2 size={14} />
                    Apply Empty Cleanup
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                  <div style={{ ...styles.summaryCard, borderColor: '#16653444' }}>
                    <div style={{ ...styles.summaryCount, color: '#4ade80' }}>{anonData.summary?.expired_empty_candidates || 0}</div>
                    <div style={styles.summaryLabel}>Expired Empty</div>
                    <div style={{ fontSize: 9, color: '#525252', marginTop: 2 }}>TTL: {anonData.config?.empty_user_ttl_hours}h</div>
                  </div>
                  <div style={{ ...styles.summaryCard, borderColor: '#16653444' }}>
                    <div style={{ ...styles.summaryCount, color: '#a3a3a3' }}>{anonData.empty_deletion_estimates?.users || 0}</div>
                    <div style={styles.summaryLabel}>Users to Delete</div>
                  </div>
                  <div style={{ ...styles.summaryCard, borderColor: '#16653444' }}>
                    <div style={{ ...styles.summaryCount, color: '#525252' }}>0</div>
                    <div style={styles.summaryLabel}>S3 Objects</div>
                    <div style={{ fontSize: 9, color: '#525252', marginTop: 2 }}>No S3 involvement</div>
                  </div>
                </div>

                {/* Expired Empty Candidates */}
                {anonData.empty_candidates && anonData.empty_candidates.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ color: '#525252', fontSize: 10, fontWeight: 700, marginBottom: 4 }}>
                      Expired Empty Candidates ({anonData.empty_candidates.length})
                    </div>
                    <div style={{ maxHeight: 150, overflow: 'auto' }}>
                      {anonData.empty_candidates.slice(0, 20).map(c => (
                        <div key={c.user_id} style={{ display: 'flex', gap: 12, padding: '3px 0', fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                          <span style={{ color: '#525252' }}>{c.user_id.substring(0, 16)}…</span>
                          <span style={{ color: '#a3a3a3' }}>{Math.round(c.age_hours)}h old</span>
                          <span style={{ color: '#4ade80' }}>empty</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty Apply Result */}
                {anonEmptyResult && (
                  <div style={{ marginTop: 10, padding: '8px 10px', background: '#0a1a0a', borderRadius: 4, border: '1px solid #16653444' }}>
                    <div style={{ color: '#4ade80', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>
                      <CheckCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      Empty Cleanup Applied
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 10 }}>
                      <span style={{ color: '#525252' }}>users_deleted: <span style={{ color: '#4ade80' }}>{anonEmptyResult.deleted?.users || 0}</span></span>
                      <span style={{ color: '#525252' }}>errors: <span style={{ color: (anonEmptyResult.errors?.length || 0) > 0 ? '#f87171' : '#404040' }}>{anonEmptyResult.errors?.length || 0}</span></span>
                    </div>
                  </div>
                )}
              </div>
            )}


            {/* ═══════════════════════════════════════════════════════════ */}
            {/* SECTION 1B: Demo-Only Anonymous Cleanup                    */}
            {/* ═══════════════════════════════════════════════════════════ */}
            {anonData && (
              <div style={{ ...styles.configCard, marginTop: 0, marginBottom: 16, borderColor: '#92400e44' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div>
                    <div style={{ ...styles.configLabel, color: '#fbbf24', letterSpacing: 1 }}>DEMO-ONLY ANONYMOUS USERS</div>
                    <div style={{ fontSize: 10, color: '#78716c', marginTop: 2 }}>
                      Untouched starter demo carousels only. No uploads, no exports, no edits.
                    </div>
                    <div style={{ fontSize: 10, color: '#78716c' }}>
                      TTL: {anonData.config?.demo_user_ttl_hours || 48}h
                    </div>
                  </div>
                  <button
                    onClick={() => setAnonDemoModal(true)}
                    disabled={!anonData || anonDemoApplying || (anonData.summary?.expired_demo_only_candidates || 0) === 0}
                    style={{
                      ...styles.btn,
                      background: (anonData.summary?.expired_demo_only_candidates || 0) > 0 ? '#78350f' : '#262626',
                      borderColor: (anonData.summary?.expired_demo_only_candidates || 0) > 0 ? '#92400e' : '#333',
                      color: (anonData.summary?.expired_demo_only_candidates || 0) > 0 ? '#fbbf24' : '#525252',
                      opacity: (!anonData || anonDemoApplying || (anonData.summary?.expired_demo_only_candidates || 0) === 0) ? 0.4 : 1,
                      fontSize: 11,
                      padding: '4px 10px',
                    }}
                  >
                    {anonDemoApplying ? 'Applying...' : 'Apply Demo Cleanup'}
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ ...styles.configCard, flex: 1, background: '#1a1a1a', padding: '6px 10px', marginBottom: 0 }}>
                    <div style={{ fontSize: 10, color: '#78716c', textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Demo-Only</div>
                    <div style={{ ...styles.summaryCount, color: '#fbbf24' }}>{anonData.summary?.demo_only_anonymous_users || 0}</div>
                  </div>
                  <div style={{ ...styles.configCard, flex: 1, background: '#1a1a1a', padding: '6px 10px', marginBottom: 0 }}>
                    <div style={{ fontSize: 10, color: '#78716c', textTransform: 'uppercase', letterSpacing: 0.5 }}>Expired (ready)</div>
                    <div style={{ ...styles.summaryCount, color: '#fbbf24' }}>{anonData.summary?.expired_demo_only_candidates || 0}</div>
                  </div>
                </div>

                {/* Expired Demo-Only Candidates */}
                {anonData.demo_only_candidates && anonData.demo_only_candidates.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 10, color: '#a8a29e', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Expired Demo-Only Candidates ({anonData.demo_only_candidates.length})
                    </div>
                    <div style={{ fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', color: '#78716c' }}>
                      {anonData.demo_only_candidates.slice(0, 20).map(c => (
                        <div key={c.user_id} style={{ display: 'flex', gap: 8, padding: '2px 0', borderBottom: '1px solid #262626' }}>
                          <span style={{ color: '#fbbf24', minWidth: 70 }}>{c.user_id.slice(0, 8)}</span>
                          <span>age:{c.age_hours}h</span>
                          <span>carousels:{c.content_counts?.carousels || 0}</span>
                          <span style={{ color: c.expired ? '#fbbf24' : '#525252' }}>{c.expired ? 'EXPIRED' : 'active'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Demo Apply Result */}
                {anonDemoResult && (
                  <div style={{ marginTop: 10, padding: '8px 10px', background: '#1a0f00', borderRadius: 4, border: '1px solid #92400e44' }}>
                    <div style={{ color: '#fbbf24', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>Demo Cleanup Result</div>
                    <div style={{ fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', display: 'flex', gap: 12, color: '#a8a29e' }}>
                      <span>users_deleted: <span style={{ color: '#fbbf24' }}>{anonDemoResult.deleted?.users || 0}</span></span>
                      <span>carousels_deleted: <span style={{ color: '#fbbf24' }}>{anonDemoResult.deleted?.carousels || 0}</span></span>
                      <span>errors: <span style={{ color: (anonDemoResult.errors?.length || 0) > 0 ? '#f87171' : '#404040' }}>{anonDemoResult.errors?.length || 0}</span></span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Demo-Only Cleanup Confirmation Modal */}
            {anonDemoModal && (
              <div style={styles.modal}>
                <div style={styles.modalContent}>
                  <h3 style={{ color: '#fbbf24', margin: '0 0 8px 0', fontSize: 14 }}>Confirm Demo-Only Cleanup</h3>
                  <p style={{ fontSize: 11, color: '#a8a29e', marginBottom: 12 }}>
                    This will delete <strong>{anonData?.summary?.expired_demo_only_candidates || 0}</strong> expired anonymous users
                    who have only untouched starter demo carousels and no other content.
                    <br /><br />
                    No S3 objects will be affected (untouched demos have no custom assets).
                  </p>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 10, color: '#78716c', marginBottom: 4 }}>
                      Type <code style={{ color: '#fbbf24' }}>APPLY DEMO CLEANUP</code> to confirm:
                    </div>
                    <input
                      value={anonDemoText}
                      onChange={e => setAnonDemoText(e.target.value)}
                      style={{ ...styles.input, width: '100%', fontSize: 11 }}
                      placeholder="APPLY DEMO CLEANUP"
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={applyAnonDemo}
                      disabled={anonDemoText !== 'APPLY DEMO CLEANUP' || anonDemoApplying}
                      style={{
                        ...styles.btn,
                        background: anonDemoText === 'APPLY DEMO CLEANUP' ? '#78350f' : '#262626',
                        color: anonDemoText === 'APPLY DEMO CLEANUP' ? '#fbbf24' : '#525252',
                        borderColor: anonDemoText === 'APPLY DEMO CLEANUP' ? '#92400e' : '#333',
                        opacity: anonDemoText !== 'APPLY DEMO CLEANUP' || anonDemoApplying ? 0.4 : 1,
                      }}
                    >
                      {anonDemoApplying ? 'Applying...' : 'Apply Demo Cleanup'}
                    </button>
                    <button onClick={() => { setAnonDemoModal(false); setAnonDemoText(''); }} style={{ ...styles.btn }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}


            {/* ═══════════════════════════════════════════════════════════ */}
            {/* SECTION 2: Advanced Content Cleanup                        */}
            {/* ═══════════════════════════════════════════════════════════ */}
            {anonData && (
              <div style={{ ...styles.configCard, marginTop: 0, marginBottom: 16, borderColor: '#7f1d1d44' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div>
                    <div style={{ color: '#f87171', fontSize: 13, fontWeight: 700 }}>
                      ⚠ Advanced: Content Cleanup
                    </div>
                    <div style={{ color: '#525252', fontSize: 10, marginTop: 2 }}>
                      Deletes expired anonymous users, their carousels, uploads, render outputs, and <strong style={{ color: '#f87171' }}>physically deletes S3 objects</strong>.
                    </div>
                  </div>
                  <button
                    onClick={() => setAnonContentModal(true)}
                    disabled={!anonData || anonContentApplying || (anonData.summary?.expired_content_candidates || 0) === 0}
                    style={{
                      ...styles.refreshBtn,
                      background: (anonData.summary?.expired_content_candidates || 0) > 0 ? '#7f1d1d' : '#262626',
                      borderColor: (anonData.summary?.expired_content_candidates || 0) > 0 ? '#991b1b' : '#333',
                      color: (anonData.summary?.expired_content_candidates || 0) > 0 ? '#fca5a5' : '#525252',
                      opacity: (!anonData || anonContentApplying || (anonData.summary?.expired_content_candidates || 0) === 0) ? 0.4 : 1,
                    }}
                  >
                    <AlertOctagon size={14} />
                    Apply Content Cleanup
                  </button>
                </div>

                {/* Warning box */}
                <div style={{ background: '#1a0a0a', border: '1px solid #7f1d1d44', borderRadius: 4, padding: '8px 10px', marginBottom: 10, fontSize: 10, color: '#f87171' }}>
                  <AlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  <strong>This action physically deletes S3 objects.</strong> It will permanently remove carousels,
                  media uploads, render outputs, and S3 files for expired anonymous users.
                  Shared S3 keys (registered users, system presets, or other anonymous sessions outside this batch) are skipped. Partial failures are retryable.
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                  <div style={{ ...styles.summaryCard, borderColor: '#7f1d1d44' }} data-testid="content-batch-card">
                    <div style={{ ...styles.summaryCount, color: '#fbbf24' }}>
                      {anonData.s3_impact?.content_batch_size ?? anonData.summary?.expired_content_candidates ?? 0}
                    </div>
                    <div style={styles.summaryLabel}>Apply-Content Batch</div>
                    <div style={{ fontSize: 9, color: '#525252', marginTop: 2 }}>
                      of {anonData.s3_impact?.total_expired_content_candidates ?? anonData.summary?.expired_content_candidates ?? 0} expired
                      &nbsp;·&nbsp;limit {anonData.config?.limit || 100}
                      &nbsp;·&nbsp;TTL {anonData.config?.content_user_ttl_hours}h
                    </div>
                  </div>
                  <div style={{ ...styles.summaryCard, borderColor: '#7f1d1d44' }} data-testid="s3-safe-to-delete-card">
                    <div style={{ ...styles.summaryCount, color: '#a78bfa' }}>{anonData.s3_impact?.estimated_physical_delete_keys ?? anonData.s3_impact?.total_objects_affected ?? 0}</div>
                    <div style={styles.summaryLabel}>S3 Keys Safe to Delete</div>
                    <div style={{ fontSize: 9, color: '#525252', marginTop: 2 }}>{anonData.s3_impact?.safe_to_delete_bytes_human || anonData.s3_impact?.total_bytes_human || '0 B'}</div>
                  </div>
                  <div style={{ ...styles.summaryCard, borderColor: '#7f1d1d44' }} data-testid="s3-skipped-card">
                    <div style={{ ...styles.summaryCount, color: '#f59e0b' }}>{anonData.s3_impact?.skipped_counts?.total_skipped || 0}</div>
                    <div style={styles.summaryLabel}>S3 Keys Skipped (Safety)</div>
                    <div style={{ fontSize: 9, color: '#525252', marginTop: 2 }}>
                      shared anon: {anonData.s3_impact?.skipped_counts?.shared_with_non_candidate_user || 0} ·
                      registered: {anonData.s3_impact?.skipped_counts?.non_anonymous_reference || 0} ·
                      preset: {anonData.s3_impact?.skipped_counts?.system_preset || 0}
                    </div>
                  </div>
                </div>

                {/* Batch scope note */}
                {(anonData.s3_impact?.content_batch_size ?? 0) <
                 (anonData.s3_impact?.total_expired_content_candidates ?? 0) && (
                  <div style={{ marginTop: 8, padding: '6px 10px', background: '#0a0f1a', border: '1px solid #1e3a5f', borderRadius: 4, fontSize: 10, color: '#93c5fd' }} data-testid="batch-truncation-note">
                    <Info size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    {anonData.s3_impact.total_expired_content_candidates - anonData.s3_impact.content_batch_size}
                    {' more expired candidate(s) are outside this batch and will be processed on subsequent runs. Increase `limit` to widen the batch.'}
                  </div>
                )}

                {/* Content Deletion Estimates */}
                {anonData.content_deletion_estimates && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ color: '#525252', fontSize: 10, fontWeight: 700, marginBottom: 4 }}>Deletion Estimates</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {Object.entries(anonData.content_deletion_estimates).map(([k, v]) => (
                        <div key={k} style={styles.configItem}>
                          <span style={{ color: '#525252' }}>{k}:</span>
                          <span style={{ color: v > 0 ? '#f87171' : '#404040' }}>{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* S3 Impact Detail — by source family */}
                {anonData.s3_impact?.by_source && anonData.s3_impact.total_unique_s3_keys > 0 && (
                  <div style={{ marginTop: 10 }} data-testid="s3-impact-by-source">
                    <div style={{ color: '#525252', fontSize: 10, fontWeight: 700, marginBottom: 4 }}>S3 Impact — by Source (unique keys)</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      <div style={styles.configItem}>
                        <span style={{ color: '#525252' }}>asset_storage:</span>
                        <span style={{ color: '#a78bfa' }}>{anonData.s3_impact.by_source.asset_storage_keys}</span>
                      </div>
                      <div style={styles.configItem}>
                        <span style={{ color: '#525252' }}>asset_proxy:</span>
                        <span style={{ color: '#a78bfa' }}>{anonData.s3_impact.by_source.asset_proxy_keys}</span>
                      </div>
                      <div style={styles.configItem}>
                        <span style={{ color: '#525252' }}>proxy_only:</span>
                        <span style={{ color: '#a78bfa' }}>{anonData.s3_impact.by_source.proxy_only_asset_keys}</span>
                      </div>
                      <div style={styles.configItem}>
                        <span style={{ color: '#525252' }}>render_output:</span>
                        <span style={{ color: '#a78bfa' }}>{anonData.s3_impact.by_source.render_output_keys}</span>
                      </div>
                      <div style={styles.configItem}>
                        <span style={{ color: '#525252' }}>legacy_render:</span>
                        <span style={{ color: anonData.s3_impact.by_source.legacy_corrupt_render_keys > 0 ? '#f59e0b' : '#404040' }}>{anonData.s3_impact.by_source.legacy_corrupt_render_keys}</span>
                      </div>
                      <div style={styles.configItem}>
                        <span style={{ color: '#525252' }}>total_unique:</span>
                        <span style={{ color: '#a78bfa' }}>{anonData.s3_impact.total_unique_s3_keys}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Skipped S3 keys — show reason breakdown (truncated preview) */}
                {anonData.s3_impact?.skipped_s3_keys && anonData.s3_impact.skipped_s3_keys.length > 0 && (
                  <div style={{ marginTop: 10 }} data-testid="s3-skipped-keys-detail">
                    <div style={{ color: '#525252', fontSize: 10, fontWeight: 700, marginBottom: 4 }}>
                      Skipped S3 Keys (Safety) — showing {anonData.s3_impact.skipped_s3_keys.length}{anonData.s3_impact.skipped_s3_keys_truncated ? ' (truncated)' : ''}
                    </div>
                    <div style={{ background: '#0a0a0a', border: '1px solid #1f1f1f', borderRadius: 4, padding: 8, maxHeight: 160, overflowY: 'auto', fontSize: 9, fontFamily: 'monospace' }}>
                      {anonData.s3_impact.skipped_s3_keys.slice(0, 20).map((sk, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, paddingBottom: 2, color: '#a3a3a3' }}>
                          <span style={{ color: sk.reason === 'system_preset' ? '#fbbf24' : sk.reason === 'non_anonymous_reference' ? '#f87171' : '#fb923c', minWidth: 220 }}>[{sk.reason}]</span>
                          <span style={{ wordBreak: 'break-all' }}>{sk.key}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Content Breakdown */}
                {anonData.summary && (anonData.summary.anonymous_with_carousels > 0 || anonData.summary.anonymous_with_media_assets > 0) && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ color: '#525252', fontSize: 10, fontWeight: 700, marginBottom: 4 }}>Content Breakdown (all anon users)</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {[
                        ['Carousels', anonData.summary.anonymous_with_carousels],
                        ['Media Assets', anonData.summary.anonymous_with_media_assets],
                        ['Render Jobs', anonData.summary.anonymous_with_render_jobs],
                        ['Media Jobs', anonData.summary.anonymous_with_media_jobs],
                        ['Asset Refs', anonData.summary.anonymous_with_asset_references],
                      ].map(([label, count]) => (
                        <div key={label} style={styles.configItem}>
                          <span style={{ color: '#525252' }}>{label}:</span>
                          <span style={{ color: count > 0 ? '#fbbf24' : '#404040' }}>{count} users</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Expired Content Candidates */}
                {anonData.content_candidates && anonData.content_candidates.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ color: '#525252', fontSize: 10, fontWeight: 700, marginBottom: 4 }}>
                      Expired Content Candidates ({anonData.content_candidates.length})
                    </div>
                    <div style={{ maxHeight: 150, overflow: 'auto' }}>
                      {anonData.content_candidates.slice(0, 20).map(c => (
                        <div key={c.user_id} style={{ display: 'flex', gap: 12, padding: '3px 0', fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                          <span style={{ color: '#525252' }}>{c.user_id.substring(0, 16)}…</span>
                          <span style={{ color: '#a3a3a3' }}>{Math.round(c.age_hours)}h old</span>
                          <span style={{ color: '#fbbf24' }}>
                            c={c.content_counts?.carousels} a={c.content_counts?.media_assets} r={c.content_counts?.render_jobs}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Content Apply Result */}
                {anonContentResult && (
                  <div style={{ marginTop: 10, padding: '8px 10px', background: anonContentResult.s3_failed > 0 ? '#1a0a0a' : '#0a1a0a', borderRadius: 4, border: `1px solid ${anonContentResult.s3_failed > 0 ? '#7f1d1d44' : '#16653444'}` }}>
                    <div style={{ color: anonContentResult.s3_failed > 0 ? '#f59e0b' : '#4ade80', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>
                      {anonContentResult.s3_failed > 0 ? (
                        <><AlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Content Cleanup — Partial (retry needed)</>
                      ) : (
                        <><CheckCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Content Cleanup Applied</>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 10 }}>
                      {Object.entries(anonContentResult.deleted || {}).map(([k, v]) => (
                        <span key={k} style={{ color: '#525252' }}>{k}: <span style={{ color: v > 0 ? '#4ade80' : '#404040' }}>{v}</span></span>
                      ))}
                      <span style={{ color: '#525252' }}>s3_deleted: <span style={{ color: '#a78bfa' }}>{anonContentResult.s3_deleted || 0}</span></span>
                      <span style={{ color: '#525252' }}>s3_skipped: <span style={{ color: '#f59e0b' }}>{anonContentResult.s3_skipped || 0}</span></span>
                      <span style={{ color: '#525252' }}>s3_failed: <span style={{ color: (anonContentResult.s3_failed || 0) > 0 ? '#f87171' : '#404040' }}>{anonContentResult.s3_failed || 0}</span></span>
                      <span style={{ color: '#525252' }}>users_retained: <span style={{ color: (anonContentResult.users_retained_partial || 0) > 0 ? '#f59e0b' : '#404040' }}>{anonContentResult.users_retained_partial || 0}</span></span>
                      <span style={{ color: '#525252' }}>errors: <span style={{ color: (anonContentResult.errors?.length || 0) > 0 ? '#f87171' : '#404040' }}>{anonContentResult.errors?.length || 0}</span></span>
                    </div>
                    {anonContentResult.s3_failed_keys && anonContentResult.s3_failed_keys.length > 0 && (
                      <div style={{ marginTop: 6, fontSize: 9, color: '#f87171' }}>
                        <strong>Failed S3 keys:</strong>
                        {anonContentResult.s3_failed_keys.map((f, i) => (
                          <div key={i} style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#a3a3a3' }}>
                            {f.key} — {f.error}
                          </div>
                        ))}
                      </div>
                    )}
                    {anonContentResult.s3_skipped_keys && anonContentResult.s3_skipped_keys.length > 0 && (
                      <div style={{ marginTop: 6, fontSize: 9, color: '#f59e0b' }}>
                        <strong>Skipped S3 keys (shared):</strong>
                        {anonContentResult.s3_skipped_keys.map((f, i) => (
                          <div key={i} style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#a3a3a3' }}>
                            {f.key} — {f.reason}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Config Info */}
            {anonData && (
              <div style={{ ...styles.configCard, marginTop: 16 }}>
                <div style={{ color: '#525252', fontSize: 10, fontWeight: 700, marginBottom: 6 }}>Configuration</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <div style={styles.configItem}>
                    <span style={{ color: '#525252' }}>empty_ttl:</span>
                    <span style={{ color: '#a3a3a3' }}>{anonData.config?.empty_user_ttl_hours}h</span>
                  </div>
                  <div style={styles.configItem}>
                    <span style={{ color: '#525252' }}>content_ttl:</span>
                    <span style={{ color: '#a3a3a3' }}>{anonData.config?.content_user_ttl_hours}h</span>
                  </div>
                  <div style={styles.configItem}>
                    <span style={{ color: '#525252' }}>mode:</span>
                    <span style={{ color: '#a3a3a3' }}>manual_only</span>
                  </div>
                </div>
              </div>
            )}

            {/* ── EMPTY CONFIRMATION MODAL ── */}
            {anonEmptyModal && (
              <div style={styles.modalOverlay} onClick={() => setAnonEmptyModal(false)}>
                <div style={styles.confirmModalCard} onClick={e => e.stopPropagation()}>
                  <Shield size={32} color="#4ade80" style={{ marginBottom: 12 }} />
                  <h3 style={{ color: '#e5e5e5', fontSize: 15, fontWeight: 700, margin: '0 0 8px' }}>
                    Apply Empty Anonymous Cleanup
                  </h3>
                  <p style={{ color: '#a3a3a3', fontSize: 12, margin: '0 0 4px' }}>
                    This will delete <strong>{anonData?.summary?.expired_empty_candidates || 0}</strong> expired anonymous users with zero content.
                  </p>
                  <p style={{ color: '#525252', fontSize: 10, marginBottom: 16 }}>
                    No carousels, no uploads, no S3 objects. Only empty user rows. Safe for routine use.
                  </p>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ color: '#a3a3a3', fontSize: 10, display: 'block', marginBottom: 4 }}>
                      Type <code style={{ color: '#4ade80' }}>APPLY ANONYMOUS CLEANUP</code> to confirm:
                    </label>
                    <input
                      type="text"
                      value={anonEmptyText}
                      onChange={e => setAnonEmptyText(e.target.value)}
                      style={styles.modalInput}
                      autoFocus
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                    <button onClick={() => { setAnonEmptyModal(false); setAnonEmptyText(''); }} style={styles.modalCancelBtn}>
                      Cancel
                    </button>
                    <button
                      onClick={applyAnonEmpty}
                      disabled={anonEmptyText !== 'APPLY ANONYMOUS CLEANUP' || anonEmptyApplying}
                      style={{
                        ...styles.modalConfirmBtn,
                        background: anonEmptyText === 'APPLY ANONYMOUS CLEANUP' ? '#14532d' : '#262626',
                        color: anonEmptyText === 'APPLY ANONYMOUS CLEANUP' ? '#4ade80' : '#525252',
                        borderColor: anonEmptyText === 'APPLY ANONYMOUS CLEANUP' ? '#166534' : '#333',
                        cursor: anonEmptyText === 'APPLY ANONYMOUS CLEANUP' ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {anonEmptyApplying ? 'Applying…' : 'Apply Empty Cleanup'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── CONTENT CONFIRMATION MODAL ── */}
            {anonContentModal && (
              <div style={styles.modalOverlay} onClick={() => setAnonContentModal(false)}>
                <div style={styles.confirmModalCard} onClick={e => e.stopPropagation()}>
                  <AlertOctagon size={32} color="#f87171" style={{ marginBottom: 12 }} />
                  <h3 style={{ color: '#f87171', fontSize: 15, fontWeight: 700, margin: '0 0 8px' }}>
                    ⚠ Advanced: Apply Content Cleanup
                  </h3>
                  <p style={{ color: '#fca5a5', fontSize: 12, margin: '0 0 8px', textAlign: 'center' }}>
                    This will <strong>permanently delete</strong> expired anonymous users, their carousels, uploads,
                    generated renders, and <strong>physically delete anonymous-owned S3 files</strong>.
                  </p>
                  <div style={{ background: '#1a0a0a', border: '1px solid #7f1d1d44', borderRadius: 4, padding: '6px 8px', marginBottom: 12, fontSize: 10 }}>
                    <div style={{ color: '#f59e0b', marginBottom: 4 }}>Impact estimates:</div>
                    <div style={{ color: '#a3a3a3', fontFamily: "'IBM Plex Mono', monospace" }}>
                      Users: {anonData?.content_deletion_estimates?.users || 0} •
                      Carousels: {anonData?.content_deletion_estimates?.carousels || 0} •
                      Assets: {anonData?.content_deletion_estimates?.media_assets || 0} •
                      Renders: {anonData?.content_deletion_estimates?.render_jobs || 0} •
                      S3 delete: {anonData?.s3_impact?.estimated_physical_delete_keys ?? anonData?.s3_impact?.total_objects_affected ?? 0} keys ({anonData?.s3_impact?.safe_to_delete_bytes_human || anonData?.s3_impact?.total_bytes_human || '0 B'}) •
                      S3 skipped: {anonData?.s3_impact?.skipped_counts?.total_skipped || 0}
                    </div>
                  </div>
                  <p style={{ color: '#525252', fontSize: 10, marginBottom: 12, textAlign: 'center' }}>
                    Shared S3 keys (registered users, system presets, or non-candidate anonymous sessions) will be skipped.
                    Partial failures are retryable. No registered/Magic Forest content will be affected.
                  </p>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ color: '#a3a3a3', fontSize: 10, display: 'block', marginBottom: 4 }}>
                      Type <code style={{ color: '#f87171' }}>APPLY ANONYMOUS CONTENT CLEANUP</code> to confirm:
                    </label>
                    <input
                      type="text"
                      value={anonContentText}
                      onChange={e => setAnonContentText(e.target.value)}
                      style={styles.modalInput}
                      autoFocus
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                    <button onClick={() => { setAnonContentModal(false); setAnonContentText(''); }} style={styles.modalCancelBtn}>
                      Cancel
                    </button>
                    <button
                      onClick={applyAnonContent}
                      disabled={anonContentText !== 'APPLY ANONYMOUS CONTENT CLEANUP' || anonContentApplying}
                      style={{
                        ...styles.modalConfirmBtn,
                        background: anonContentText === 'APPLY ANONYMOUS CONTENT CLEANUP' ? '#7f1d1d' : '#262626',
                        color: anonContentText === 'APPLY ANONYMOUS CONTENT CLEANUP' ? '#fca5a5' : '#525252',
                        borderColor: anonContentText === 'APPLY ANONYMOUS CONTENT CLEANUP' ? '#991b1b' : '#333',
                        cursor: anonContentText === 'APPLY ANONYMOUS CONTENT CLEANUP' ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {anonContentApplying ? 'Applying…' : '⚠ Apply Content Cleanup'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}


// ── PR 8A.6a: Staged Cleanup Panel ──────────────────────────────────────

const MANIFEST_CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'expired_renders', label: 'Expired Renders' },
  { value: 'invalidated_renders', label: 'Invalidated Renders' },
  { value: 'soft_deleted_with_s3', label: 'Soft-Deleted with S3' },
  { value: 'orphaned_assets', label: 'Orphaned Assets' },
  { value: 'stale_uploads', label: 'Stale Uploads' },
  { value: 'unused_confirmed_assets', label: 'Unused Confirmed' },
  { value: 'possible_duplicate_size_groups', label: 'Possible Dupes (Size)' },
  { value: 'duplicate_groups', label: 'Duplicate Groups' },
  { value: 'legacy_unused_confirmed', label: '⚠ Legacy Unused Confirmed' },
];

function StagedCleanupPanel({
  headers, manifest, setManifest, manifestLoading, manifestError, setManifestError,
  manifestCategory, setManifestCategory, manifestMinAge, setManifestMinAge,
  manifestIncludeHigh, setManifestIncludeHigh, selectedItems, toggleItemSelection,
  selectByCategory, eligibleByCategory, isMixedSelection, selectionCategories,
  clearSelection, generateManifest, openConfirmModal, exportManifest,
  execResult, manifestHistory, historyLoading, fetchHistory, fetchCandidates, onPreview,
  fetchManifestDetail, manifestDetail, manifestDetailLoading, setManifestDetail,
  verifyDuplicates, dupVerification, dupVerifyLoading, setDupVerification,
}) {
  // PR 8A.8 — Duplicate reference rewrite modal (local to this panel)
  const [rewriteModal, setRewriteModal] = useState(null);
  const [rewriteResult, setRewriteResult] = useState(null);
  return (
    <>
      {/* Staged Cleanup info banner */}
      <div style={{ ...styles.readOnlyBanner, borderColor: '#166534' + '44', background: '#052e16' + '99', color: '#86efac' }}>
        <Play size={16} color="#4ade80" />
        <span>
          <strong>Staged Cleanup</strong> — generate a manifest, review evidence per item,
          then execute selected items. Deletable categories: <em>expired renders</em>, <em>invalidated renders</em>, <em>soft-deleted-with-S3</em>, <em>orphaned assets</em>, <em>verified duplicates</em>, and <em>legacy unused confirmed</em>.
        </span>
      </div>

      {manifestError && <div style={styles.errorBanner}>{manifestError}</div>}

      {/* ── Generate Manifest Form ── */}
      <div style={{ ...styles.tableCard, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <FileText size={16} color="#f59e0b" />
          <span style={{ color: '#e5e5e5', fontSize: 14, fontWeight: 600 }}>Generate Manifest</span>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={mStyles.label}>Category</label>
            <select value={manifestCategory} onChange={e => setManifestCategory(e.target.value)} style={mStyles.select}>
              {MANIFEST_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label style={mStyles.label}>Min Age (days)</label>
            <input type="number" min={0} value={manifestMinAge} onChange={e => setManifestMinAge(Number(e.target.value))} style={mStyles.input} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 4 }}>
            <input type="checkbox" checked={manifestIncludeHigh} onChange={e => setManifestIncludeHigh(e.target.checked)}
              id="incl-high" style={{ accentColor: '#f59e0b' }} />
            <label htmlFor="incl-high" style={{ color: '#a3a3a3', fontSize: 12, cursor: 'pointer' }}>Include HIGH risk</label>
          </div>
          <button onClick={generateManifest} disabled={manifestLoading} style={{ ...styles.tabBtnActive, padding: '8px 20px', marginBottom: 0 }}>
            {manifestLoading ? <><Loader size={13} className="animate-spin" /> Generating…</> : 'Generate Manifest'}
          </button>
        </div>
      </div>

      {/* ── Manifest Review ── */}
      {manifest && (
        <>
          {/* Summary Bar */}
          <div style={{ ...styles.totalsRow, gridTemplateColumns: 'repeat(5, 1fr)' }}>
            <div style={styles.totalCard}>
              <div style={styles.totalLabel}>Total Items</div>
              <div style={{ ...styles.totalValue, fontSize: 20 }}>{manifest.total_items}</div>
            </div>
            <div style={styles.totalCard}>
              <div style={styles.totalLabel}>Eligible</div>
              <div style={{ ...styles.totalValue, fontSize: 20, color: '#4ade80' }}>{manifest.eligible_items}</div>
            </div>
            <div style={styles.totalCard}>
              <div style={styles.totalLabel}>Blocked</div>
              <div style={{ ...styles.totalValue, fontSize: 20, color: '#f87171' }}>{manifest.blocked_items}</div>
            </div>
            <div style={styles.totalCard}>
              <div style={styles.totalLabel}>Total Size</div>
              <div style={{ ...styles.totalValue, fontSize: 20 }}>{manifest.total_size_human}</div>
            </div>
            <div style={styles.totalCard}>
              <div style={styles.totalLabel}>Selected</div>
              <div style={{ ...styles.totalValue, fontSize: 20, color: selectedItems.size > 0 ? '#f59e0b' : '#525252' }}>{selectedItems.size}</div>
            </div>
          </div>

          {/* Risk summary + Action bar */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            {manifest.risk_summary && Object.entries(manifest.risk_summary).map(([risk, count]) => {
              if (count === 0) return null;
              const colors = RISK_COLORS[risk] || { bg: '#1a1a1b', border: '#333', text: '#737373', label: risk.toUpperCase() };
              return (
                <span key={risk} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 10px', background: colors.bg, borderRadius: 4,
                  border: `1px solid ${colors.border}`, color: colors.text, fontSize: 11, fontWeight: 600,
                }}>
                  {colors.label}: {count}
                </span>
              );
            })}
            <span style={{ flex: 1 }} />
            <button onClick={() => exportManifest('csv')} style={styles.tabBtn}><Download size={13} /> CSV</button>
            <button onClick={() => exportManifest('json')} style={styles.tabBtn}><Download size={13} /> JSON</button>
            {/* Per-category selection buttons — no mixed "Select All" */}
            {Object.entries(eligibleByCategory).map(([cat, count]) => (
              <button
                key={cat}
                onClick={() => selectByCategory(cat)}
                style={{
                  ...styles.tabBtn,
                  borderColor: CATEGORY_META[cat]?.color || '#166534',
                  color: CATEGORY_META[cat]?.color || '#4ade80',
                  fontSize: 11,
                }}
              >
                Select {CATEGORY_META[cat]?.label || cat} ({count})
              </button>
            ))}
            {selectedItems.size > 0 && (
              <>
                <button onClick={clearSelection} style={styles.tabBtn}>Clear</button>
                {isMixedSelection && (
                  <span style={{
                    padding: '3px 10px', background: '#1c0a0a', borderRadius: 4,
                    border: '1px solid #991b1b', color: '#f87171', fontSize: 10, fontWeight: 600,
                  }}>
                    Mixed categories selected — clear and select one category
                  </span>
                )}
                {!isMixedSelection && (
                  <button onClick={openConfirmModal}
                    style={{ ...styles.tabBtn, background: '#7f1d1d', borderColor: '#991b1b', color: '#fca5a5', fontWeight: 600 }}>
                    <Trash2 size={13} /> Execute ({selectedItems.size})
                  </button>
                )}
              </>
            )}
          </div>

          {/* Legacy Unused Confirmed Warning */}
          {manifest.category_filter === 'legacy_unused_confirmed' && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              padding: '12px 14px', marginBottom: 16,
              background: '#1c0a0a', border: '1px solid #dc262655', borderRadius: 6,
            }}>
              <AlertOctagon size={18} color="#dc2626" style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 13, color: '#fca5a5', fontWeight: 700 }}>
                  Legacy Unused Confirmed — Manual / Legacy / Irreversible
                </span>
                <span style={{ fontSize: 11, color: '#d4d4d4', lineHeight: 1.6 }}>
                  These assets are confirmed and tracked, but not currently referenced by live carousels,
                  asset references, render jobs, or backups/system objects. They may include user-retained
                  library/workspace assets from before lifecycle tracking existed.
                  Delete only if you accept that this legacy state cannot be reliably classified.
                </span>
                <span style={{ fontSize: 10, color: '#737373', fontStyle: 'italic' }}>
                  This is NOT a normal safe cleanup category. Requires manual selection and typed confirmation.
                </span>
              </div>
            </div>
          )}

          {/* Manifest Items Table */}
          <div style={styles.tableCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ color: '#e5e5e5', fontSize: 14, fontWeight: 600 }}>Manifest Items</span>
              <span style={{ color: '#525252', fontSize: 11, fontFamily: "'IBM Plex Mono', monospace" }}>
                {manifest.manifest_id}
              </span>
            </div>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, width: 32 }}></th>
                    <th style={styles.th}>Category</th>
                    <th style={styles.th}>ID</th>
                    <th style={styles.th}>Object Key</th>
                    <th style={styles.th}>Kind</th>
                    <th style={styles.th}>Size</th>
                    <th style={styles.th}>Lifecycle</th>
                    <th style={styles.th}>Risk</th>
                    <th style={styles.th}>Recommendation</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {manifest.items.map((item, idx) => {
                    const isSelected = selectedItems.has(item.item_id);
                    const eligible = item.cleanup_eligible;
                    const meta = CATEGORY_META[item.category] || { color: '#737373' };
                    const lcState = item.lifecycle_state;
                    const lcBlockers = item.lifecycle_blockers || [];
                    const lcColor = lcState === 'active' ? '#f87171' : lcState === 'unused' ? '#fbbf24' : lcState === 'removed' ? '#c084fc' : lcState === 'deleted' ? '#525252' : '#3f3f46';
                    return (
                      <tr key={item.item_id} style={idx % 2 === 0 ? styles.trEven : {}}>
                        <td style={styles.td}>
                          {eligible ? (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleItemSelection(item.item_id)}
                              style={{ accentColor: '#f59e0b', cursor: 'pointer' }}
                            />
                          ) : (
                            <span title="Not eligible" style={{ color: '#3a3a3a', fontSize: 14 }}>—</span>
                          )}
                        </td>
                        <td style={styles.td}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ width: 3, height: 12, borderRadius: 1, background: meta.color, display: 'inline-block' }} />
                            <span style={{ fontSize: 10, color: '#a3a3a3' }}>{CATEGORY_META[item.category]?.label || item.category}</span>
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span title={item.asset_id || item.job_id || '—'} style={{ cursor: 'help' }}>
                            {_truncId(item.asset_id || item.job_id)}
                          </span>
                        </td>
                        <td style={styles.tdKey} title={item.object_key}>{_truncateKey(item.object_key)}</td>
                        <td style={styles.td}>{item.kind || '—'}</td>
                        <td style={styles.td}>{item.size_bytes > 0 ? _humanBytes(item.size_bytes) : '—'}</td>
                        <td style={styles.td}>
                          {item.item_type === 'media_asset' && lcState ? (
                            <span title={lcBlockers.length > 0 ? lcBlockers.join('\n') : (item.lifecycle_reason || '')} style={{
                              padding: '1px 6px', borderRadius: 3, fontSize: 9, fontWeight: 700, letterSpacing: 0.3,
                              background: lcState === 'active' ? '#1c0a0a' : lcState === 'unused' ? '#1c1917' : lcState === 'removed' ? '#1e1b4b' : '#0a0a0a',
                              border: `1px solid ${lcColor}44`,
                              color: lcColor, cursor: lcBlockers.length > 0 ? 'help' : 'default',
                            }}>
                              {lcState.toUpperCase()}{lcBlockers.length > 0 ? ' ⊘' : ''}
                            </span>
                          ) : item.item_type === 'render_output' ? (
                            <span style={{ fontSize: 9, color: '#3f3f46' }}>n/a</span>
                          ) : '—'}
                        </td>
                        <td style={styles.td}><ManifestRiskBadge risk={item.risk_level} /></td>
                        <td style={{ ...styles.td, maxWidth: 250, fontSize: 10, color: eligible ? '#86efac' : '#737373' }}>
                          {item.cleanup_recommendation}
                        </td>
                        <td style={styles.td}>
                          <button onClick={() => onPreview(item.object_key)} style={styles.previewBtn} title="Preview">
                            <Eye size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Warnings */}
          {manifest.warnings && manifest.warnings.length > 0 && (
            <div style={{ ...styles.readOnlyBanner, borderColor: '#854d0e44', marginTop: 0 }}>
              <AlertTriangle size={14} color="#fbbf24" />
              <span style={{ fontSize: 12, color: '#d4a574' }}>{manifest.warnings.join(' ')}</span>
            </div>
          )}
        </>
      )}

      {/* ── Execution Result ── */}
      {execResult && (() => {
        const hasFailures = execResult.failed > 0;
        const allSkipped = execResult.deleted === 0 && execResult.skipped > 0;
        const borderColor = hasFailures ? '#991b1b' : allSkipped ? '#854d0e' : '#166534';
        const statusColor = hasFailures ? '#f87171' : allSkipped ? '#fbbf24' : '#4ade80';
        const StatusIcon = hasFailures ? XCircle : allSkipped ? AlertTriangle : CheckCircle;
        const statusLabel = hasFailures
          ? `Execution Result — ${execResult.status}`
          : allSkipped
            ? `Execution Result — All Items Skipped (0 deleted)`
            : `Execution Result — ${execResult.status}`;
        return (
        <div style={{ ...styles.tableCard, borderColor }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <StatusIcon size={18} color={statusColor} />
            <span style={{ color: '#e5e5e5', fontSize: 14, fontWeight: 600 }}>
              {statusLabel}
            </span>
          </div>
          {allSkipped && (
            <div style={{
              background: '#1c1917', border: '1px solid #854d0e', borderRadius: 6,
              padding: '8px 12px', marginBottom: 12, fontSize: 11, color: '#fbbf24',
            }}>
              No items were deleted. All selected items were skipped during revalidation.
              Check the per-item reasons below — items may have become referenced or their state changed since the manifest was generated.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
            <ExecStat label="Deleted" value={execResult.deleted} color="#4ade80" icon={CheckCircle} />
            <ExecStat label="Skipped" value={execResult.skipped} color="#fbbf24" icon={SkipForward} />
            <ExecStat label="Failed" value={execResult.failed} color="#f87171" icon={XCircle} />
            <ExecStat label="Reclaimed" value={execResult.bytes_reclaimed_human} color="#e5e5e5" icon={Archive} />
          </div>
          {/* Per-item results */}
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Item</th>
                  <th style={styles.th}>Category</th>
                  <th style={styles.th}>Object Key</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {execResult.items.map((ir, idx) => (
                  <tr key={ir.item_id} style={idx % 2 === 0 ? styles.trEven : {}}>
                    <td style={styles.td}>{_truncId(ir.item_id)}</td>
                    <td style={styles.td}>{CATEGORY_META[ir.category]?.label || ir.category}</td>
                    <td style={styles.tdKey} title={ir.object_key}>{_truncateKey(ir.object_key)}</td>
                    <td style={styles.td}>
                      <span style={{
                        padding: '1px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                        background: ir.status === 'deleted' ? '#052e16' : ir.status === 'skipped' ? '#1c1917' : '#1c0a0a',
                        border: `1px solid ${ir.status === 'deleted' ? '#166534' : ir.status === 'skipped' ? '#854d0e' : '#991b1b'}`,
                        color: ir.status === 'deleted' ? '#4ade80' : ir.status === 'skipped' ? '#fbbf24' : '#f87171',
                      }}>
                        {ir.status.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ ...styles.td, fontSize: 10, maxWidth: 300 }}>{ir.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={() => { fetchCandidates(); generateManifest(); }}
            style={{ ...styles.tabBtnActive, marginTop: 12, padding: '8px 18px' }}>
            <RefreshCw size={13} /> Refresh Diagnostics
          </button>
        </div>
        );
      })()}

      {/* ── Duplicate Hash Verification ── */}
      <div style={{ ...styles.tableCard, marginTop: 16 }} data-testid="duplicate-hash-verification-panel">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <ScatterChart size={16} color="#a855f7" />
          <span style={{ color: '#e5e5e5', fontSize: 14, fontWeight: 600 }}>Duplicate Hash Verification</span>
          <button
            data-testid="verify-duplicates-btn"
            onClick={verifyDuplicates}
            disabled={dupVerifyLoading}
            style={{ ...styles.tabBtn, marginLeft: 'auto' }}
          >
            {dupVerifyLoading
              ? <><Loader size={11} className="animate-spin" style={{ display: 'inline-block' }} /> Verifying…</>
              : <><RefreshCw size={11} /> Verify Hashes</>}
          </button>
        </div>
        <div style={{ ...styles.readOnlyBanner, marginBottom: 12 }}>
          <Shield size={12} />
          <span style={{ fontSize: 11 }}>
            Computes SHA-256 content hashes for size-only duplicate candidates.
            Classifies each non-canonical member as safe / rewrite-possible / blocked / unclear.
            Does NOT delete anything. Reference rewrite requires typed confirmation.
          </span>
        </div>
        {dupVerification && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 12 }}>
              <div style={{ background: '#0a0a0a', border: '1px solid #1f1f1f', borderRadius: 6, padding: '8px 12px' }} data-testid="dupcard-verified">
                <div style={{ color: '#737373', fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>Verified Dups</div>
                <div style={{ color: '#4ade80', fontSize: 18, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>
                  {dupVerification.groups_verified}
                </div>
              </div>
              <div style={{ background: '#0a0a0a', border: '1px solid #1f1f1f', borderRadius: 6, padding: '8px 12px' }} data-testid="dupcard-not-duplicate">
                <div style={{ color: '#737373', fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>Not Duplicate</div>
                <div style={{ color: '#fbbf24', fontSize: 18, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>
                  {dupVerification.groups_not_duplicate}
                </div>
              </div>
              <div style={{ background: '#0a0a0a', border: '1px solid #1f1f1f', borderRadius: 6, padding: '8px 12px' }} data-testid="dupcard-hash-missing">
                <div style={{ color: '#737373', fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>Hash Missing</div>
                <div style={{ color: '#525252', fontSize: 18, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>
                  {dupVerification.groups_hash_missing}
                </div>
              </div>
              <div style={{ background: '#0a0a0a', border: '1px solid #1f1f1f', borderRadius: 6, padding: '8px 12px' }} data-testid="dupcard-reclaimable">
                <div style={{ color: '#737373', fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>Reclaimable</div>
                <div style={{ color: '#e5e5e5', fontSize: 18, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>
                  {dupVerification.total_reclaimable_human}
                </div>
              </div>
            </div>
            {dupVerification.reachability_breakdown && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 8, fontSize: 10, color: '#a3a3a3', fontFamily: "'IBM Plex Mono', monospace" }} data-testid="reachability-breakdown">
                <span style={{ color: '#4ade80' }}>safe: {dupVerification.reachability_breakdown.safe_duplicate_unused}</span>
                <span style={{ color: '#06b6d4' }}>rewrite-possible: {dupVerification.reachability_breakdown.duplicate_referenced_rewrite_possible}</span>
                <span style={{ color: '#ef4444' }}>blocked: {dupVerification.reachability_breakdown.duplicate_referenced_blocked}</span>
                <span style={{ color: '#525252' }}>unclear: {dupVerification.reachability_breakdown.duplicate_unclear}</span>
                <span style={{ color: '#737373', marginLeft: 'auto' }}>
                  duplicate_of_asset_id writes: {dupVerification.duplicate_of_asset_id_writes || 0}
                </span>
              </div>
            )}
            <div style={{ color: '#737373', fontSize: 10, marginBottom: 8 }}>
              Hashes computed this run: {dupVerification.hashes_computed} | Failed: {dupVerification.hashes_failed}
            </div>
            {dupVerification.groups.map((g, gi) => (
              <div key={gi} data-testid={`dup-group-${g.group_id || gi}`} style={{
                background: '#0a0a0a', border: '1px solid #1f1f1f', borderRadius: 6,
                padding: '10px 14px', marginBottom: 8,
              }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{
                    padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                    background: g.classification === 'verified_duplicate' ? '#052e16'
                      : g.classification === 'not_duplicate_hash_mismatch' ? '#1c1917' : '#0a0a0a',
                    border: `1px solid ${g.classification === 'verified_duplicate' ? '#166534'
                      : g.classification === 'not_duplicate_hash_mismatch' ? '#854d0e' : '#333'}`,
                    color: g.classification === 'verified_duplicate' ? '#4ade80'
                      : g.classification === 'not_duplicate_hash_mismatch' ? '#fbbf24' : '#a3a3a3',
                  }}>{g.classification.replace(/_/g, ' ').toUpperCase()}</span>
                  {g.group_id && (
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: '#525252' }}>
                      {g.group_id}
                    </span>
                  )}
                  <span style={{ color: '#a3a3a3', fontSize: 11 }}>{g.kind} · {g.content_type} · {g.size_bytes_human}</span>
                  <span style={{ color: '#525252', fontSize: 11 }}>× {g.group_count}</span>
                  {g.content_hash_prefix && (
                    <span style={{ color: '#525252', fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                      hash: {g.content_hash_prefix}
                    </span>
                  )}
                  {g.excess_copies_count > 0 && (
                    <span style={{ color: '#4ade80', fontSize: 10 }}>
                      {g.excess_copies_count} excess · {g.potential_reclaimable_human} reclaimable
                    </span>
                  )}
                </div>
                {g.canonical_reason && (
                  <div style={{ color: '#737373', fontSize: 10, marginBottom: 6 }}>
                    Canonical pick reason: {g.canonical_reason}
                  </div>
                )}
                {g.members.map((m, mi) => {
                  const r = m.reachability || {};
                  const rState = r.reachability_state;
                  const rColor = rState === 'safe_duplicate_unused' ? '#4ade80'
                    : rState === 'duplicate_referenced_rewrite_possible' ? '#06b6d4'
                    : rState === 'duplicate_referenced_blocked' ? '#ef4444'
                    : rState === 'duplicate_unclear' ? '#a3a3a3'
                    : '#525252';
                  const refsTotal = (r.carousel_reference_count || 0)
                    + (r.asset_references_count || 0)
                    + (r.render_jobs_count || 0);
                  return (
                    <div key={mi} data-testid={`dup-member-${m.asset_id}`} style={{
                      display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0',
                      fontSize: 10, color: m.is_canonical ? '#4ade80' : '#a3a3a3',
                      flexWrap: 'wrap',
                    }}>
                      {m.is_canonical && <span title="Canonical">★</span>}
                      <span style={{ fontFamily: "'IBM Plex Mono', monospace", minWidth: 200 }}>{m.asset_id}</span>
                      {m.hash_available
                        ? <span style={{ color: '#525252' }} title={m.content_hash}>hash: {(m.content_hash || '').slice(0, 12)}…</span>
                        : <span style={{ color: '#991b1b' }}>no hash</span>}
                      {m.orphaned_at && <span style={{ color: '#f59e0b' }}>orphaned</span>}
                      {!m.is_canonical && rState && (
                        <span style={{
                          padding: '1px 6px', borderRadius: 3, fontSize: 9, fontWeight: 600,
                          color: rColor, border: `1px solid ${rColor}55`,
                          background: `${rColor}11`, textTransform: 'uppercase',
                          letterSpacing: 0.3,
                        }}>
                          {rState.replace(/_/g, ' ').replace('duplicate ', '')}
                        </span>
                      )}
                      {!m.is_canonical && refsTotal > 0 && (
                        <span style={{ color: '#737373' }}>
                          refs: c{r.carousel_reference_count || 0} · a{r.asset_references_count || 0} · r{r.render_jobs_count || 0}
                        </span>
                      )}
                      {!m.is_canonical && rState === 'duplicate_referenced_rewrite_possible' && (
                        <button
                          data-testid={`rewrite-btn-${m.asset_id}`}
                          onClick={() => setRewriteModal({
                            duplicate_asset_id: m.asset_id,
                            canonical_asset_id: g.canonical_asset_id,
                            confirmation_text: '',
                            preview: r,
                          })}
                          style={{
                            marginLeft: 'auto',
                            padding: '2px 8px', fontSize: 9, fontWeight: 600,
                            background: '#0c4a6e', color: '#7dd3fc',
                            border: '1px solid #075985', borderRadius: 3,
                            cursor: 'pointer',
                          }}
                        >
                          Rewrite → Canonical
                        </button>
                      )}
                      {m.duplicate_of_asset_id && (
                        <span style={{ color: '#525252', fontSize: 9 }} title={m.duplicate_of_asset_id}>
                          dup_of: {m.duplicate_of_asset_id.slice(0, 14)}…
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Duplicate Reference Rewrite Modal ── */}
      {rewriteModal && (
        <div style={styles.modalOverlay} data-testid="rewrite-modal" onClick={() => setRewriteModal(null)}>
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              <ArrowRightLeft size={16} /> Rewrite Duplicate References
            </h3>
            <div style={{ color: '#a3a3a3', fontSize: 12, marginBottom: 12 }}>
              This will rewrite carousel JSONB references from the duplicate to the canonical asset.
              Only the following fields are touched (closed list):
              <ul style={{ margin: '6px 0 0 18px', padding: 0, color: '#737373', fontSize: 11 }}>
                <li>document.logo_asset_id</li>
                <li>slides[].bgAssetId</li>
                <li>slides[].imageWidget.asset_id</li>
              </ul>
              <strong style={{ color: '#fbbf24', display: 'block', marginTop: 8, fontSize: 11 }}>
                videoWidget.asset_id is NEVER rewritten by this action.
              </strong>
            </div>
            <div style={{ background: '#0a0a0a', border: '1px solid #1f1f1f', borderRadius: 6, padding: '10px 12px', marginBottom: 12, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>
              <div>duplicate_asset_id: <span style={{ color: '#fbbf24' }}>{rewriteModal.duplicate_asset_id}</span></div>
              <div>canonical_asset_id: <span style={{ color: '#4ade80' }}>{rewriteModal.canonical_asset_id}</span></div>
              {rewriteModal.preview && (
                <div style={{ marginTop: 6, color: '#737373' }}>
                  Will rewrite {rewriteModal.preview.rewrite_eligible_count} ref(s) in fields:
                  {' '}{(rewriteModal.preview.rewrite_eligible_fields || []).join(', ')}
                </div>
              )}
            </div>
            <div style={{ color: '#a3a3a3', fontSize: 11, marginBottom: 6 }}>
              Type <code style={{ color: '#fbbf24' }}>REWRITE DUPLICATE REFERENCES</code> to confirm:
            </div>
            <input
              data-testid="rewrite-confirmation-input"
              type="text"
              value={rewriteModal.confirmation_text || ''}
              onChange={e => setRewriteModal({ ...rewriteModal, confirmation_text: e.target.value })}
              placeholder="REWRITE DUPLICATE REFERENCES"
              style={styles.modalInput}
              autoFocus
            />
            {rewriteResult && (
              <div style={{
                marginTop: 10, padding: '8px 12px', borderRadius: 6, fontSize: 11,
                background: rewriteResult.ok ? '#052e16' : '#7f1d1d',
                color: rewriteResult.ok ? '#86efac' : '#fca5a5',
                border: `1px solid ${rewriteResult.ok ? '#166534' : '#991b1b'}`,
              }}>
                {rewriteResult.ok
                  ? `Rewrote ${rewriteResult.rewritten_carousels} carousel(s) · post_rewrite_clean=${rewriteResult.post_rewrite_clean}`
                  : `Error: ${rewriteResult.detail || rewriteResult.error || 'unknown'}`}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
              <button
                data-testid="rewrite-cancel-btn"
                onClick={() => { setRewriteModal(null); setRewriteResult(null); }}
                style={styles.modalCancelBtn}
              >Cancel</button>
              <button
                data-testid="rewrite-confirm-btn"
                onClick={async () => {
                  setRewriteResult(null);
                  try {
                    const resp = await fetch(
                      `${BACKEND_URL}/api/admin/cleanup/rewrite-duplicate-references`,
                      {
                        method: 'POST',
                        headers: { ...headers(), 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          duplicate_asset_id: rewriteModal.duplicate_asset_id,
                          canonical_asset_id: rewriteModal.canonical_asset_id,
                          confirmation: rewriteModal.confirmation_text,
                        }),
                      }
                    );
                    const data = await resp.json();
                    if (resp.ok) {
                      setRewriteResult({ ok: true, ...data });
                      // refresh verification view
                      setTimeout(() => verifyDuplicates(), 600);
                    } else {
                      setRewriteResult({ ok: false, detail: data.detail || `HTTP ${resp.status}` });
                    }
                  } catch (e) {
                    setRewriteResult({ ok: false, detail: e.message });
                  }
                }}
                disabled={rewriteModal.confirmation_text !== 'REWRITE DUPLICATE REFERENCES'}
                style={{
                  ...styles.modalConfirmBtn,
                  opacity: rewriteModal.confirmation_text === 'REWRITE DUPLICATE REFERENCES' ? 1 : 0.4,
                  cursor: rewriteModal.confirmation_text === 'REWRITE DUPLICATE REFERENCES' ? 'pointer' : 'not-allowed',
                }}
              >Confirm Rewrite</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Manifest History ── */}
      <div style={{ ...styles.tableCard, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Clock size={16} color="#525252" />
          <span style={{ color: '#e5e5e5', fontSize: 14, fontWeight: 600 }}>Manifest History</span>
          <button onClick={fetchHistory} disabled={historyLoading}
            style={{ ...styles.previewBtn, marginLeft: 'auto', padding: '4px 10px', gap: 4, display: 'inline-flex', alignItems: 'center' }}>
            <RefreshCw size={11} className={historyLoading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
        {!manifestHistory ? (
          <div style={{ color: '#525252', fontSize: 12, textAlign: 'center', padding: 20 }}>Loading…</div>
        ) : manifestHistory.manifests.length === 0 ? (
          <div style={{ color: '#525252', fontSize: 12, textAlign: 'center', padding: 20 }}>No manifests generated yet.</div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Manifest ID</th>
                  <th style={styles.th}>Generated</th>
                  <th style={styles.th}>Filter</th>
                  <th style={styles.th}>Items</th>
                  <th style={styles.th}>Eligible</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Deleted</th>
                  <th style={styles.th}>Failed</th>
                  <th style={styles.th}>Reclaimed</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {manifestHistory.manifests.map((m, idx) => (
                  <tr key={m.manifest_id} style={idx % 2 === 0 ? styles.trEven : {}}>
                    <td style={styles.td}><span style={{ cursor: 'help' }} title={m.manifest_id}>{_truncId(m.manifest_id)}</span></td>
                    <td style={styles.td}>{_timeAgo(m.generated_at)}</td>
                    <td style={styles.td}>{m.category_filter}</td>
                    <td style={styles.td}>{m.total_items}</td>
                    <td style={styles.td}>{m.eligible_items}</td>
                    <td style={styles.td}>
                      <span style={{
                        padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                        background: m.status === 'completed' ? '#052e16'
                          : m.status === 'partially_completed' ? '#1c1917'
                          : m.status === 'failed' ? '#1c0a0a'
                          : m.status === 'generated' ? '#1a1a1b' : '#1c1917',
                        border: `1px solid ${m.status === 'completed' ? '#166534'
                          : m.status === 'partially_completed' ? '#854d0e'
                          : m.status === 'failed' ? '#991b1b'
                          : m.status === 'generated' ? '#333' : '#854d0e'}`,
                        color: m.status === 'completed' ? '#4ade80'
                          : m.status === 'partially_completed' ? '#fbbf24'
                          : m.status === 'failed' ? '#f87171'
                          : m.status === 'generated' ? '#a3a3a3' : '#fbbf24',
                      }}>
                        {m.status}
                      </span>
                    </td>
                    <td style={styles.td}>{m.items_deleted > 0 ? m.items_deleted : '—'}</td>
                    <td style={styles.td}>{m.items_failed > 0
                      ? <span style={{ color: '#f87171', fontWeight: 600 }}>{m.items_failed}</span>
                      : '—'}</td>
                    <td style={styles.td}>{m.bytes_reclaimed > 0 ? m.bytes_reclaimed_human : '—'}</td>
                    <td style={styles.td}>
                      <button
                        onClick={() => fetchManifestDetail(m.manifest_id)}
                        style={styles.previewBtn}
                        title="Inspect manifest detail"
                        disabled={manifestDetailLoading}
                      >
                        <Search size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Manifest Detail / Recovery Diagnostics ── */}
      {manifestDetailLoading && (
        <div style={{ ...styles.tableCard, marginTop: 16, textAlign: 'center', padding: 24 }}>
          <Loader size={16} className="animate-spin" style={{ display: 'inline-block', marginRight: 8 }} />
          <span style={{ color: '#a3a3a3', fontSize: 12 }}>Loading manifest detail…</span>
        </div>
      )}
      {manifestDetail && (
        <ManifestDetailPanel detail={manifestDetail} onClose={() => setManifestDetail(null)} onPreview={onPreview} />
      )}
    </>
  );
}


// ── PR 8A.6a hotfix: Manifest Detail Panel ──────────────────────────────

function ManifestDetailPanel({ detail, onClose, onPreview }) {
  const diag = detail.diagnostics || {};
  const statusColor = (st) => {
    if (st === 'success') return '#4ade80';
    if (st === 'failed') return '#f87171';
    if (st === 'skipped') return '#fbbf24';
    return '#525252';
  };
  const resultBadge = (result) => {
    if (!result) return <span style={{ color: '#525252', fontSize: 10 }}>—</span>;
    return (
      <span style={{
        padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
        background: result === 'success' ? '#052e16' : result === 'failed' ? '#1c0a0a' : result === 'skipped' ? '#1c1917' : '#0a0a0a',
        border: `1px solid ${result === 'success' ? '#166534' : result === 'failed' ? '#991b1b' : result === 'skipped' ? '#854d0e' : '#333'}`,
        color: statusColor(result),
      }}>
        {result.toUpperCase()}
      </span>
    );
  };

  return (
    <div style={{ ...styles.tableCard, marginTop: 16, borderColor: diag.items_failed > 0 ? '#991b1b' : '#262626' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Search size={16} color="#f59e0b" />
        <span style={{ color: '#e5e5e5', fontSize: 14, fontWeight: 600 }}>
          Manifest Detail — Recovery Diagnostics
        </span>
        <span style={{ color: '#525252', fontSize: 11, fontFamily: "'IBM Plex Mono', monospace" }}>
          {detail.manifest_id}
        </span>
        <button onClick={onClose}
          style={{ ...styles.previewBtn, marginLeft: 'auto', padding: '4px 10px', gap: 4, display: 'inline-flex', alignItems: 'center' }}>
          <X size={11} /> Close
        </button>
      </div>

      {/* Diagnostics Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 16 }}>
        <div style={{ background: '#0a0a0a', border: '1px solid #1f1f1f', borderRadius: 6, padding: '8px 12px' }}>
          <div style={{ color: '#737373', fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>Deleted</div>
          <div style={{ color: '#4ade80', fontSize: 18, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>
            {diag.items_deleted_successfully || 0}
          </div>
        </div>
        <div style={{ background: '#0a0a0a', border: '1px solid #1f1f1f', borderRadius: 6, padding: '8px 12px' }}>
          <div style={{ color: '#737373', fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>Failed</div>
          <div style={{ color: diag.items_failed > 0 ? '#f87171' : '#525252', fontSize: 18, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>
            {diag.items_failed || 0}
          </div>
        </div>
        <div style={{ background: '#0a0a0a', border: '1px solid #1f1f1f', borderRadius: 6, padding: '8px 12px' }}>
          <div style={{ color: '#737373', fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>Skipped</div>
          <div style={{ color: '#fbbf24', fontSize: 18, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>
            {diag.items_skipped || 0}
          </div>
        </div>
        <div style={{ background: '#0a0a0a', border: '1px solid #1f1f1f', borderRadius: 6, padding: '8px 12px' }}>
          <div style={{ color: '#737373', fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>No Action</div>
          <div style={{ color: '#525252', fontSize: 18, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>
            {diag.items_no_action || 0}
          </div>
        </div>
        <div style={{ background: '#0a0a0a', border: '1px solid #1f1f1f', borderRadius: 6, padding: '8px 12px' }}>
          <div style={{ color: '#737373', fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>Total Actions</div>
          <div style={{ color: '#e5e5e5', fontSize: 18, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>
            {diag.total_actions || 0}
          </div>
        </div>
      </div>

      {/* Status banner */}
      <div style={{
        ...styles.readOnlyBanner,
        borderColor: detail.status === 'completed' ? '#16653444' : detail.status === 'failed' ? '#991b1b44' : '#854d0e44',
        background: detail.status === 'completed' ? '#052e1699' : detail.status === 'failed' ? '#1c0a0a99' : '#1c191799',
        marginBottom: 16,
      }}>
        {detail.status === 'completed' ? <CheckCircle size={14} color="#4ade80" /> :
         detail.status === 'failed' ? <XCircle size={14} color="#f87171" /> :
         <AlertTriangle size={14} color="#fbbf24" />}
        <span style={{ fontSize: 12, color: '#d4d4d4' }}>
          Manifest status: <strong>{detail.status}</strong>
          {detail.executed_at && <> · Executed: {_timeAgo(detail.executed_at)}</>}
          {detail.executed_by && <> · By: {detail.executed_by}</>}
        </span>
      </div>

      {/* Failed items detail */}
      {diag.failed_items_detail && diag.failed_items_detail.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: '#f87171', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
            Failed Items
          </div>
          {diag.failed_items_detail.map(fi => (
            <div key={fi.item_id} style={{
              background: '#1c0a0a', border: '1px solid #991b1b', borderRadius: 6, padding: '8px 12px', marginBottom: 6,
            }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                <XCircle size={12} color="#f87171" />
                <span style={{ fontSize: 11, color: '#fca5a5', fontFamily: "'IBM Plex Mono', monospace" }}>
                  {fi.item_id}
                </span>
                <span style={{ fontSize: 10, color: '#a3a3a3' }}>{CATEGORY_META[fi.category]?.label || fi.category}</span>
              </div>
              <div style={{ fontSize: 10, color: '#f87171', fontFamily: "'IBM Plex Mono', monospace", wordBreak: 'break-all' }}>
                {fi.delete_error || 'Unknown error'}
              </div>
              {fi.object_key && (
                <div style={{ fontSize: 10, color: '#525252', marginTop: 4 }}>
                  Key: {fi.object_key}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* All items table */}
      <div style={{ color: '#a3a3a3', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
        All Items ({detail.items?.length || 0})
      </div>
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Item ID</th>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>Object Key</th>
              <th style={styles.th}>Revalidation</th>
              <th style={styles.th}>Delete Result</th>
              <th style={styles.th}>Error</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(detail.items || []).map((item, idx) => (
              <tr key={item.item_id} style={idx % 2 === 0 ? styles.trEven : {}}>
                <td style={styles.td}>
                  <span title={item.item_id} style={{ cursor: 'help' }}>{_truncId(item.item_id)}</span>
                </td>
                <td style={styles.td}>
                  <span style={{ fontSize: 10, color: '#a3a3a3' }}>{CATEGORY_META[item.category]?.label || item.category}</span>
                </td>
                <td style={styles.tdKey} title={item.object_key}>{_truncateKey(item.object_key)}</td>
                <td style={styles.td}>{resultBadge(item.revalidation_result)}</td>
                <td style={styles.td}>{resultBadge(item.delete_result)}</td>
                <td style={{ ...styles.td, maxWidth: 200, fontSize: 10, color: '#f87171', wordBreak: 'break-all' }}>
                  {item.delete_error || '—'}
                </td>
                <td style={styles.td}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {item.object_key && (
                      <button onClick={() => onPreview(item.object_key)} style={styles.previewBtn} title="Preview">
                        <Eye size={12} />
                      </button>
                    )}
                    {item.actions && item.actions.length > 0 && (
                      <span title={`${item.actions.length} action(s) recorded`}
                        style={{ fontSize: 10, color: '#525252', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                        <Activity size={10} /> {item.actions.length}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Raw Actions Log */}
      {detail.all_actions && detail.all_actions.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ color: '#a3a3a3', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
            Audit Actions ({detail.all_actions.length})
          </div>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Action</th>
                  <th style={styles.th}>Item</th>
                  <th style={styles.th}>Precheck</th>
                  <th style={styles.th}>Delete Result</th>
                  <th style={styles.th}>Verification</th>
                  <th style={styles.th}>Error</th>
                  <th style={styles.th}>Time</th>
                </tr>
              </thead>
              <tbody>
                {detail.all_actions.map((a, idx) => (
                  <tr key={a.action_id} style={idx % 2 === 0 ? styles.trEven : {}}>
                    <td style={styles.td}>
                      <span style={{ color: a.action === 'delete_s3' ? '#4ade80' : a.action === 'fail' ? '#f87171' : '#fbbf24', fontSize: 11, fontWeight: 600 }}>
                        {a.action}
                      </span>
                    </td>
                    <td style={styles.td}>{_truncId(a.item_id)}</td>
                    <td style={styles.td}>{resultBadge(a.precheck_result)}</td>
                    <td style={styles.td}>{resultBadge(a.delete_result)}</td>
                    <td style={styles.td}>
                      <span style={{ fontSize: 10, color: a.verification_result === 'confirmed_deleted' ? '#4ade80' : '#525252' }}>
                        {a.verification_result || '—'}
                      </span>
                    </td>
                    <td style={{ ...styles.td, maxWidth: 200, fontSize: 10, color: '#f87171', wordBreak: 'break-all' }}>
                      {a.error || '—'}
                    </td>
                    <td style={styles.td}>{_timeAgo(a.completed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


function ExecStat({ label, value, color, icon: Icon }) {
  return (
    <div style={{ background: '#0a0a0a', border: '1px solid #1f1f1f', borderRadius: 6, padding: '10px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <Icon size={13} color={color} />
        <span style={{ color: '#737373', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
      </div>
      <div style={{ color, fontSize: 18, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>{value}</div>
    </div>
  );
}

function ManifestRiskBadge({ risk }) {
  const colorMap = {
    low: { bg: '#052e16', border: '#166534', text: '#4ade80', label: 'LOW' },
    medium: { bg: '#1c1917', border: '#854d0e', text: '#fbbf24', label: 'MED' },
    high: { bg: '#1c0a0a', border: '#991b1b', text: '#f87171', label: 'HIGH' },
    blocked: { bg: '#0a0a0a', border: '#333', text: '#525252', label: 'BLOCKED' },
  };
  const r = colorMap[risk] || colorMap.blocked;
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 4,
      fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
      background: r.bg, border: `1px solid ${r.border}`, color: r.text,
    }}>
      {r.label}
    </span>
  );
}

function _humanBytes(b) {
  if (!b || b === 0) return '0 B';
  if (b >= 1073741824) return (b / 1073741824).toFixed(1) + ' GB';
  if (b >= 1048576) return (b / 1048576).toFixed(1) + ' MB';
  if (b >= 1024) return (b / 1024).toFixed(1) + ' KB';
  return b + ' B';
}

// ── Manifest-specific styles ────────────────────────────────────────────

const mStyles = {
  label: { display: 'block', color: '#737373', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  select: {
    padding: '8px 12px', background: '#0a0a0a', border: '1px solid #333', borderRadius: 6,
    color: '#e5e5e5', fontSize: 13, outline: 'none', cursor: 'pointer', minWidth: 170,
  },
  input: {
    padding: '8px 12px', background: '#0a0a0a', border: '1px solid #333', borderRadius: 6,
    color: '#e5e5e5', fontSize: 13, outline: 'none', width: 100,
  },
};


// ── Sub Components ───────────────────────────────────────────────────────

function DiagBadge({ label, value, color }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 10px', background: '#0a0a0a', borderRadius: 4,
      border: '1px solid #1f1f1f',
    }}>
      <span style={{ color: '#525252', fontSize: 10 }}>{label}</span>
      <span style={{ color, fontSize: 13, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>
        {value}
      </span>
    </div>
  );
}

function RiskBadge({ risk }) {
  const r = RISK_COLORS[risk] || RISK_COLORS.medium;
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 4,
      fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
      background: r.bg, border: `1px solid ${r.border}`, color: r.text,
    }}>
      {r.label}
    </span>
  );
}

function StorageKeyCell({ storageKey, onPreview, headers }) {
  if (!storageKey) return <span style={{ color: '#3a3a3a' }}>—</span>;

  const isImage = /\.(png|jpe?g|webp|gif)$/i.test(storageKey);
  const isVideo = /\.(mp4|mov|avi|mkv|webm)$/i.test(storageKey);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={styles.keyText} title={storageKey}>{_truncateKey(storageKey)}</span>
      {(isImage || isVideo) && onPreview && (
        <button
          onClick={(e) => { e.stopPropagation(); onPreview(storageKey); }}
          style={styles.previewBtn}
          title="Preview"
        >
          <Eye size={12} />
        </button>
      )}
    </div>
  );
}


// ── S3 Coverage Drill-Down Cards ──────────────────────────────────────

const COVERAGE_CARD_META = {
  // ── Keep ──────────────────────────────────────────────────────────────
  keep_live_referenced:        { label: 'Live Referenced', color: '#22c55e', badge: 'KEEP / PROTECTED', badgeColor: '#166534' },
  keep_tracked_active:         { label: 'Tracked Active (Proxies)', color: '#22c55e', badge: 'KEEP', badgeColor: '#166534' },
  keep_workspace_retained:     { label: 'Workspace / Library Retained', color: '#22c55e', badge: 'KEEP / RETAINED', badgeColor: '#166534' },
  keep_render_current:         { label: 'Render Outputs \u2014 Current / Not Expired', color: '#22c55e', badge: 'KEEP', badgeColor: '#166534' },
  keep_system_or_backup:       { label: 'System / Backup', color: '#22c55e', badge: 'KEEP / SYSTEM', badgeColor: '#166534' },
  keep_preset_or_system_asset: { label: 'Presets / System Assets', color: '#22c55e', badge: 'KEEP / PROTECTED', badgeColor: '#166534' },
  keep_migrated_live:          { label: 'Migrated Originals \u2014 Live / Referenced', color: '#22c55e', badge: 'KEEP / LEGACY / PROTECTED', badgeColor: '#166534' },
  // ── Attention ─────────────────────────────────────────────────────────
  untracked_s3_objects:              { label: 'Untracked S3 Objects', color: '#f59e0b', badge: 'INVESTIGATE', badgeColor: '#92400e' },
  tracked_not_live_not_orphaned:     { label: 'Tracked / Not Live / Not Orphaned', color: '#f59e0b', badge: 'INVESTIGATE', badgeColor: '#92400e' },
  render_no_expiry:                  { label: 'Render Outputs \u2014 No Expiry', color: '#f59e0b', badge: 'INVESTIGATE / TTL MISSING', badgeColor: '#92400e' },
  render_no_context:                 { label: 'Render Outputs \u2014 No Current Context', color: '#f59e0b', badge: 'INVESTIGATE', badgeColor: '#92400e' },
  render_deleted_but_s3_exists:      { label: 'Render Outputs \u2014 Deleted but S3 Exists', color: '#f59e0b', badge: 'INVESTIGATE / INCONSISTENCY', badgeColor: '#92400e' },
  storage_deleted_but_object_exists: { label: 'Storage Deleted But Object Exists', color: '#f59e0b', badge: 'INVESTIGATE', badgeColor: '#92400e' },
  migrated_not_live:                 { label: 'Migrated Originals \u2014 Not Live Referenced', color: '#f59e0b', badge: 'INVESTIGATE / LEGACY', badgeColor: '#92400e' },
  db_points_to_missing_s3:          { label: 'DB Points to Missing S3', color: '#f59e0b', badge: 'INVESTIGATE', badgeColor: '#92400e' },
  // ── Cleanup ───────────────────────────────────────────────────────────
  stale_uploads:             { label: 'Stale Uploads', color: '#ef4444', badge: 'CLEANUP', badgeColor: '#991b1b' },
  expired_renders:           { label: 'Expired Renders', color: '#ef4444', badge: 'CLEANUP', badgeColor: '#991b1b' },
  invalidated_renders:       { label: 'Invalidated Renders', color: '#e879f9', badge: 'CLEANUP', badgeColor: '#86198f' },
  orphaned_assets:           { label: 'Orphaned Assets', color: '#ef4444', badge: 'CLEANUP', badgeColor: '#991b1b' },
  soft_deleted_with_s3:      { label: 'Soft-Deleted with S3', color: '#ef4444', badge: 'CLEANUP', badgeColor: '#991b1b' },
  verified_duplicates:       { label: 'Verified Duplicates', color: '#ef4444', badge: 'CLEANUP', badgeColor: '#991b1b' },
  legacy_unused_confirmed:   { label: 'Legacy Unused Confirmed', color: '#ef4444', badge: 'CLEANUP', badgeColor: '#991b1b' },
  // ── Unclassified ──────────────────────────────────────────────────────
  unclassified: { label: 'Unclassified S3 Objects', color: '#dc2626', badge: 'SHOULD ALWAYS BE 0', badgeColor: '#7f1d1d' },
};

/* ── Coverage Item Table (enriched) ──────────────────────────────────── */

const _mono9 = { fontFamily: "'IBM Plex Mono', monospace", fontSize: 9 };
const _mono10 = { fontFamily: "'IBM Plex Mono', monospace", fontSize: 10 };
const _lbl = { fontSize: 8, color: '#3a3a3a', textTransform: 'uppercase', letterSpacing: 0.3 };

function CoverageCarouselContext({ item }) {
  if (!item.carousel_id && !item.field_path) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3 }}>
      {item.carousel_name && (
        <span style={{ ..._mono9, color: '#60a5fa', background: '#172554', padding: '1px 4px', borderRadius: 2 }}>
          {item.carousel_name}
        </span>
      )}
      {item.carousel_id && !item.carousel_name && (
        <span style={{ ..._mono9, color: '#525252' }}>carousel: {_truncId(item.carousel_id)}</span>
      )}
      {item.slide_index != null && (
        <span style={{ ..._mono9, color: '#a78bfa' }}>slide[{item.slide_index}]</span>
      )}
      {item.field_path && (
        <span style={{ ..._mono9, color: '#737373' }}>{item.field_path}</span>
      )}
      {item.reference_count > 1 && (
        <span style={{ ..._mono9, color: '#f59e0b' }}>+{item.reference_count - 1} more refs</span>
      )}
      {item.reference_source && (
        <span style={{ ..._mono9, color: '#3a3a3a' }}>via {item.reference_source}</span>
      )}
    </div>
  );
}

function CoverageItemTable({ items, categoryKey }) {
  if (!items || items.length === 0) return <div style={{ color: '#3a3a3a', fontSize: 11, padding: 8 }}>No items to display</div>;

  // Detect if items are render-output types
  const isRenderCategory = categoryKey && (
    categoryKey.startsWith('render_') ||
    categoryKey === 'keep_render_current' ||
    categoryKey === 'expired_renders' ||
    categoryKey === 'invalidated_renders'
  );

  if (isRenderCategory) {
    return (
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Job / Output Key</th>
              <th style={styles.th}>Kind</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Size</th>
              <th style={styles.th}>Age</th>
              <th style={styles.th}>Expiry</th>
              <th style={styles.th}>Context</th>
              <th style={styles.th}>Reason</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={item.job_id || item.key || i} style={i % 2 === 0 ? styles.trEven : undefined}>
                <td style={{ ...styles.tdKey, maxWidth: 380 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ ..._mono10, color: '#737373' }} title={item.key || item.output_key}>
                      {_truncateKey(item.key || item.output_key || '—')}
                    </span>
                    {item.job_id && <span style={{ ..._mono9, color: '#525252' }}>job: {_truncId(item.job_id)}</span>}
                    {item.asset_id && <span style={{ ..._mono9, color: '#525252' }}>asset: {_truncId(item.asset_id)}</span>}
                    {item.render_hash && <span style={{ ..._mono9, color: '#3a3a3a' }}>hash: {item.render_hash.substring(0, 12)}...</span>}
                    {item.source_job_id && <span style={{ ..._mono9, color: '#a78bfa' }}>cache-of: {_truncId(item.source_job_id)}</span>}
                  </div>
                </td>
                <td style={styles.td}>
                  {item.kind ? <KindBadge kind={item.kind} /> : <span style={{ color: '#3a3a3a' }}>—</span>}
                </td>
                <td style={{ ...styles.td, ..._mono10 }}>
                  {item.status || '—'}
                </td>
                <td style={{ ...styles.td, ..._mono10, whiteSpace: 'nowrap' }}>
                  {item.size_human || '—'}
                </td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 10 }}>
                  {item.age || '—'}
                </td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 10 }}>
                  {item.expires_in && <span style={{ color: '#22c55e' }}>{item.expires_in}</span>}
                  {item.expired_by && <span style={{ color: '#ef4444' }}>-{item.expired_by}</span>}
                  {item.expired_at && !item.expired_by && <span style={{ color: '#ef4444' }}>expired {new Date(item.expired_at).toLocaleDateString()}</span>}
                  {item.output_deleted_at && <span style={{ color: '#f59e0b' }}>deleted {new Date(item.output_deleted_at).toLocaleDateString()}</span>}
                  {!item.expires_in && !item.expired_by && !item.expired_at && !item.output_deleted_at && item.output_expires_at && (
                    <span style={{ color: '#525252' }}>{new Date(item.output_expires_at).toLocaleDateString()}</span>
                  )}
                  {!item.expires_in && !item.expired_by && !item.expired_at && !item.output_deleted_at && !item.output_expires_at && (
                    <span style={{ color: '#f59e0b' }}>no TTL</span>
                  )}
                </td>
                <td style={{ ...styles.td, maxWidth: 200, fontSize: 10 }}>
                  <CoverageCarouselContext item={item} />
                  {!item.carousel_id && !item.field_path && (
                    <span style={{ color: '#3a3a3a', fontSize: 9 }}>No carousel context</span>
                  )}
                </td>
                <td style={{ ...styles.td, maxWidth: 260, fontSize: 10, color: '#737373' }}>
                  {item.reason || ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Default media asset / generic table
  return (
    <div style={styles.tableContainer}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Key / ID</th>
            <th style={styles.th}>Kind</th>
            <th style={styles.th}>User</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Size</th>
            <th style={styles.th}>Age</th>
            <th style={styles.th}>Context</th>
            <th style={styles.th}>Reason</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={item.key || item.asset_id || i} style={i % 2 === 0 ? styles.trEven : undefined}>
              <td style={{ ...styles.tdKey, maxWidth: 380 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ ..._mono10, color: '#737373' }} title={item.key || item.storage_key}>
                    {_truncateKey(item.key || item.storage_key || '—')}
                  </span>
                  {item.asset_id && <span style={{ ..._mono9, color: '#525252' }}>asset: {_truncId(item.asset_id)}</span>}
                  {item.source_asset_id && <span style={{ ..._mono9, color: '#525252' }}>source: {_truncId(item.source_asset_id)}</span>}
                  {item.content_hash && <span style={{ ..._mono9, color: '#3a3a3a' }}>hash: {item.content_hash.substring(0, 12)}...</span>}
                </div>
              </td>
              <td style={styles.td}>
                {item.kind ? <KindBadge kind={item.kind} /> : <span style={{ color: '#3a3a3a' }}>—</span>}
              </td>
              <td style={{ ...styles.td, ..._mono10 }}>
                {item.user_id ? _truncId(item.user_id) : '—'}
              </td>
              <td style={{ ...styles.td, ..._mono10 }}>
                {item.status || '—'}
                {item.is_migrated_prefix && <span style={{ marginLeft: 4, color: '#a78bfa', fontSize: 8 }}>migrated</span>}
                {item.is_old_upload_prefix && <span style={{ marginLeft: 4, color: '#f59e0b', fontSize: 8 }}>legacy</span>}
              </td>
              <td style={{ ...styles.td, ..._mono10, whiteSpace: 'nowrap' }}>
                {item.size_human || '—'}
              </td>
              <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 10 }}>
                {item.age || (item.last_modified ? new Date(item.last_modified).toLocaleDateString() : (item.db_created_at ? new Date(item.db_created_at).toLocaleDateString() : '—'))}
              </td>
              <td style={{ ...styles.td, maxWidth: 200, fontSize: 10 }}>
                <CoverageCarouselContext item={item} />
                {!item.carousel_id && !item.field_path && item.source && (
                  <span style={{ ..._mono9, color: '#3a3a3a' }}>src: {item.source}</span>
                )}
                {item.storage_deleted_at && <div style={{ color: '#f59e0b', fontSize: 9 }}>deleted_at: {new Date(item.storage_deleted_at).toLocaleDateString()}</div>}
                {item.orphaned_at && <div style={{ color: '#ef4444', fontSize: 9 }}>orphaned: {new Date(item.orphaned_at).toLocaleDateString()}</div>}
              </td>
              <td style={{ ...styles.td, maxWidth: 260, fontSize: 10, color: '#737373' }}>
                {item.reason || item.detail || item.content_type || ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CoverageDrillDown({ coverage, onPreview, headers, onRefresh }) {
  const [expandedCov, setExpandedCov] = React.useState({});
  const [normalizeModal, setNormalizeModal] = React.useState(false);
  const [normalizeConfirm, setNormalizeConfirm] = React.useState('');
  const [normalizeRunning, setNormalizeRunning] = React.useState(false);
  const [normalizeResult, setNormalizeResult] = React.useState(null);
  const [migrateModal, setMigrateModal] = React.useState(false);
  const [migrateConfirm, setMigrateConfirm] = React.useState('');
  const [migrateRunning, setMigrateRunning] = React.useState(false);
  const [migrateResult, setMigrateResult] = React.useState(null);
  const toggleCov = (k) => setExpandedCov(p => ({ ...p, [k]: !p[k] }));

  if (!coverage) return null;

  const NORMALIZE_CONFIRMATION = 'MARK LEGACY RENDERS AS EXPIRED';

  const handleNormalize = async () => {
    if (normalizeConfirm !== NORMALIZE_CONFIRMATION) return;
    const items = (coverage.attention_items || {}).render_no_expiry || [];
    if (items.length === 0) return;

    const jobIds = items.map(i => i.job_id).filter(Boolean);
    if (jobIds.length === 0) return;

    setNormalizeRunning(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/cleanup/normalize-legacy-render-ttls`, {
        method: 'POST',
        headers: headers ? headers() : {},
        body: JSON.stringify({ job_ids: jobIds, confirmation: NORMALIZE_CONFIRMATION }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        setNormalizeResult({ error: err.detail || `HTTP ${resp.status}` });
      } else {
        const d = await resp.json();
        setNormalizeResult(d);
        if (d.normalized_count > 0 && onRefresh) {
          setTimeout(onRefresh, 1000);
        }
      }
    } catch (e) {
      setNormalizeResult({ error: e.message });
    }
    setNormalizeRunning(false);
  };

  const MIGRATE_CONFIRMATION = 'MARK LEGACY MIGRATED AS ORPHANED';

  const handleMigrateOrphan = async () => {
    if (migrateConfirm !== MIGRATE_CONFIRMATION) return;
    const items = (coverage.attention_items || {}).migrated_not_live || [];
    if (items.length === 0) return;

    const assetIds = items.map(i => i.asset_id).filter(Boolean);
    if (assetIds.length === 0) return;

    setMigrateRunning(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/cleanup/normalize-legacy-migrated-orphans`, {
        method: 'POST',
        headers: headers ? headers() : {},
        body: JSON.stringify({ asset_ids: assetIds, confirmation: MIGRATE_CONFIRMATION }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        setMigrateResult({ error: err.detail || `HTTP ${resp.status}` });
      } else {
        const d = await resp.json();
        setMigrateResult(d);
        if (d.normalized_count > 0 && onRefresh) {
          setTimeout(onRefresh, 1000);
        }
      }
    } catch (e) {
      setMigrateResult({ error: e.message });
    }
    setMigrateRunning(false);
  };

  const sections = [
    { group: 'Verified / Keep', groupColor: '#22c55e',
      cats: Object.entries(coverage.keep_categories).map(([k, v]) => ({
        key: k, ...v, items: (coverage.keep_items || {})[k] || [],
      })),
    },
    { group: 'Needs Attention', groupColor: '#f59e0b',
      cats: [
        ...Object.entries(coverage.attention_categories).map(([k, v]) => ({
          key: k, ...v, items: (coverage.attention_items || {})[k] || [],
        })),
        // db_points_to_missing_s3 is a DB diagnostic, not an S3 bucket
        {
          key: 'db_points_to_missing_s3',
          count: coverage.db_diagnostics.db_points_to_missing_s3.count,
          size: coverage.db_diagnostics.db_points_to_missing_s3.size,
          size_human: coverage.db_diagnostics.db_points_to_missing_s3.size_human,
          items: coverage.db_diagnostics.db_points_to_missing_s3.items || [],
        },
      ],
    },
    { group: 'Cleanup Candidates (use Staged Cleanup)', groupColor: '#ef4444',
      cats: Object.entries(coverage.cleanup_categories).map(([k, v]) => ({
        key: k, ...v, items: (coverage.cleanup_items || {})[k] || [],
      })),
    },
  ];

  // Always add unclassified section (should always be 0)
  const uncl = coverage.unclassified || { count: 0, size: 0, size_human: '0 B', items: [] };
  sections.push({
    group: 'Unclassified', groupColor: uncl.count > 0 ? '#dc2626' : '#22c55e',
    cats: [{
      key: 'unclassified',
      count: uncl.count,
      size: uncl.size,
      size_human: uncl.size_human,
      items: uncl.items || [],
    }],
  });

  const renderNoExpiryItems = (coverage.attention_items || {}).render_no_expiry || [];
  const hasNormalizableItems = renderNoExpiryItems.length > 0 && renderNoExpiryItems.some(i => i.job_id);

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <Database size={14} color="#525252" />
        <span style={{ fontSize: 13, fontWeight: 600, color: '#a3a3a3' }}>
          Coverage Drill-Down
        </span>
        <span style={{
          padding: '2px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700,
          background: '#1a1a1b', border: '1px solid #262626', color: '#525252',
          textTransform: 'uppercase', letterSpacing: 0.5,
        }}>
          READ ONLY / DIAGNOSTIC
        </span>
      </div>

      {sections.map((section) => (
        <div key={section.group} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: section.groupColor, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, fontWeight: 700 }}>
            {section.group}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
            {section.cats.map((cat) => {
              const meta = COVERAGE_CARD_META[cat.key] || { label: cat.key, color: '#737373', badge: '?', badgeColor: '#333' };
              const hasItems = cat.count > 0;
              const isExpanded = expandedCov[cat.key];
              return (
                <button
                  key={cat.key}
                  onClick={() => hasItems && toggleCov(cat.key)}
                  style={{
                    ...styles.summaryCard,
                    borderColor: hasItems ? meta.color + '44' : '#1a1a1b',
                    cursor: hasItems ? 'pointer' : 'default',
                    opacity: hasItems ? 1 : 0.5,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ color: '#a3a3a3', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3, flex: 1, textAlign: 'left' }}>
                      {meta.label}
                    </span>
                    {hasItems && (isExpanded
                      ? <ChevronDown size={12} color="#525252" />
                      : <ChevronRight size={12} color="#525252" />
                    )}
                  </div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 20, fontWeight: 700, color: hasItems ? '#e5e5e5' : '#3a3a3a', textAlign: 'left' }}>
                    {cat.count}
                  </div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: '#525252', marginTop: 2, textAlign: 'left' }}>
                    {cat.size_human}
                  </div>
                  <div style={{ marginTop: 6, textAlign: 'left' }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 6px', borderRadius: 3,
                      fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                      background: meta.badgeColor + '33', color: meta.color, border: `1px solid ${meta.badgeColor}66`,
                    }}>
                      {meta.badge}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Expanded detail tables */}
          {section.cats.map((cat) => {
            if (!expandedCov[cat.key] || !cat.items || cat.items.length === 0) return null;
            const meta = COVERAGE_CARD_META[cat.key] || { label: cat.key, color: '#737373' };
            const isRenderNoExpiry = cat.key === 'render_no_expiry';
            const isMigratedNotLive = cat.key === 'migrated_not_live';
            const hasMigrateItems = isMigratedNotLive && cat.items.some(i => i.asset_id);
            return (
              <div key={`detail-${cat.key}`} style={{ ...styles.tableCard, marginTop: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 3, height: 16, borderRadius: 2, background: meta.color }} />
                  <span style={{ color: '#e5e5e5', fontSize: 13, fontWeight: 600 }}>{meta.label}</span>
                  <span style={{ color: '#525252', fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                    ({cat.count} total, showing {cat.items.length})
                  </span>
                  {isRenderNoExpiry && hasNormalizableItems && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setNormalizeModal(true); setNormalizeConfirm(''); setNormalizeResult(null); }}
                      style={{
                        marginLeft: 8, padding: '4px 12px', borderRadius: 4,
                        fontSize: 10, fontWeight: 600, cursor: 'pointer',
                        background: '#92400e', color: '#fbbf24', border: '1px solid #b45309',
                      }}
                    >
                      Mark as Expired
                    </button>
                  )}
                  {isMigratedNotLive && hasMigrateItems && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setMigrateModal(true); setMigrateConfirm(''); setMigrateResult(null); }}
                      style={{
                        marginLeft: 8, padding: '4px 12px', borderRadius: 4,
                        fontSize: 10, fontWeight: 600, cursor: 'pointer',
                        background: '#92400e', color: '#fbbf24', border: '1px solid #b45309',
                      }}
                    >
                      Mark Legacy Migrated as Orphaned
                    </button>
                  )}
                  <span style={{
                    marginLeft: 'auto', padding: '2px 8px', borderRadius: 3,
                    fontSize: 8, fontWeight: 700, textTransform: 'uppercase',
                    background: '#1a1a1b', border: '1px solid #262626', color: '#525252',
                  }}>
                    {(isRenderNoExpiry || isMigratedNotLive) ? 'NORMALIZABLE' : 'READ ONLY'}
                  </span>
                </div>
                <CoverageItemTable items={cat.items} categoryKey={cat.key} />
              </div>
            );
          })}

          {/* Unclassified warning inline */}
          {section.group === 'Unclassified' && uncl.count > 0 && (
            <div style={{ ...styles.readOnlyBanner, borderColor: '#dc262644', background: '#1c0a0a', marginTop: 10 }}>
              <AlertOctagon size={14} color="#dc2626" />
              <span style={{ fontSize: 11, color: '#fca5a5' }}>
                Some S3 objects are not classified by the current diagnostics model.
                Add/fix diagnostic categories before considering cleanup automation.
              </span>
            </div>
          )}
        </div>
      ))}

      {/* ── Normalize Legacy Render TTLs Modal ── */}
      {normalizeModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }} onClick={() => !normalizeRunning && setNormalizeModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#1a1a1b', border: '1px solid #262626', borderRadius: 8,
              padding: 24, maxWidth: 520, width: '90%', boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <AlertTriangle size={18} color="#f59e0b" />
              <span style={{ fontSize: 15, fontWeight: 700, color: '#e5e5e5' }}>
                Normalize Legacy Render TTLs
              </span>
            </div>

            {!normalizeResult ? (
              <>
                <div style={{ fontSize: 12, color: '#a3a3a3', lineHeight: 1.6, marginBottom: 16 }}>
                  This action does not delete files. It only assigns an expired TTL to legacy
                  generated render outputs with no expiry, so they can be removed later through
                  the normal <strong style={{ color: '#ef4444' }}>Expired Renders</strong> staged cleanup flow.
                </div>

                <div style={{
                  background: '#0a0a0a', border: '1px solid #262626', borderRadius: 6,
                  padding: 12, marginBottom: 16,
                }}>
                  <div style={{ fontSize: 10, color: '#737373', marginBottom: 4 }}>
                    Items to normalize:
                  </div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, color: '#fbbf24' }}>
                    {renderNoExpiryItems.length} render output{renderNoExpiryItems.length !== 1 ? 's' : ''}
                    {' · '}
                    {coverage.attention_categories?.render_no_expiry?.size_human || '0 B'}
                  </div>
                  <div style={{ fontSize: 10, color: '#525252', marginTop: 4 }}>
                    Each job will be re-validated before update. Ineligible items are skipped.
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 10, color: '#737373', display: 'block', marginBottom: 4 }}>
                    Type exactly: <span style={{ color: '#fbbf24', fontWeight: 700 }}>{NORMALIZE_CONFIRMATION}</span>
                  </label>
                  <input
                    type="text"
                    value={normalizeConfirm}
                    onChange={(e) => setNormalizeConfirm(e.target.value)}
                    disabled={normalizeRunning}
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: 4,
                      background: '#0a0a0a', border: '1px solid #262626', color: '#e5e5e5',
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
                      boxSizing: 'border-box',
                    }}
                    placeholder={NORMALIZE_CONFIRMATION}
                  />
                </div>

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setNormalizeModal(false)}
                    disabled={normalizeRunning}
                    style={{
                      padding: '6px 16px', borderRadius: 4, fontSize: 11,
                      background: '#262626', color: '#a3a3a3', border: '1px solid #3a3a3a',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleNormalize}
                    disabled={normalizeConfirm !== NORMALIZE_CONFIRMATION || normalizeRunning}
                    style={{
                      padding: '6px 16px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                      background: normalizeConfirm === NORMALIZE_CONFIRMATION ? '#92400e' : '#262626',
                      color: normalizeConfirm === NORMALIZE_CONFIRMATION ? '#fbbf24' : '#525252',
                      border: `1px solid ${normalizeConfirm === NORMALIZE_CONFIRMATION ? '#b45309' : '#3a3a3a'}`,
                      cursor: normalizeConfirm === NORMALIZE_CONFIRMATION ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {normalizeRunning ? 'Processing...' : 'Confirm Normalization'}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Results */}
                {normalizeResult.error ? (
                  <div style={{ padding: 12, background: '#1c0a0a', border: '1px solid #dc262644', borderRadius: 6, marginBottom: 16 }}>
                    <span style={{ color: '#fca5a5', fontSize: 12 }}>Error: {normalizeResult.error}</span>
                  </div>
                ) : (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{
                      display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12,
                    }}>
                      <div style={{ background: '#0a0a0a', border: '1px solid #166534', borderRadius: 6, padding: 10, textAlign: 'center' }}>
                        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 20, color: '#22c55e', fontWeight: 700 }}>
                          {normalizeResult.normalized_count}
                        </div>
                        <div style={{ fontSize: 9, color: '#737373', textTransform: 'uppercase' }}>Normalized</div>
                      </div>
                      <div style={{ background: '#0a0a0a', border: '1px solid #262626', borderRadius: 6, padding: 10, textAlign: 'center' }}>
                        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 20, color: '#f59e0b', fontWeight: 700 }}>
                          {normalizeResult.skipped_count}
                        </div>
                        <div style={{ fontSize: 9, color: '#737373', textTransform: 'uppercase' }}>Skipped</div>
                      </div>
                      <div style={{ background: '#0a0a0a', border: '1px solid #262626', borderRadius: 6, padding: 10, textAlign: 'center' }}>
                        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 20, color: '#ef4444', fontWeight: 700 }}>
                          {normalizeResult.failed_count}
                        </div>
                        <div style={{ fontSize: 9, color: '#737373', textTransform: 'uppercase' }}>Failed</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: '#a3a3a3' }}>
                      Affected: <strong style={{ color: '#e5e5e5' }}>{normalizeResult.affected_bytes_human}</strong>
                      {' · '}Items now in <strong style={{ color: '#ef4444' }}>Expired Renders</strong> cleanup flow
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setNormalizeModal(false)}
                    style={{
                      padding: '6px 16px', borderRadius: 4, fontSize: 11,
                      background: '#262626', color: '#a3a3a3', border: '1px solid #3a3a3a',
                      cursor: 'pointer',
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Mark Legacy Migrated as Orphaned Modal ── */}
      {migrateModal && (() => {
        const migrateItems = (coverage.attention_items || {}).migrated_not_live || [];
        return (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }} onClick={() => !migrateRunning && setMigrateModal(false)}>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#1a1a1b', border: '1px solid #262626', borderRadius: 8,
                padding: 24, maxWidth: 560, width: '90%', boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <AlertTriangle size={18} color="#f59e0b" />
                <span style={{ fontSize: 15, fontWeight: 700, color: '#e5e5e5' }}>
                  Mark Legacy Migrated as Orphaned
                </span>
              </div>

              {!migrateResult ? (
                <>
                  <div style={{ fontSize: 12, color: '#a3a3a3', lineHeight: 1.6, marginBottom: 16 }}>
                    These are legacy migrated original media assets. This action does not delete files.
                    It only marks selected unreferenced migrated assets as orphaned so they can be reviewed
                    and deleted later through the normal <strong style={{ color: '#ef4444' }}>Orphaned Assets</strong> staged
                    cleanup flow. <strong style={{ color: '#fbbf24' }}>Original media cannot be regenerated after physical deletion.</strong>
                  </div>

                  <div style={{
                    background: '#0a0a0a', border: '1px solid #262626', borderRadius: 6,
                    padding: 12, marginBottom: 16,
                  }}>
                    <div style={{ fontSize: 10, color: '#737373', marginBottom: 4 }}>
                      Items to mark as orphaned:
                    </div>
                    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, color: '#fbbf24' }}>
                      {migrateItems.length} migrated asset{migrateItems.length !== 1 ? 's' : ''}
                      {' · '}
                      {coverage.attention_categories?.migrated_not_live?.size_human || '0 B'}
                    </div>
                    <div style={{ fontSize: 10, color: '#525252', marginTop: 4 }}>
                      Each asset will be re-validated before update. Referenced items are skipped.
                    </div>
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 10, color: '#737373', display: 'block', marginBottom: 4 }}>
                      Type exactly: <span style={{ color: '#fbbf24', fontWeight: 700 }}>{MIGRATE_CONFIRMATION}</span>
                    </label>
                    <input
                      type="text"
                      value={migrateConfirm}
                      onChange={(e) => setMigrateConfirm(e.target.value)}
                      disabled={migrateRunning}
                      style={{
                        width: '100%', padding: '8px 10px', borderRadius: 4,
                        background: '#0a0a0a', border: '1px solid #262626', color: '#e5e5e5',
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
                        boxSizing: 'border-box',
                      }}
                      placeholder={MIGRATE_CONFIRMATION}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setMigrateModal(false)}
                      disabled={migrateRunning}
                      style={{
                        padding: '6px 16px', borderRadius: 4, fontSize: 11,
                        background: '#262626', color: '#a3a3a3', border: '1px solid #3a3a3a',
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleMigrateOrphan}
                      disabled={migrateConfirm !== MIGRATE_CONFIRMATION || migrateRunning}
                      style={{
                        padding: '6px 16px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                        background: migrateConfirm === MIGRATE_CONFIRMATION ? '#92400e' : '#262626',
                        color: migrateConfirm === MIGRATE_CONFIRMATION ? '#fbbf24' : '#525252',
                        border: `1px solid ${migrateConfirm === MIGRATE_CONFIRMATION ? '#b45309' : '#3a3a3a'}`,
                        cursor: migrateConfirm === MIGRATE_CONFIRMATION ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {migrateRunning ? 'Processing...' : 'Confirm Mark as Orphaned'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {migrateResult.error ? (
                    <div style={{ padding: 12, background: '#1c0a0a', border: '1px solid #dc262644', borderRadius: 6, marginBottom: 16 }}>
                      <span style={{ color: '#fca5a5', fontSize: 12 }}>Error: {migrateResult.error}</span>
                    </div>
                  ) : (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12,
                      }}>
                        <div style={{ background: '#0a0a0a', border: '1px solid #166534', borderRadius: 6, padding: 10, textAlign: 'center' }}>
                          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 20, color: '#22c55e', fontWeight: 700 }}>
                            {migrateResult.normalized_count}
                          </div>
                          <div style={{ fontSize: 9, color: '#737373', textTransform: 'uppercase' }}>Marked Orphaned</div>
                        </div>
                        <div style={{ background: '#0a0a0a', border: '1px solid #262626', borderRadius: 6, padding: 10, textAlign: 'center' }}>
                          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 20, color: '#f59e0b', fontWeight: 700 }}>
                            {migrateResult.skipped_count}
                          </div>
                          <div style={{ fontSize: 9, color: '#737373', textTransform: 'uppercase' }}>Skipped</div>
                        </div>
                        <div style={{ background: '#0a0a0a', border: '1px solid #262626', borderRadius: 6, padding: 10, textAlign: 'center' }}>
                          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 20, color: '#ef4444', fontWeight: 700 }}>
                            {migrateResult.failed_count}
                          </div>
                          <div style={{ fontSize: 9, color: '#737373', textTransform: 'uppercase' }}>Failed</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: '#a3a3a3' }}>
                        Affected: <strong style={{ color: '#e5e5e5' }}>{migrateResult.affected_bytes_human}</strong>
                        {' · '}Items now in <strong style={{ color: '#ef4444' }}>Orphaned Assets</strong> cleanup flow
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setMigrateModal(false)}
                      style={{
                        padding: '6px 16px', borderRadius: 4, fontSize: 11,
                        background: '#262626', color: '#a3a3a3', border: '1px solid #3a3a3a',
                        cursor: 'pointer',
                      }}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}


function CandidateTable({ samples, category, onPreview, headers, onUsage }) {
  return (
    <div style={styles.tableContainer}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Risk</th>
            <th style={styles.th}>ID</th>
            <th style={styles.th}>Kind</th>
            <th style={styles.th}>User</th>
            <th style={styles.th}>Size</th>
            <th style={styles.th}>Age</th>
            <th style={styles.th}>Storage Key</th>
            {category === 'orphaned_assets' && <th style={styles.th}>Refs</th>}
            <th style={styles.th}>Reason</th>
            <th style={{ ...styles.th, width: 60 }}>Inspect</th>
          </tr>
        </thead>
        <tbody>
          {samples.map((s, i) => (
            <tr key={s.asset_id || i} style={i % 2 === 0 ? styles.trEven : undefined}>
              <td style={styles.td}><RiskBadge risk={s.risk} /></td>
              <td style={{ ...styles.td, fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                {_truncId(s.asset_id)}
              </td>
              <td style={styles.td}>
                <KindBadge kind={s.kind} />
              </td>
              <td style={{ ...styles.td, fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                {_truncId(s.user_id)}
              </td>
              <td style={{ ...styles.td, fontFamily: "'IBM Plex Mono', monospace", whiteSpace: 'nowrap' }}>
                {s.size_human || '—'}
              </td>
              <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 11 }}>
                {s.age || s.age_since_orphaned || s.age_since_deleted || '—'}
              </td>
              <td style={styles.tdKey}>
                <StorageKeyCell storageKey={s.storage_key} onPreview={onPreview} headers={headers} />
              </td>
              {category === 'orphaned_assets' && (
                <td style={styles.td}>
                  <span style={{
                    color: s.ref_count > 0 ? '#f87171' : '#4ade80',
                    fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace",
                  }}>
                    {s.ref_count}
                  </span>
                  {s.has_carousel_ref && (
                    <span style={{ color: '#f87171', fontSize: 9, marginLeft: 4 }}>+JSONB</span>
                  )}
                </td>
              )}
              <td style={{ ...styles.td, maxWidth: 300, fontSize: 11, color: '#737373' }}>
                {s.reason}
              </td>
              <td style={styles.td}>
                {(s.asset_id || s.storage_key) && (
                  <UsageButton assetId={s.asset_id} storageKey={s.storage_key} onUsage={onUsage} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function InvalidatedRendersTable({ samples }) {
  const REASON_COLORS = {
    slide_changed: '#f59e0b',
    slide_deleted: '#ef4444',
    carousel_deleted: '#dc2626',
    manual_admin_invalidation: '#8b5cf6',
    unknown: '#525252',
  };

  return (
    <div style={styles.tableContainer}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Risk</th>
            <th style={styles.th}>Job ID</th>
            <th style={styles.th}>Kind</th>
            <th style={styles.th}>Reason</th>
            <th style={styles.th}>Invalidated</th>
            <th style={styles.th}>Active Sharers</th>
            <th style={styles.th}>Output Key</th>
            <th style={styles.th}>Detail</th>
          </tr>
        </thead>
        <tbody>
          {samples.map((s, i) => (
            <tr key={s.job_id || i} style={i % 2 === 0 ? styles.trEven : undefined}>
              <td style={styles.td}><RiskBadge risk={s.risk} /></td>
              <td style={{ ...styles.td, fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                {_truncId(s.job_id)}
              </td>
              <td style={styles.td}><KindBadge kind={s.kind} /></td>
              <td style={styles.td}>
                <span style={{
                  fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
                  background: (REASON_COLORS[s.output_invalidated_reason] || '#525252') + '22',
                  color: REASON_COLORS[s.output_invalidated_reason] || '#737373',
                  border: `1px solid ${(REASON_COLORS[s.output_invalidated_reason] || '#525252')}44`,
                }}>
                  {s.output_invalidated_reason || 'unknown'}
                </span>
              </td>
              <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 11, color: '#a3a3a3' }}>
                {s.output_invalidated_at ? new Date(s.output_invalidated_at).toLocaleString() : '—'}
              </td>
              <td style={styles.td}>
                <span style={{
                  color: (s.active_non_invalidated_sharers || 0) > 0 ? '#f87171' : '#4ade80',
                  fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace",
                }}>
                  {s.active_non_invalidated_sharers || 0}
                </span>
                {(s.active_non_invalidated_sharers || 0) > 0 && (
                  <span style={{ color: '#854d0e', fontSize: 9, marginLeft: 4 }}>SHARED — PROTECTED</span>
                )}
              </td>
              <td style={styles.tdKey}>
                <span style={styles.keyText} title={s.output_key}>{_truncateKey(s.output_key)}</span>
              </td>
              <td style={{ ...styles.td, maxWidth: 280, fontSize: 11, color: '#737373' }}>
                {s.reason}
                {s.is_expired && (
                  <span style={{ color: '#f87171', fontSize: 9, marginLeft: 4 }}>EXPIRED</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function ExpiredRendersTable({ samples }) {
  return (
    <div style={styles.tableContainer}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Risk</th>
            <th style={styles.th}>Job ID</th>
            <th style={styles.th}>Kind</th>
            <th style={styles.th}>User</th>
            <th style={styles.th}>Expired</th>
            <th style={styles.th}>Cache Hit</th>
            <th style={styles.th}>Active Sharers</th>
            <th style={styles.th}>Output Key</th>
            <th style={styles.th}>Reason</th>
          </tr>
        </thead>
        <tbody>
          {samples.map((s, i) => (
            <tr key={s.job_id || i} style={i % 2 === 0 ? styles.trEven : undefined}>
              <td style={styles.td}><RiskBadge risk={s.risk} /></td>
              <td style={{ ...styles.td, fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                {_truncId(s.job_id)}
              </td>
              <td style={styles.td}><KindBadge kind={s.kind} /></td>
              <td style={{ ...styles.td, fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                {_truncId(s.user_id)}
              </td>
              <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 11 }}>
                {s.age_since_expiry}
              </td>
              <td style={styles.td}>
                {s.is_cache_hit
                  ? <span style={{ color: '#06b6d4', fontSize: 10, fontWeight: 600 }}>CACHE</span>
                  : <span style={{ color: '#3a3a3a', fontSize: 10 }}>—</span>
                }
              </td>
              <td style={styles.td}>
                <span style={{
                  color: s.active_sharers > 0 ? '#f87171' : '#4ade80',
                  fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace",
                }}>
                  {s.active_sharers}
                </span>
                {s.active_sharers > 0 && (
                  <span style={{ color: '#854d0e', fontSize: 9, marginLeft: 4 }}>SHARED</span>
                )}
              </td>
              <td style={styles.tdKey}>
                <span style={styles.keyText} title={s.output_key}>{_truncateKey(s.output_key)}</span>
              </td>
              <td style={{ ...styles.td, maxWidth: 280, fontSize: 11, color: '#737373' }}>
                {s.reason}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DuplicateGroupsTable({ samples, onPreview, headers }) {
  const [expandedGroup, setExpandedGroup] = useState({});

  return (
    <div>
      {samples.map((group, gi) => (
        <div key={gi} style={{ ...styles.dupeGroup, borderColor: '#1a3a4a' }}>
          <button
            onClick={() => setExpandedGroup(prev => ({ ...prev, [gi]: !prev[gi] }))}
            style={styles.dupeHeader}
          >
            <RiskBadge risk={group.risk} />
            <span style={{ color: '#a3a3a3', fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>
              {group.copy_count} copies
            </span>
            <KindBadge kind={group.kind} />
            <span style={{ color: '#525252', fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
              hash: {group.content_hash}
            </span>
            <span style={{ color: '#525252', fontSize: 10 }}>
              user: {_truncId(group.user_id)}
            </span>
            <span style={{ color: '#06b6d4', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, fontWeight: 600, marginLeft: 'auto' }}>
              +{group.excess_copies} excess · {group.potential_savings_human}
            </span>
            {expandedGroup[gi] ? <ChevronDown size={14} color="#525252" /> : <ChevronRight size={14} color="#525252" />}
          </button>

          <div style={{ padding: '0 12px 4px', fontSize: 11, color: '#737373' }}>
            {group.reason}
          </div>

          {expandedGroup[gi] && (
            <div style={{ padding: '0 12px 12px' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Asset ID</th>
                    <th style={styles.th}>Size</th>
                    <th style={styles.th}>Created</th>
                    <th style={styles.th}>Storage Key</th>
                  </tr>
                </thead>
                <tbody>
                  {group.members.map((m, mi) => (
                    <tr key={m.asset_id} style={mi % 2 === 0 ? styles.trEven : undefined}>
                      <td style={{ ...styles.td, fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                        {mi === 0 && <span style={{ color: '#4ade80', fontSize: 9, marginRight: 4 }}>ORIG</span>}
                        {_truncId(m.asset_id)}
                      </td>
                      <td style={{ ...styles.td, fontFamily: "'IBM Plex Mono', monospace" }}>
                        {m.size_human}
                      </td>
                      <td style={{ ...styles.td, fontSize: 11 }}>
                        {m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td style={styles.tdKey}>
                        <StorageKeyCell storageKey={m.storage_key} onPreview={onPreview} headers={headers} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function UnusedAssetsTable({ samples, category, onPreview, headers, onUsage }) {
  return (
    <div>
      {/* Reachability audit warning banner */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px', marginBottom: 12,
        background: '#1c1917', border: '1px solid #854d0e44',
        borderRadius: 6, color: '#d4a574', fontSize: 11,
      }}>
        <Unlink size={14} color="#f97316" />
        <span>
          <strong>Reachability audit only.</strong> These confirmed assets are tracked in media_assets but
          not currently found in live carousel references. Not automatically safe to delete.
          {category.carousel_asset_fields_checked && (
            <span style={{ color: '#737373' }}>
              {' · '}Fields checked: {category.carousel_asset_fields_checked.join(', ')}
            </span>
          )}
        </span>
      </div>
      {/* Warnings from scanner */}
      {(category.warnings || []).map((w, i) => (
        <div key={i} style={{
          padding: '6px 12px', marginBottom: 8,
          background: '#1c0a0a', border: '1px solid #991b1b44',
          borderRadius: 6, color: '#fca5a5', fontSize: 11,
        }}>
          ⚠ {w}
        </div>
      ))}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Risk</th>
              <th style={styles.th}>ID</th>
              <th style={styles.th}>Kind</th>
              <th style={styles.th}>User</th>
              <th style={styles.th}>Size</th>
              <th style={styles.th}>Age</th>
              <th style={styles.th}>Refs</th>
              <th style={styles.th}>Storage Key</th>
              <th style={styles.th}>Reason</th>
              <th style={{ ...styles.th, width: 60 }}>Inspect</th>
            </tr>
          </thead>
          <tbody>
            {samples.map((s, i) => (
              <tr key={s.asset_id || i} style={i % 2 === 0 ? styles.trEven : undefined}>
                <td style={styles.td}><RiskBadge risk={s.risk} /></td>
                <td style={{ ...styles.td, fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                  {_truncId(s.asset_id)}
                </td>
                <td style={styles.td}><KindBadge kind={s.kind} /></td>
                <td style={{ ...styles.td, fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                  {_truncId(s.user_id)}
                </td>
                <td style={{ ...styles.td, fontFamily: "'IBM Plex Mono', monospace", whiteSpace: 'nowrap' }}>
                  {s.size_human || '—'}
                </td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 11 }}>
                  {s.age_since_created || '—'}
                </td>
                <td style={styles.td}>
                  <span style={{ fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                    <span style={{ color: s.in_asset_references ? '#fbbf24' : '#3a3a3a' }}>
                      {s.in_asset_references ? 'REF' : '—'}
                    </span>
                    {' / '}
                    <span style={{ color: s.in_carousel_jsonb ? '#fbbf24' : '#3a3a3a' }}>
                      {s.in_carousel_jsonb ? 'JSONB' : '—'}
                    </span>
                  </span>
                </td>
                <td style={styles.tdKey}>
                  <StorageKeyCell storageKey={s.storage_key} onPreview={onPreview} headers={headers} />
                </td>
                <td style={{ ...styles.td, maxWidth: 280, fontSize: 11, color: '#737373' }}>
                  {s.reason}
                </td>
                <td style={styles.td}>
                  <UsageButton assetId={s.asset_id} storageKey={s.storage_key} onUsage={onUsage} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PossibleDupeSizeTable({ samples, category, onPreview, headers, onUsage }) {
  const [expandedGroup, setExpandedGroup] = useState({});

  return (
    <div>
      {/* Unverified warning banner */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px', marginBottom: 12,
        background: '#1c0a0a', border: '1px solid #991b1b44',
        borderRadius: 6, color: '#fca5a5', fontSize: 11,
      }}>
        <AlertTriangle size={14} color="#f87171" />
        <span>
          <strong>Possible duplicate groups — UNVERIFIED.</strong> Grouped by same owner, kind,
          content type, and exact byte size only. Same size does NOT prove same content.
          <strong> Hash verification required before any cleanup decision.</strong>
        </span>
      </div>
      {/* Warnings from scanner */}
      {(category.warnings || []).map((w, i) => (
        <div key={i} style={{
          padding: '6px 12px', marginBottom: 8,
          background: '#0a0a0a', border: '1px solid #1f1f1f',
          borderRadius: 6, color: '#737373', fontSize: 11,
        }}>
          ℹ {w}
        </div>
      ))}
      {samples.map((group, gi) => (
        <div key={gi} style={{ ...styles.dupeGroup, borderColor: '#2d1f4e' }}>
          <button
            onClick={() => setExpandedGroup(prev => ({ ...prev, [gi]: !prev[gi] }))}
            style={styles.dupeHeader}
          >
            <RiskBadge risk={group.risk} />
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '1px 6px', borderRadius: 3,
              fontSize: 9, fontWeight: 700, background: '#1c0a0a',
              border: '1px solid #991b1b', color: '#f87171',
            }}>
              UNVERIFIED
            </span>
            <span style={{ color: '#a3a3a3', fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>
              {group.copy_count} assets
            </span>
            <KindBadge kind={group.kind} />
            <span style={{ color: '#525252', fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
              size: {group.size_human}
            </span>
            <span style={{ color: '#525252', fontSize: 10 }}>
              user: {_truncId(group.user_id)}
            </span>
            {group.has_any_content_hash && (
              <span style={{ color: '#4ade80', fontSize: 9, fontWeight: 600 }}>HAS HASH</span>
            )}
            <span style={{ color: '#a855f7', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, fontWeight: 600, marginLeft: 'auto' }}>
              +{group.excess_copies} possible excess · {group.potential_savings_human}
            </span>
            {expandedGroup[gi] ? <ChevronDown size={14} color="#525252" /> : <ChevronRight size={14} color="#525252" />}
          </button>

          <div style={{ padding: '0 12px 4px', fontSize: 11, color: '#737373' }}>
            {group.reason}
          </div>

          {expandedGroup[gi] && (
            <div style={{ padding: '0 12px 12px' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Asset ID</th>
                    <th style={styles.th}>Size</th>
                    <th style={styles.th}>Hash</th>
                    <th style={styles.th}>Created</th>
                    <th style={styles.th}>Storage Key</th>
                    <th style={{ ...styles.th, width: 60 }}>Inspect</th>
                  </tr>
                </thead>
                <tbody>
                  {group.members.map((m, mi) => (
                    <tr key={m.asset_id} style={mi % 2 === 0 ? styles.trEven : undefined}>
                      <td style={{ ...styles.td, fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                        {mi === 0 && <span style={{ color: '#a855f7', fontSize: 9, marginRight: 4 }}>FIRST</span>}
                        {_truncId(m.asset_id)}
                      </td>
                      <td style={{ ...styles.td, fontFamily: "'IBM Plex Mono', monospace" }}>
                        {m.size_human}
                      </td>
                      <td style={styles.td}>
                        {m.has_content_hash
                          ? <span style={{ color: '#4ade80', fontSize: 9, fontWeight: 600 }}>YES</span>
                          : <span style={{ color: '#3a3a3a', fontSize: 9 }}>NO</span>
                        }
                      </td>
                      <td style={{ ...styles.td, fontSize: 11 }}>
                        {m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td style={styles.tdKey}>
                        <StorageKeyCell storageKey={m.storage_key} onPreview={onPreview} headers={headers} />
                      </td>
                      <td style={styles.td}>
                        <UsageButton assetId={m.asset_id} storageKey={m.storage_key} onUsage={onUsage} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ReferencedDeletedTable({ samples, category, onPreview, headers, onUsage }) {
  return (
    <div>
      {/* LIVE DEPENDENCY banner */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 14px', marginBottom: 12,
        background: '#1c0505', border: '1px solid #dc262688',
        borderRadius: 6, color: '#fca5a5', fontSize: 11,
      }}>
        <ShieldAlert size={16} color="#ef4444" />
        <div>
          <div style={{ fontWeight: 700, fontSize: 12, color: '#f87171', marginBottom: 2 }}>
            LIVE CAROUSEL DEPENDENCIES — DO NOT DELETE
          </div>
          <div>
            These assets have <code style={{ color: '#fbbf24' }}>deleted_at</code> set in media_assets
            but are still referenced by current carousel JSONB. They are NOT cleanup candidates.
            Metadata conflict requires admin decision:
          </div>
          <div style={{ marginTop: 4, color: '#a3a3a3', fontSize: 10 }}>
            <strong>Option A:</strong> If carousel should keep using asset → restore asset state and insert missing reference.
            <br />
            <strong>Option B:</strong> If carousel should NOT use asset → fix carousel document first.
          </div>
        </div>
      </div>
      {/* Warnings */}
      {(category.warnings || []).map((w, i) => (
        <div key={i} style={{
          padding: '6px 12px', marginBottom: 6, fontSize: 10,
          background: '#0a0a0a', border: '1px solid #1f1f1f',
          borderRadius: 4, color: '#737373',
        }}>
          {w}
        </div>
      ))}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Risk</th>
              <th style={styles.th}>Asset ID</th>
              <th style={styles.th}>Kind</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Deleted At</th>
              <th style={styles.th}>Carousel</th>
              <th style={styles.th}>Field Path</th>
              <th style={styles.th}>Ref Row?</th>
              <th style={styles.th}>Size</th>
              <th style={{ ...styles.th, width: 60 }}>Inspect</th>
            </tr>
          </thead>
          <tbody>
            {samples.map((s, i) => (
              <tr key={`${s.asset_id}-${s.carousel_id}-${i}`} style={i % 2 === 0 ? styles.trEven : undefined}>
                <td style={styles.td}><RiskBadge risk={s.risk} /></td>
                <td style={{ ...styles.td, fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                  {_truncId(s.asset_id)}
                </td>
                <td style={styles.td}><KindBadge kind={s.kind} /></td>
                <td style={styles.td}>
                  <span style={{
                    padding: '1px 5px', borderRadius: 3, fontSize: 9,
                    background: '#1c0505', color: '#f87171',
                    border: '1px solid #991b1b44',
                  }}>
                    {s.status} + deleted
                  </span>
                </td>
                <td style={{ ...styles.td, fontSize: 10, color: '#f87171', whiteSpace: 'nowrap' }}>
                  {s.deleted_at ? new Date(s.deleted_at).toLocaleDateString() : '—'}
                </td>
                <td style={{ ...styles.td, fontSize: 11, maxWidth: 120 }}>
                  {s.carousel_title || _truncId(s.carousel_id)}
                </td>
                <td style={{ ...styles.td, fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", color: '#a3a3a3' }}>
                  {s.field_path || '—'}
                </td>
                <td style={styles.td}>
                  <span style={{
                    color: s.in_asset_references ? '#4ade80' : '#f87171',
                    fontSize: 9, fontWeight: 600,
                  }}>
                    {s.in_asset_references ? 'YES' : 'NO'}
                  </span>
                </td>
                <td style={{ ...styles.td, fontFamily: "'IBM Plex Mono', monospace", whiteSpace: 'nowrap' }}>
                  {s.size_human || '—'}
                </td>
                <td style={styles.td}>
                  <UsageButton assetId={s.asset_id} storageKey={s.storage_key} onUsage={onUsage} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function DryRunCard({ dryRun, loading, onFetch }) {
  const [showDetails, setShowDetails] = useState(false);

  if (!dryRun && !loading) {
    return (
      <div style={{
        padding: '14px 18px', marginTop: 12, background: '#0a0a14',
        border: '1px solid #1e1e3a', borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ color: '#c4b5fd', fontSize: 13, fontWeight: 700 }}>
            <Activity size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
            Reference Rebuild Dry-Run
          </div>
          <div style={{ color: '#525252', fontSize: 11, marginTop: 2 }}>
            Compute what a full asset_references rebuild would produce. Read-only — no changes.
          </div>
        </div>
        <button onClick={onFetch} style={{
          padding: '6px 16px', background: '#1e1b4b', borderRadius: 6,
          border: '1px solid #312e81', color: '#a78bfa', fontSize: 12,
          cursor: 'pointer', fontWeight: 600,
        }}>
          Run Dry-Run
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '14px 18px', marginTop: 12, background: '#0a0a14', border: '1px solid #1e1e3a', borderRadius: 8, color: '#525252', fontSize: 12 }}>
        Computing reference rebuild dry-run...
      </div>
    );
  }

  const bd = dryRun.breakdown || {};
  const sm = dryRun.summary || {};

  return (
    <div style={{
      marginTop: 12, background: '#0a0a14',
      border: `1px solid ${sm.healthy ? '#16653444' : '#991b1b44'}`,
      borderRadius: 8, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid #1f1f2f',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Activity size={14} color="#a78bfa" />
          <span style={{ color: '#c4b5fd', fontSize: 13, fontWeight: 700 }}>Reference Rebuild Dry-Run</span>
          <span style={{
            padding: '2px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700,
            background: sm.healthy ? '#052e16' : '#1c0505',
            border: `1px solid ${sm.healthy ? '#166534' : '#dc2626'}`,
            color: sm.healthy ? '#4ade80' : '#f87171',
          }}>
            {sm.healthy ? 'HEALTHY' : 'ISSUES FOUND'}
          </span>
          <span style={{
            padding: '2px 8px', background: '#0c1929', borderRadius: 4,
            border: '1px solid #1e40af', color: '#60a5fa', fontSize: 9, fontWeight: 700,
          }}>
            DRY RUN ONLY
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowDetails(!showDetails)} style={{
            padding: '4px 12px', background: '#1e1b4b', borderRadius: 4,
            border: '1px solid #312e81', color: '#a78bfa', fontSize: 10,
            cursor: 'pointer', fontWeight: 600,
          }}>
            {showDetails ? 'Hide Details' : 'Show Details'}
          </button>
          <button onClick={onFetch} style={{
            padding: '4px 12px', background: '#0a0a0a', borderRadius: 4,
            border: '1px solid #1f1f1f', color: '#525252', fontSize: 10,
            cursor: 'pointer',
          }}>
            Refresh
          </button>
        </div>
      </div>

      {/* Summary stats */}
      <div style={{ padding: '12px 18px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <DryRunStat label="Expected" value={dryRun.expected_references} color="#c4b5fd" />
        <DryRunStat label="Current" value={dryRun.current_references} color="#a3a3a3" />
        <DryRunStat label="Missing (Active)" value={bd.missing_to_active_assets?.count || 0} color="#38bdf8" />
        <DryRunStat label="Missing (Deleted)" value={bd.missing_to_deleted_assets?.count || 0} color="#f87171" />
        <DryRunStat label="Stale" value={bd.stale_references?.count || 0} color="#fb923c" />
        <DryRunStat label="Deleted but Live" value={bd.referenced_deleted_assets?.count || 0} color="#ef4444" />
        <DryRunStat label="Correct" value={bd.correctly_tracked?.count || 0} color="#4ade80" />
      </div>

      {/* Issues */}
      {sm.issues && sm.issues.length > 0 && (
        <div style={{ padding: '0 18px 12px' }}>
          {sm.issues.map((issue, i) => (
            <div key={i} style={{
              padding: '4px 10px', marginBottom: 4, fontSize: 11, borderRadius: 4,
              background: issue.includes('consistent') ? '#052e1611' : '#1c050511',
              border: `1px solid ${issue.includes('consistent') ? '#16653422' : '#991b1b22'}`,
              color: issue.includes('consistent') ? '#4ade80' : '#a3a3a3',
            }}>
              {issue.includes('consistent') ? '✓' : '⚠'} {issue}
            </div>
          ))}
        </div>
      )}

      {/* Future repair notes */}
      {showDetails && dryRun.future_repair_notes && (
        <div style={{ padding: '0 18px 12px' }}>
          <div style={{ fontSize: 10, color: '#525252', fontWeight: 600, marginBottom: 4 }}>FUTURE REPAIR NOTES</div>
          {dryRun.future_repair_notes.map((note, i) => (
            <div key={i} style={{ fontSize: 10, color: '#525252', padding: '2px 0' }}>• {note}</div>
          ))}
        </div>
      )}

      {/* Details: breakdown samples */}
      {showDetails && (
        <div style={{ padding: '0 18px 14px' }}>
          {bd.missing_to_active_assets?.samples?.length > 0 && (
            <DryRunBreakdown title="Missing References (Active Assets)" color="#38bdf8" samples={bd.missing_to_active_assets.samples} />
          )}
          {bd.missing_to_deleted_assets?.samples?.length > 0 && (
            <DryRunBreakdown title="Missing References (Deleted Assets)" color="#f87171" samples={bd.missing_to_deleted_assets.samples} />
          )}
          {bd.stale_references?.samples?.length > 0 && (
            <DryRunBreakdown title="Stale References" color="#fb923c" samples={bd.stale_references.samples} />
          )}
          {bd.referenced_deleted_assets?.samples?.length > 0 && (
            <DryRunBreakdown title="Live Dependencies (Deleted Assets)" color="#ef4444" samples={bd.referenced_deleted_assets.samples} />
          )}
        </div>
      )}
    </div>
  );
}


function DryRunStat({ label, value, color }) {
  return (
    <div style={{
      padding: '6px 12px', background: '#0a0a0a', borderRadius: 6,
      border: '1px solid #1f1f1f', textAlign: 'center', minWidth: 80,
    }}>
      <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", color }}>{value}</div>
      <div style={{ fontSize: 9, color: '#525252', marginTop: 1 }}>{label}</div>
    </div>
  );
}


function DryRunBreakdown({ title, color, samples }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: 8 }}>
      <button onClick={() => setOpen(!open)} style={{
        display: 'flex', alignItems: 'center', gap: 6, width: '100%',
        padding: '6px 10px', background: 'none', border: '1px solid #1f1f1f',
        borderRadius: 4, cursor: 'pointer', color: '#a3a3a3', fontSize: 11,
      }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
        <span style={{ fontWeight: 600 }}>{title}</span>
        <span style={{ color: '#525252', marginLeft: 'auto' }}>{samples.length} sample(s)</span>
        {open ? <ChevronDown size={12} color="#525252" /> : <ChevronRight size={12} color="#525252" />}
      </button>
      {open && (
        <div style={{ padding: '8px 0 0 18px' }}>
          {samples.slice(0, 15).map((s, i) => (
            <div key={i} style={{
              padding: '4px 8px', marginBottom: 3, fontSize: 10,
              borderLeft: `2px solid ${color}22`, color: '#737373',
            }}>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#a3a3a3' }}>
                {_truncId(s.asset_id)}
              </span>
              {s.field_path && <span style={{ color: '#525252' }}> → {s.field_path}</span>}
              {s.carousel_title && <span style={{ color: '#525252' }}> in "{s.carousel_title}"</span>}
              {s.deleted_at && <span style={{ color: '#f87171' }}> deleted:{s.deleted_at.split('T')[0]}</span>}
              <div style={{ color: '#3a3a3a', fontSize: 9, marginTop: 1 }}>{s.reason}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function ReferenceRepairCard({ headers }) {
  const [repairData, setRepairData] = useState(null);
  const [repairLoading, setRepairLoading] = useState(false);
  const [confirmA, setConfirmA] = useState('');
  const [confirmB, setConfirmB] = useState('');
  const [actionAResult, setActionAResult] = useState(null);
  const [actionBResult, setActionBResult] = useState(null);
  const [actionALoading, setActionALoading] = useState(false);
  const [actionBLoading, setActionBLoading] = useState(false);
  const [showSamples, setShowSamples] = useState({});

  const fetchRepairDryrun = useCallback(async () => {
    setRepairLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/storage/reference-repair-dryrun`, { headers });
      if (res.ok) {
        setRepairData(await res.json());
        setActionAResult(null);
        setActionBResult(null);
        setConfirmA('');
        setConfirmB('');
      }
    } catch (_) { /* ignore */ }
    setRepairLoading(false);
  }, [headers]);

  const executeActionA = async () => {
    setActionALoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/storage/reference-repair/rebuild-active`, {
        method: 'POST', headers, body: JSON.stringify({ confirm: confirmA }),
      });
      const d = await res.json();
      if (res.ok) { setActionAResult(d); setConfirmA(''); await fetchRepairDryrun(); }
      else setActionAResult({ success: false, error: d.detail || `HTTP ${res.status}` });
    } catch (e) { setActionAResult({ success: false, error: e.message }); }
    setActionALoading(false);
  };

  const executeActionB = async () => {
    setActionBLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/storage/reference-repair/restore-live-backgrounds`, {
        method: 'POST', headers, body: JSON.stringify({ confirm: confirmB }),
      });
      const d = await res.json();
      if (res.ok) { setActionBResult(d); setConfirmB(''); await fetchRepairDryrun(); }
      else setActionBResult({ success: false, error: d.detail || `HTTP ${res.status}` });
    } catch (e) { setActionBResult({ success: false, error: e.message }); }
    setActionBLoading(false);
  };

  const toggleSamples = (key) => setShowSamples(prev => ({ ...prev, [key]: !prev[key] }));

  // Not loaded yet
  if (!repairData && !repairLoading) {
    return (
      <div style={{
        padding: '14px 18px', marginTop: 12, background: '#0a0a14',
        border: '1px solid #1e3a1e', borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ color: '#86efac', fontSize: 13, fontWeight: 700 }}>
            <Shield size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
            Reference Repair
          </div>
          <div style={{ color: '#525252', fontSize: 11, marginTop: 2 }}>
            Database consistency repair. No S3 operations. No file deletion. No carousel changes.
          </div>
        </div>
        <button onClick={fetchRepairDryrun} style={{
          padding: '6px 16px', background: '#14532d', borderRadius: 6,
          border: '1px solid #166534', color: '#86efac', fontSize: 12,
          cursor: 'pointer', fontWeight: 600,
        }}>
          Load Dry-Run
        </button>
      </div>
    );
  }

  if (repairLoading && !repairData) {
    return (
      <div style={{ padding: '14px 18px', marginTop: 12, background: '#0a0a14', border: '1px solid #1e3a1e', borderRadius: 8, color: '#525252', fontSize: 12 }}>
        Computing reference repair dry-run...
      </div>
    );
  }

  if (!repairData) return null;

  const aa = repairData.action_a || {};
  const ab = repairData.action_b || {};
  const unsafe = repairData.unsafe_items || {};
  const stale = repairData.stale_references || {};
  const sm = repairData.summary || {};
  const isHealthy = sm.healthy;

  return (
    <div style={{
      marginTop: 12, background: '#0a0a14',
      border: `1px solid ${isHealthy ? '#16653444' : '#166534'}`,
      borderRadius: 8, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid #1f2f1f',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Shield size={14} color="#86efac" />
          <span style={{ color: '#86efac', fontSize: 13, fontWeight: 700 }}>Reference Repair</span>
          <span style={{
            padding: '2px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700,
            background: isHealthy ? '#052e16' : '#1c1917',
            border: `1px solid ${isHealthy ? '#166534' : '#854d0e'}`,
            color: isHealthy ? '#4ade80' : '#fbbf24',
          }}>
            {isHealthy ? 'CONSISTENT' : 'REPAIRS AVAILABLE'}
          </span>
        </div>
        <button onClick={fetchRepairDryrun} disabled={repairLoading} style={{
          padding: '4px 12px', background: '#0a0a0a', borderRadius: 4,
          border: '1px solid #1f1f1f', color: '#525252', fontSize: 10,
          cursor: 'pointer',
        }}>
          {repairLoading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Safety warnings */}
      <div style={{ padding: '10px 18px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {['Database repair', 'No S3 operations', 'No file deletion', 'No carousel changes', 'Live dependencies'].map(w => (
          <span key={w} style={{
            padding: '2px 8px', background: '#052e16', borderRadius: 4,
            border: '1px solid #16653444', color: '#86efac', fontSize: 9, fontWeight: 600,
          }}>{w}</span>
        ))}
      </div>

      {/* Summary stats */}
      <div style={{ padding: '8px 18px 12px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <DryRunStat label="Expected Refs" value={repairData.expected_references} color="#86efac" />
        <DryRunStat label="Correct" value={repairData.current_correct_references} color="#4ade80" />
        <DryRunStat label="Action A" value={aa.rows_to_insert} color="#38bdf8" />
        <DryRunStat label="Action B" value={ab.assets_to_restore} color="#f59e0b" />
        <DryRunStat label="Unsafe" value={unsafe.count} color="#f87171" />
        <DryRunStat label="Stale" value={stale.count} color="#737373" />
      </div>

      {/* Issues */}
      {sm.issues && sm.issues.length > 0 && (
        <div style={{ padding: '0 18px 10px' }}>
          {sm.issues.map((issue, i) => (
            <div key={i} style={{
              padding: '4px 10px', marginBottom: 4, fontSize: 11, borderRadius: 4,
              background: issue.includes('consistent') ? '#052e1611' : '#1c191711',
              border: `1px solid ${issue.includes('consistent') ? '#16653422' : '#854d0e22'}`,
              color: issue.includes('consistent') ? '#4ade80' : '#a3a3a3',
            }}>
              {issue.includes('consistent') ? '✓' : '⚠'} {issue}
            </div>
          ))}
        </div>
      )}

      {/* ── Action A Card ──────────────────────────────────────────── */}
      {aa.rows_to_insert > 0 && (
        <div style={{
          margin: '0 18px 12px', padding: 14,
          background: '#0c1929', border: '1px solid #1e40af44',
          borderRadius: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#38bdf8' }} />
            <span style={{ color: '#93c5fd', fontSize: 13, fontWeight: 700 }}>
              Action A — Rebuild Active Missing References
            </span>
            <span style={{
              padding: '2px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700,
              background: '#0c1929', border: '1px solid #1e40af',
              color: '#60a5fa',
            }}>
              {aa.rows_to_insert} row(s)
            </span>
          </div>
          <div style={{ fontSize: 11, color: '#737373', marginBottom: 10 }}>
            {aa.description}
          </div>

          {/* Samples toggle */}
          <button onClick={() => toggleSamples('a')} style={{
            padding: '3px 10px', background: 'none', border: '1px solid #1f1f3f',
            borderRadius: 4, color: '#525252', fontSize: 10, cursor: 'pointer', marginBottom: 8,
          }}>
            {showSamples.a ? 'Hide' : 'Show'} {aa.samples?.length || 0} sample(s)
          </button>

          {showSamples.a && aa.samples && (
            <div style={{ marginBottom: 10, maxHeight: 200, overflow: 'auto' }}>
              {aa.samples.map((s, i) => (
                <div key={i} style={{
                  padding: '4px 8px', marginBottom: 3, fontSize: 10,
                  borderLeft: '2px solid #1e40af22', color: '#737373',
                }}>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#93c5fd' }}>
                    {_truncId(s.asset_id)}
                  </span>
                  <span style={{ color: '#525252' }}> → {s.field_path}</span>
                  <span style={{ color: '#525252' }}> in "{s.carousel_title}"</span>
                  <span style={{ color: '#3a3a3a', marginLeft: 8 }}>{s.kind}</span>
                </div>
              ))}
            </div>
          )}

          {/* Confirmation input + execute */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="text"
              value={confirmA}
              onChange={e => setConfirmA(e.target.value)}
              placeholder="Type: REBUILD ACTIVE REFERENCES"
              style={{
                flex: 1, padding: '6px 10px', background: '#0a0a1a',
                border: '1px solid #1e1e3a', borderRadius: 4,
                color: '#a3a3a3', fontSize: 11, fontFamily: "'IBM Plex Mono', monospace",
              }}
            />
            <button
              onClick={executeActionA}
              disabled={confirmA !== 'REBUILD ACTIVE REFERENCES' || actionALoading}
              style={{
                padding: '6px 14px', borderRadius: 4,
                background: confirmA === 'REBUILD ACTIVE REFERENCES' ? '#1e40af' : '#1a1a2e',
                border: `1px solid ${confirmA === 'REBUILD ACTIVE REFERENCES' ? '#3b82f6' : '#2d2d5e'}`,
                color: confirmA === 'REBUILD ACTIVE REFERENCES' ? '#dbeafe' : '#525252',
                fontSize: 11, fontWeight: 600, cursor: 'pointer',
                opacity: confirmA === 'REBUILD ACTIVE REFERENCES' && !actionALoading ? 1 : 0.5,
              }}
            >
              {actionALoading ? 'Executing...' : 'Execute Action A'}
            </button>
          </div>

          {/* Result */}
          {actionAResult && (
            <div style={{
              marginTop: 8, padding: '8px 10px', borderRadius: 4,
              background: actionAResult.success ? '#052e16' : '#1c0505',
              border: `1px solid ${actionAResult.success ? '#166534' : '#991b1b'}`,
              fontSize: 11,
              color: actionAResult.success ? '#4ade80' : '#f87171',
            }}>
              {actionAResult.success
                ? `✓ Done — inserted: ${actionAResult.inserted}, skipped: ${actionAResult.skipped}, orphan_cleared: ${actionAResult.orphan_cleared}`
                : `✗ Error: ${actionAResult.error}`}
            </div>
          )}
        </div>
      )}

      {/* ── Action B Card ──────────────────────────────────────────── */}
      {ab.assets_to_restore > 0 && (
        <div style={{
          margin: '0 18px 12px', padding: 14,
          background: '#1c1917', border: '1px solid #854d0e44',
          borderRadius: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b' }} />
            <span style={{ color: '#fbbf24', fontSize: 13, fontWeight: 700 }}>
              Action B — Restore Deleted-but-Live Backgrounds
            </span>
            <span style={{
              padding: '2px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700,
              background: '#1c1917', border: '1px solid #854d0e',
              color: '#fbbf24',
            }}>
              {ab.assets_to_restore} asset(s), {ab.refs_to_insert} ref(s)
            </span>
          </div>
          <div style={{ fontSize: 11, color: '#737373', marginBottom: 10 }}>
            {ab.description}
          </div>

          {/* Samples toggle */}
          <button onClick={() => toggleSamples('b')} style={{
            padding: '3px 10px', background: 'none', border: '1px solid #2f2f1f',
            borderRadius: 4, color: '#525252', fontSize: 10, cursor: 'pointer', marginBottom: 8,
          }}>
            {showSamples.b ? 'Hide' : 'Show'} {ab.samples?.length || 0} sample(s)
          </button>

          {showSamples.b && ab.samples && (
            <div style={{ marginBottom: 10, maxHeight: 200, overflow: 'auto' }}>
              {ab.samples.map((s, i) => (
                <div key={i} style={{
                  padding: '4px 8px', marginBottom: 3, fontSize: 10,
                  borderLeft: '2px solid #854d0e22', color: '#737373',
                }}>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#fbbf24' }}>
                    {_truncId(s.asset_id)}
                  </span>
                  <span style={{ color: '#525252' }}> → {s.field_path}</span>
                  <span style={{ color: '#525252' }}> in "{s.carousel_title}"</span>
                  <span style={{ color: '#f87171', marginLeft: 6 }}>deleted: {s.deleted_at?.split('T')[0]}</span>
                </div>
              ))}
            </div>
          )}

          {/* Confirmation input + execute */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="text"
              value={confirmB}
              onChange={e => setConfirmB(e.target.value)}
              placeholder="Type: RESTORE LIVE BACKGROUNDS"
              style={{
                flex: 1, padding: '6px 10px', background: '#0a0a0a',
                border: '1px solid #2f2f1f', borderRadius: 4,
                color: '#a3a3a3', fontSize: 11, fontFamily: "'IBM Plex Mono', monospace",
              }}
            />
            <button
              onClick={executeActionB}
              disabled={confirmB !== 'RESTORE LIVE BACKGROUNDS' || actionBLoading}
              style={{
                padding: '6px 14px', borderRadius: 4,
                background: confirmB === 'RESTORE LIVE BACKGROUNDS' ? '#854d0e' : '#1c1917',
                border: `1px solid ${confirmB === 'RESTORE LIVE BACKGROUNDS' ? '#d97706' : '#3f3f1f'}`,
                color: confirmB === 'RESTORE LIVE BACKGROUNDS' ? '#fef3c7' : '#525252',
                fontSize: 11, fontWeight: 600, cursor: 'pointer',
                opacity: confirmB === 'RESTORE LIVE BACKGROUNDS' && !actionBLoading ? 1 : 0.5,
              }}
            >
              {actionBLoading ? 'Executing...' : 'Execute Action B'}
            </button>
          </div>

          {/* Result */}
          {actionBResult && (
            <div style={{
              marginTop: 8, padding: '8px 10px', borderRadius: 4,
              background: actionBResult.success ? '#052e16' : '#1c0505',
              border: `1px solid ${actionBResult.success ? '#166534' : '#991b1b'}`,
              fontSize: 11,
              color: actionBResult.success ? '#4ade80' : '#f87171',
            }}>
              {actionBResult.success
                ? `✓ Done — assets_restored: ${actionBResult.assets_restored}, refs_inserted: ${actionBResult.refs_inserted}, skipped: ${actionBResult.assets_skipped}`
                : `✗ Error: ${actionBResult.error}`}
            </div>
          )}
        </div>
      )}

      {/* ── Unsafe Items ───────────────────────────────────────────── */}
      {unsafe.count > 0 && (
        <div style={{ margin: '0 18px 12px', padding: 10, background: '#1c0a0a', border: '1px solid #991b1b44', borderRadius: 6 }}>
          <div style={{ fontSize: 11, color: '#f87171', fontWeight: 600, marginBottom: 4 }}>
            {unsafe.count} Ambiguous/Unsafe Item(s) — Manual Review Required
          </div>
          <div style={{ fontSize: 10, color: '#737373', marginBottom: 6 }}>{unsafe.description}</div>
          <button onClick={() => toggleSamples('unsafe')} style={{
            padding: '2px 8px', background: 'none', border: '1px solid #991b1b22',
            borderRadius: 3, color: '#525252', fontSize: 9, cursor: 'pointer',
          }}>
            {showSamples.unsafe ? 'Hide' : 'Show'} samples
          </button>
          {showSamples.unsafe && (unsafe.samples || []).map((s, i) => (
            <div key={i} style={{
              padding: '3px 8px', marginTop: 3, fontSize: 10,
              borderLeft: '2px solid #991b1b22', color: '#737373',
            }}>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#fca5a5' }}>
                {_truncId(s.asset_id)}
              </span>
              <span style={{ color: '#525252' }}> → {s.field_path} in "{s.carousel_title}"</span>
              <div style={{ color: '#3a3a3a', fontSize: 9 }}>{s.reason}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Healthy state ──────────────────────────────────────────── */}
      {isHealthy && (
        <div style={{
          margin: '0 18px 14px', padding: '10px 14px', borderRadius: 6,
          background: '#052e16', border: '1px solid #16653444',
          fontSize: 12, color: '#4ade80', fontWeight: 600,
        }}>
          ✓ All references are consistent — no repairs needed
        </div>
      )}
    </div>
  );
}


function UsageButton({ assetId, storageKey, onUsage }) {
  if (!assetId && !storageKey) return null;
  return (
    <button
      onClick={() => onUsage({ asset_id: assetId, storage_key: storageKey })}
      title="Inspect usage — why tracked?"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        padding: '2px 8px', background: '#1a1a2e', borderRadius: 4,
        border: '1px solid #2d2d5e', color: '#818cf8', fontSize: 10,
        cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
      }}
    >
      <Search size={10} /> Usage
    </button>
  );
}


function ReferenceConsistencyTable({ samples, category, type, onPreview, headers, onUsage }) {
  const isStale = type === 'stale';
  return (
    <div>
      {/* Banner */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px', marginBottom: 12,
        background: isStale ? '#1c1917' : '#0c1929',
        border: `1px solid ${isStale ? '#854d0e44' : '#1e40af44'}`,
        borderRadius: 6, color: isStale ? '#d4a574' : '#93c5fd', fontSize: 11,
      }}>
        {isStale ? <LinkIcon size={14} color="#fb923c" /> : <Search size={14} color="#38bdf8" />}
        <span>
          {isStale ? (
            <><strong>Suspected stale references.</strong> asset_references rows exist
            but current carousel JSONB no longer contains the asset. Diagnostic only — not cleanup-safe.</>
          ) : (
            <><strong>Missing asset_references rows.</strong> Carousel JSONB contains these assets
            but no matching asset_references row exists. Reference mismatch — diagnostic only.</>
          )}
        </span>
      </div>
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Risk</th>
              <th style={styles.th}>Asset ID</th>
              <th style={styles.th}>Kind</th>
              <th style={styles.th}>Carousel</th>
              {!isStale && <th style={styles.th}>Field Path</th>}
              {isStale && <th style={styles.th}>Ref Count</th>}
              <th style={styles.th}>Size</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Reason</th>
              <th style={{ ...styles.th, width: 60 }}>Inspect</th>
            </tr>
          </thead>
          <tbody>
            {samples.map((s, i) => (
              <tr key={`${s.asset_id}-${s.carousel_id}-${i}`} style={i % 2 === 0 ? styles.trEven : undefined}>
                <td style={styles.td}><RiskBadge risk={s.risk} /></td>
                <td style={{ ...styles.td, fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                  {_truncId(s.asset_id)}
                </td>
                <td style={styles.td}><KindBadge kind={s.kind} /></td>
                <td style={{ ...styles.td, fontSize: 11, maxWidth: 150 }}>
                  {s.carousel_title || _truncId(s.carousel_id)}
                </td>
                {!isStale && (
                  <td style={{ ...styles.td, fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", color: '#a3a3a3' }}>
                    {s.field_path || '—'}
                  </td>
                )}
                {isStale && (
                  <td style={{ ...styles.td, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {s.ref_count}
                  </td>
                )}
                <td style={{ ...styles.td, fontFamily: "'IBM Plex Mono', monospace", whiteSpace: 'nowrap' }}>
                  {s.size_human || '—'}
                </td>
                <td style={{ ...styles.td, fontSize: 10 }}>
                  <span style={{
                    padding: '1px 5px', borderRadius: 3, fontSize: 9,
                    background: s.asset_status === 'confirmed' ? '#052e1622' : '#1c0a0a',
                    color: s.asset_status === 'confirmed' ? '#4ade80' : '#f87171',
                    border: `1px solid ${s.asset_status === 'confirmed' ? '#16653444' : '#991b1b44'}`,
                  }}>
                    {s.asset_status || 'unknown'}
                  </span>
                </td>
                <td style={{ ...styles.td, maxWidth: 250, fontSize: 11, color: '#737373' }}>
                  {s.reason}
                </td>
                <td style={styles.td}>
                  <UsageButton assetId={s.asset_id} storageKey={s.storage_key} onUsage={onUsage} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function AssetUsageModal({ assetId, storageKey, headers, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (assetId) params.set('asset_id', assetId);
    if (storageKey) params.set('storage_key', storageKey);

    fetch(`${BACKEND_URL}/api/admin/storage/asset-usage?${params.toString()}`, { headers })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [assetId, storageKey, headers]);

  const us = data?.usage_summary || {};

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
    }} onClick={onClose}>
      <div style={{
        background: '#141414', border: '1px solid #2a2a2a', borderRadius: 10,
        width: 760, maxHeight: '85vh', overflow: 'auto',
        padding: 0, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: '1px solid #1f1f1f',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Search size={16} color="#818cf8" />
            <span style={{ color: '#e5e5e5', fontWeight: 700, fontSize: 14 }}>Asset Usage Inspector</span>
            <span style={{
              padding: '2px 8px', background: '#0c1929', borderRadius: 4,
              border: '1px solid #1e40af', color: '#60a5fa', fontSize: 9, fontWeight: 700,
            }}>DIAGNOSTIC ONLY</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#525252' }}>
            <X size={18} />
          </button>
        </div>

        {loading && <div style={{ padding: 40, textAlign: 'center', color: '#525252' }}>Loading usage report...</div>}
        {error && <div style={{ padding: 20, color: '#f87171' }}>Error: {error}</div>}

        {data && (
          <div style={{ padding: '16px 20px' }}>
            {/* Asset metadata */}
            {data.asset ? (
              <div style={{ marginBottom: 16, padding: 12, background: '#0a0a0a', borderRadius: 6, border: '1px solid #1f1f1f' }}>
                <div style={{ fontSize: 10, color: '#525252', marginBottom: 6, fontWeight: 600 }}>ASSET METADATA</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, fontSize: 11 }}>
                  <div><span style={{ color: '#525252' }}>ID: </span><span style={{ color: '#a3a3a3', fontFamily: "'IBM Plex Mono', monospace", fontSize: 10 }}>{data.asset.asset_id}</span></div>
                  <div><span style={{ color: '#525252' }}>Kind: </span><span style={{ color: '#a3a3a3' }}>{data.asset.kind}</span></div>
                  <div><span style={{ color: '#525252' }}>Status: </span><span style={{ color: data.asset.status === 'confirmed' ? '#4ade80' : '#f87171' }}>{data.asset.status}</span></div>
                  <div><span style={{ color: '#525252' }}>User: </span><span style={{ color: '#a3a3a3', fontFamily: "'IBM Plex Mono', monospace", fontSize: 10 }}>{_truncId(data.asset.user_id)}</span></div>
                  <div><span style={{ color: '#525252' }}>Type: </span><span style={{ color: '#a3a3a3' }}>{data.asset.content_type || '—'}</span></div>
                  <div><span style={{ color: '#525252' }}>Size: </span><span style={{ color: '#a3a3a3' }}>{data.asset.size_bytes ? `${(data.asset.size_bytes / 1024 / 1024).toFixed(2)} MB` : '—'}</span></div>
                  {data.asset.orphaned_at && <div style={{ gridColumn: 'span 3' }}><span style={{ color: '#f87171' }}>Orphaned: {new Date(data.asset.orphaned_at).toLocaleString()}</span></div>}
                  {data.asset.deleted_at && <div style={{ gridColumn: 'span 3' }}><span style={{ color: '#f87171' }}>Deleted: {new Date(data.asset.deleted_at).toLocaleString()}</span></div>}
                </div>
                <div style={{ marginTop: 6, fontSize: 10, color: '#3a3a3a', fontFamily: "'IBM Plex Mono', monospace", wordBreak: 'break-all' }}>
                  {data.asset.storage_key}
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: 16, padding: 12, background: '#1c0a0a', borderRadius: 6, border: '1px solid #991b1b', color: '#fca5a5', fontSize: 12 }}>
                No media_assets record found.
                {data.lookup_confidence === 'low' && ' (storage_key lookup — limited confidence)'}
              </div>
            )}

            {/* Usage Summary — the key visual */}
            <div style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              <UsageBadge label="In Carousel JSONB" value={us.used_in_current_carousel_jsonb} />
              <UsageBadge label="In asset_references" value={us.used_in_asset_references_table} />
              <UsageBadge label="In render_jobs" value={us.used_in_render_jobs} />
              <UsageBadge label="In backups/system" value={us.used_in_backups || us.used_in_system_objects} />
            </div>

            {/* Verdict */}
            <div style={{
              padding: '10px 14px', marginBottom: 16, borderRadius: 6,
              background: us.is_really_used_by_current_slides ? '#052e16' : '#1c0a0a',
              border: `1px solid ${us.is_really_used_by_current_slides ? '#166534' : '#991b1b'}`,
            }}>
              <div style={{
                fontSize: 13, fontWeight: 700,
                color: us.is_really_used_by_current_slides ? '#4ade80' : '#f87171',
              }}>
                {us.is_really_used_by_current_slides
                  ? '✓ Used in current carousel slides'
                  : '✗ Not found in current carousel JSONB'
                }
              </div>
              {us.suspected_stale_reference && (
                <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 4 }}>
                  ⚠ Suspected stale reference — asset_references row exists but carousel JSONB disagrees
                </div>
              )}
              <div style={{ fontSize: 10, color: '#525252', marginTop: 4 }}>
                Confidence: {data.lookup_confidence} · {data.carousel_count_scanned} carousel(s) scanned · Fields: {(data.carousel_asset_fields_checked || []).join(', ')}
              </div>
            </div>

            {/* Carousel JSONB References */}
            {data.carousel_jsonb_references.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: '#525252', marginBottom: 6, fontWeight: 600 }}>
                  CAROUSEL JSONB REFERENCES ({data.carousel_jsonb_references.length})
                </div>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Carousel</th>
                      <th style={styles.th}>State</th>
                      <th style={styles.th}>Slide</th>
                      <th style={styles.th}>Field Path</th>
                      <th style={styles.th}>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.carousel_jsonb_references.map((ref, i) => (
                      <tr key={i} style={i % 2 === 0 ? styles.trEven : undefined}>
                        <td style={{ ...styles.td, fontSize: 11 }}>{ref.carousel_title}</td>
                        <td style={{ ...styles.td, fontSize: 10, color: '#737373' }}>{ref.carousel_state}</td>
                        <td style={{ ...styles.td, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>
                          {ref.slide_index !== null ? `#${ref.slide_index}` : '—'}
                        </td>
                        <td style={{ ...styles.td, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: '#a3a3a3' }}>
                          {ref.field_path}
                        </td>
                        <td style={styles.td}><KindBadge kind={ref.field_type} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* asset_references table rows */}
            {data.asset_reference_rows.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: '#525252', marginBottom: 6, fontWeight: 600 }}>
                  ASSET_REFERENCES TABLE ROWS ({data.asset_reference_rows.length})
                </div>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Carousel</th>
                      <th style={styles.th}>Ref Count</th>
                      <th style={styles.th}>User</th>
                      <th style={styles.th}>Created</th>
                      <th style={styles.th}>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.asset_reference_rows.map((ref, i) => (
                      <tr key={i} style={i % 2 === 0 ? styles.trEven : undefined}>
                        <td style={{ ...styles.td, fontSize: 11 }}>{ref.carousel_title}</td>
                        <td style={{ ...styles.td, fontFamily: "'IBM Plex Mono', monospace" }}>{ref.ref_count}</td>
                        <td style={{ ...styles.td, fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>{_truncId(ref.user_id)}</td>
                        <td style={{ ...styles.td, fontSize: 11 }}>{ref.created_at ? new Date(ref.created_at).toLocaleDateString() : '—'}</td>
                        <td style={{ ...styles.td, fontSize: 11 }}>{ref.updated_at ? new Date(ref.updated_at).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Mismatches */}
            {data.mismatches.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: '#525252', marginBottom: 6, fontWeight: 600 }}>
                  MISMATCHES ({data.mismatches.length})
                </div>
                {data.mismatches.map((m, i) => (
                  <div key={i} style={{
                    padding: '8px 12px', marginBottom: 6, borderRadius: 6,
                    background: m.severity === 'warning' ? '#1c1917' : '#0a0a0a',
                    border: `1px solid ${m.severity === 'warning' ? '#854d0e44' : '#1f1f1f'}`,
                  }}>
                    <span style={{
                      padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 700, marginRight: 8,
                      background: m.severity === 'warning' ? '#1c1917' : '#0c1929',
                      color: m.severity === 'warning' ? '#fbbf24' : '#60a5fa',
                      border: `1px solid ${m.severity === 'warning' ? '#854d0e' : '#1e40af'}`,
                    }}>
                      {m.type.replace(/_/g, ' ').toUpperCase()}
                    </span>
                    <span style={{ fontSize: 11, color: '#a3a3a3' }}>{m.message}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Tracking Sources */}
            <div>
              <div style={{ fontSize: 10, color: '#525252', marginBottom: 6, fontWeight: 600 }}>TRACKING SOURCES</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {Object.entries(data.tracking_sources).map(([source, present]) => (
                  <div key={source} style={{
                    padding: '3px 10px', borderRadius: 4, fontSize: 10,
                    background: present ? '#052e1622' : '#0a0a0a',
                    border: `1px solid ${present ? '#16653444' : '#1f1f1f'}`,
                    color: present ? '#4ade80' : '#3a3a3a',
                    fontWeight: present ? 600 : 400,
                  }}>
                    {present ? '●' : '○'} {source}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function UsageBadge({ label, value }) {
  return (
    <div style={{
      padding: '8px 10px', borderRadius: 6, textAlign: 'center',
      background: value ? '#052e1622' : '#0a0a0a',
      border: `1px solid ${value ? '#16653444' : '#1f1f1f'}`,
    }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: value ? '#4ade80' : '#3a3a3a' }}>
        {value ? 'Yes' : 'No'}
      </div>
      <div style={{ fontSize: 9, color: '#525252', marginTop: 2 }}>{label}</div>
    </div>
  );
}


function KindBadge({ kind }) {
  const colors = {
    video: '#a78bfa',
    image: '#06b6d4',
    background: '#10b981',
  };
  const c = colors[kind] || '#737373';
  return (
    <span style={{
      display: 'inline-block', padding: '1px 6px', borderRadius: 3,
      fontSize: 10, fontWeight: 600, background: c + '18', color: c,
      border: `1px solid ${c}33`,
    }}>
      {kind || '?'}
    </span>
  );
}

function PreviewModal({ s3Key, headers, onClose }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isImage = /\.(png|jpe?g|webp|gif)$/i.test(s3Key);
  const isVideo = /\.(mp4|mov|avi|mkv|webm)$/i.test(s3Key);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/admin/storage/preview?key=${encodeURIComponent(s3Key)}`, { headers })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => { if (!cancelled) { setUrl(data.url); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [s3Key, headers]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.85)', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#141415', border: '1px solid #333', borderRadius: 10,
          padding: 16, maxWidth: 720, width: '90%', position: 'relative',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 8, right: 8, background: 'none',
            border: 'none', cursor: 'pointer', color: '#737373', padding: 4,
          }}
        >
          <X size={18} />
        </button>
        <div style={{ color: '#525252', fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", marginBottom: 10, paddingRight: 24, wordBreak: 'break-all' }}>
          {s3Key}
        </div>
        {loading && <div style={{ color: '#525252', textAlign: 'center', padding: 40 }}>Loading preview…</div>}
        {error && <div style={{ color: '#ef4444', textAlign: 'center', padding: 40 }}>Preview unavailable: {error}</div>}
        {url && !error && isImage && (
          <img
            src={url}
            alt="Preview"
            style={{ width: '100%', borderRadius: 6, maxHeight: 480, objectFit: 'contain', background: '#000' }}
          />
        )}
        {url && !error && isVideo && (
          <video
            src={url}
            controls
            autoPlay={false}
            style={{ width: '100%', borderRadius: 6, background: '#000', maxHeight: 480 }}
          />
        )}
        {url && !error && !isImage && !isVideo && (
          <div style={{ color: '#525252', textAlign: 'center', padding: 40 }}>
            Cannot preview this file type.{' '}
            <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#f59e0b' }}>
              Open <ExternalLink size={12} style={{ verticalAlign: 'middle' }} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

function _timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function _truncId(id) {
  if (!id) return '—';
  if (id.length <= 12) return id;
  return id.slice(0, 8) + '…';
}

function _truncateKey(key) {
  if (!key) return '';
  if (key.length <= 55) return key;
  return '…' + key.slice(-52);
}


// ── Normalize Persisted Media URLs Card ──────────────────────────────────

function NormalizeMediaUrlsCard({ headers }) {
  const [dryRunData, setDryRunData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [applyResult, setApplyResult] = useState(null);
  const [applyLoading, setApplyLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const CONFIRM_STRING = 'NORMALIZE PERSISTED MEDIA URLS';

  const runDryRun = useCallback(async () => {
    setLoading(true);
    setApplyResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/cleanup/normalize-persisted-media-urls`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dry_run: true }),
      });
      if (res.ok) {
        setDryRunData(await res.json());
        setConfirmText('');
      }
    } catch (_) { /* ignore */ }
    setLoading(false);
  }, [headers]);

  const runApply = async () => {
    setApplyLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/cleanup/normalize-persisted-media-urls`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dry_run: false, confirm: confirmText }),
      });
      const d = await res.json();
      if (res.ok) { setApplyResult(d); setConfirmText(''); await runDryRun(); }
      else setApplyResult({ error: d.detail || `HTTP ${res.status}` });
    } catch (e) { setApplyResult({ error: e.message }); }
    setApplyLoading(false);
  };

  // Not loaded yet
  if (!dryRunData && !loading) {
    return (
      <div style={{
        padding: '14px 18px', marginTop: 12, background: '#0a0a14',
        border: '1px solid #1e2e3e', borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ color: '#60a5fa', fontSize: 13, fontWeight: 700 }}>
            <Database size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
            Normalize Persisted Media URLs
          </div>
          <div style={{ color: '#525252', fontSize: 11, marginTop: 2 }}>
            Replace presigned S3 URLs in carousel.document with stable /api/media/asset/ routes. No S3 operations.
          </div>
        </div>
        <button onClick={runDryRun} style={{
          padding: '6px 16px', background: '#172554', borderRadius: 6,
          border: '1px solid #1e3a5f', color: '#60a5fa', fontSize: 12,
          cursor: 'pointer', fontWeight: 600,
        }}>
          Run Dry-Run
        </button>
      </div>
    );
  }

  if (loading && !dryRunData) {
    return (
      <div style={{ padding: '14px 18px', marginTop: 12, background: '#0a0a14', border: '1px solid #1e2e3e', borderRadius: 8, color: '#525252', fontSize: 12 }}>
        Scanning carousel documents for presigned URLs...
      </div>
    );
  }

  if (!dryRunData) return null;

  const d = dryRunData;
  const hasChanges = d.total_fields_replaced > 0;
  const bt = d.changes_by_type || {};

  return (
    <div style={{
      marginTop: 12, background: '#0a0a14',
      border: `1px solid ${hasChanges ? '#1e3a5f' : '#1e2e3e44'}`,
      borderRadius: 8, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid #1a2a3a',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Database size={14} color="#60a5fa" />
          <span style={{ color: '#60a5fa', fontSize: 13, fontWeight: 700 }}>Normalize Persisted Media URLs</span>
          <span style={{
            padding: '2px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700,
            background: hasChanges ? '#1c1917' : '#052e16',
            border: `1px solid ${hasChanges ? '#854d0e' : '#166534'}`,
            color: hasChanges ? '#fbbf24' : '#4ade80',
          }}>
            {hasChanges ? `${d.total_fields_replaced} FIELDS TO REPLACE` : 'ALL CLEAN'}
          </span>
        </div>
        <button onClick={runDryRun} disabled={loading} style={{
          padding: '4px 12px', background: '#0a0a0a', borderRadius: 4,
          border: '1px solid #1f1f1f', color: '#525252', fontSize: 10, cursor: 'pointer',
        }}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Safety pills */}
      <div style={{ padding: '10px 18px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {['DB normalization only', 'No S3 operations', 'No file deletion', 'No asset lifecycle changes', 'Carousel document URLs only'].map(w => (
          <span key={w} style={{
            padding: '2px 8px', background: '#17254411', borderRadius: 4,
            border: '1px solid #1e3a5f44', color: '#60a5fa', fontSize: 9, fontWeight: 600,
          }}>{w}</span>
        ))}
      </div>

      {/* Summary stats */}
      <div style={{ padding: '8px 18px 12px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <DryRunStat label="Carousels Scanned" value={d.carousels_scanned} color="#60a5fa" />
        <DryRunStat label="Slides Scanned" value={d.slides_scanned} color="#60a5fa" />
        <DryRunStat label="Fields to Replace" value={d.total_fields_replaced} color={hasChanges ? '#fbbf24' : '#4ade80'} />
        <DryRunStat label="Carousels with Changes" value={d.carousels_with_changes} color={hasChanges ? '#fbbf24' : '#4ade80'} />
        <DryRunStat label="Legacy src-only" value={d.legacy_src_only_count} color="#a3a3a3" />
      </div>

      {/* Breakdown by type */}
      {hasChanges && (
        <div style={{ padding: '0 18px 12px' }}>
          <div style={{ fontSize: 10, color: '#525252', marginBottom: 6 }}>Replacement breakdown:</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              { key: 'backgroundUrl_presigned', label: 'BG presigned', color: '#f59e0b' },
              { key: 'backgroundUrl_legacy', label: 'BG legacy', color: '#fb923c' },
              { key: 'imageWidget_src_presigned', label: 'Image presigned', color: '#ec4899' },
              { key: 'imageWidget_src_legacy', label: 'Image legacy', color: '#f472b6' },
              { key: 'videoWidget_src_presigned', label: 'Video presigned', color: '#8b5cf6' },
              { key: 'videoWidget_src_legacy', label: 'Video legacy', color: '#a78bfa' },
              { key: 'logo_url_presigned', label: 'Logo presigned', color: '#06b6d4' },
              { key: 'logo_url_legacy', label: 'Logo legacy', color: '#22d3ee' },
            ].filter(t => bt[t.key] > 0).map(t => (
              <span key={t.key} style={{
                padding: '2px 8px', borderRadius: 4, fontSize: 10,
                background: '#0a0a0a', border: `1px solid ${t.color}44`, color: t.color,
              }}>
                {t.label}: {bt[t.key]}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Per-carousel details toggle */}
      {d.per_carousel_changes && d.per_carousel_changes.length > 0 && (
        <div style={{ padding: '0 18px 12px' }}>
          <button onClick={() => setShowDetails(!showDetails)} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', color: '#60a5fa', fontSize: 11,
            cursor: 'pointer', padding: 0,
          }}>
            {showDetails ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            Per-carousel details ({d.per_carousel_changes.length} carousels)
          </button>

          {showDetails && d.per_carousel_changes.map((cc, ci) => (
            <div key={ci} style={{
              marginTop: 8, padding: 10, background: '#0a0a0a', borderRadius: 6,
              border: '1px solid #1f1f1f',
            }}>
              <div style={{ color: '#d4d4d4', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>
                {cc.carousel_name} <span style={{ color: '#525252', fontWeight: 400 }}>({cc.carousel_id})</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                <thead>
                  <tr style={{ color: '#525252' }}>
                    <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #1f1f1f' }}>Field</th>
                    <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #1f1f1f' }}>Old Value</th>
                    <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #1f1f1f' }}>New Value</th>
                    <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #1f1f1f' }}>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {cc.changes.map((ch, chi) => (
                    <tr key={chi}>
                      <td style={{ padding: '3px 6px', color: '#a3a3a3', fontFamily: "'IBM Plex Mono', monospace" }}>{ch.field_path}</td>
                      <td style={{ padding: '3px 6px', color: '#737373', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ch.old_value_summary}</td>
                      <td style={{ padding: '3px 6px', color: '#4ade80', fontFamily: "'IBM Plex Mono', monospace" }}>{ch.new_value}</td>
                      <td style={{ padding: '3px 6px', color: '#525252' }}>{ch.replacement_type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Legacy src-only items */}
      {d.legacy_src_only_count > 0 && d.legacy_src_only_items && (
        <div style={{ padding: '0 18px 12px' }}>
          <div style={{
            padding: '6px 10px', background: '#1c191711', border: '1px solid #854d0e22',
            borderRadius: 4, color: '#a3a3a3', fontSize: 10,
          }}>
            ⚠ {d.legacy_src_only_count} legacy src-only widget(s) found (no asset_id). These are NOT auto-rewritten.
          </div>
        </div>
      )}

      {/* Apply section */}
      {hasChanges && (
        <div style={{
          margin: '0 18px 14px', padding: 14,
          background: '#0c0c1a', borderRadius: 8, border: '1px solid #1e3a5f44',
        }}>
          <div style={{ fontSize: 11, color: '#60a5fa', fontWeight: 600, marginBottom: 8 }}>
            Apply URL Normalization
          </div>
          <div style={{ fontSize: 10, color: '#525252', marginBottom: 8 }}>
            This will update carousel.document URL fields in the database. No S3 operations. No file deletion.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              placeholder={CONFIRM_STRING}
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              style={{
                flex: 1, padding: '6px 10px', background: '#0a0a0a', borderRadius: 4,
                border: '1px solid #1f1f1f', color: '#d4d4d4', fontSize: 11,
                fontFamily: "'IBM Plex Mono', monospace",
              }}
            />
            <button
              onClick={runApply}
              disabled={confirmText !== CONFIRM_STRING || applyLoading}
              style={{
                padding: '6px 16px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                cursor: confirmText === CONFIRM_STRING ? 'pointer' : 'not-allowed',
                background: confirmText === CONFIRM_STRING ? '#172554' : '#0a0a0a',
                border: `1px solid ${confirmText === CONFIRM_STRING ? '#1e3a5f' : '#1f1f1f'}`,
                color: confirmText === CONFIRM_STRING ? '#60a5fa' : '#333',
                opacity: applyLoading ? 0.5 : 1,
              }}
            >
              {applyLoading ? 'Applying...' : 'Apply'}
            </button>
          </div>

          {/* Apply result */}
          {applyResult && (
            <div style={{
              marginTop: 8, padding: '6px 10px', borderRadius: 4, fontSize: 10,
              background: applyResult.error ? '#1c0a0a' : '#052e16',
              border: `1px solid ${applyResult.error ? '#991b1b' : '#166534'}`,
              color: applyResult.error ? '#f87171' : '#4ade80',
            }}>
              {applyResult.error
                ? `Error: ${applyResult.error}`
                : `✓ Applied — ${applyResult.total_fields_replaced} fields normalized in ${applyResult.carousels_with_changes} carousels`
              }
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Normalize Deleted Asset Status Card ──────────────────────────────────

function NormalizeDeletedStatusCard({ headers }) {
  const [dryRunData, setDryRunData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [applyResult, setApplyResult] = useState(null);
  const [applyLoading, setApplyLoading] = useState(false);
  const [showSamples, setShowSamples] = useState(false);

  const CONFIRM_STRING = 'NORMALIZE DELETED ASSET STATUS';

  const runDryRun = useCallback(async () => {
    setLoading(true);
    setApplyResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/cleanup/normalize-deleted-asset-status`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dry_run: true }),
      });
      if (res.ok) {
        setDryRunData(await res.json());
        setConfirmText('');
      }
    } catch (_) { /* ignore */ }
    setLoading(false);
  }, [headers]);

  const runApply = async () => {
    setApplyLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/cleanup/normalize-deleted-asset-status`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dry_run: false, confirm: confirmText }),
      });
      const d = await res.json();
      if (res.ok) { setApplyResult(d); setConfirmText(''); await runDryRun(); }
      else setApplyResult({ error: d.detail || `HTTP ${res.status}` });
    } catch (e) { setApplyResult({ error: e.message }); }
    setApplyLoading(false);
  };

  // Not loaded yet
  if (!dryRunData && !loading) {
    return (
      <div style={{
        padding: '14px 18px', marginTop: 12, background: '#0a0a14',
        border: '1px solid #2e1e1e', borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ color: '#fb923c', fontSize: 13, fontWeight: 700 }}>
            <Activity size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
            Normalize Deleted Asset Status
          </div>
          <div style={{ color: '#525252', fontSize: 11, marginTop: 2 }}>
            Fix storage_deleted_at IS NOT NULL but status ≠ 'deleted' inconsistency. No S3 operations.
          </div>
        </div>
        <button onClick={runDryRun} style={{
          padding: '6px 16px', background: '#431407', borderRadius: 6,
          border: '1px solid #7c2d12', color: '#fb923c', fontSize: 12,
          cursor: 'pointer', fontWeight: 600,
        }}>
          Run Dry-Run
        </button>
      </div>
    );
  }

  if (loading && !dryRunData) {
    return (
      <div style={{ padding: '14px 18px', marginTop: 12, background: '#0a0a14', border: '1px solid #2e1e1e', borderRadius: 8, color: '#525252', fontSize: 12 }}>
        Scanning for inconsistent asset status rows...
      </div>
    );
  }

  if (!dryRunData) return null;

  const d = dryRunData;
  const hasIssues = d.inconsistent_count > 0;

  return (
    <div style={{
      marginTop: 12, background: '#0a0a14',
      border: `1px solid ${hasIssues ? '#7c2d12' : '#2e1e1e44'}`,
      borderRadius: 8, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid #2a1a1a',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Activity size={14} color="#fb923c" />
          <span style={{ color: '#fb923c', fontSize: 13, fontWeight: 700 }}>Normalize Deleted Asset Status</span>
          <span style={{
            padding: '2px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700,
            background: hasIssues ? '#1c0a0a' : '#052e16',
            border: `1px solid ${hasIssues ? '#991b1b' : '#166534'}`,
            color: hasIssues ? '#f87171' : '#4ade80',
          }}>
            {hasIssues ? `${d.inconsistent_count} INCONSISTENT` : 'ALL CONSISTENT'}
          </span>
        </div>
        <button onClick={runDryRun} disabled={loading} style={{
          padding: '4px 12px', background: '#0a0a0a', borderRadius: 4,
          border: '1px solid #1f1f1f', color: '#525252', fontSize: 10, cursor: 'pointer',
        }}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Safety pills */}
      <div style={{ padding: '10px 18px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {['DB status repair only', 'No S3 operations', 'No storage_key changes', 'No file deletion', 'Sets status=deleted for already-removed S3 objects'].map(w => (
          <span key={w} style={{
            padding: '2px 8px', background: '#43140711', borderRadius: 4,
            border: '1px solid #7c2d1244', color: '#fb923c', fontSize: 9, fontWeight: 600,
          }}>{w}</span>
        ))}
      </div>

      {/* Stats */}
      <div style={{ padding: '8px 18px 12px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <DryRunStat label="Inconsistent Rows" value={d.inconsistent_count} color={hasIssues ? '#f87171' : '#4ade80'} />
        {Object.entries(d.by_current_status || {}).map(([status, count]) => (
          <DryRunStat key={status} label={`Status: ${status}`} value={count} color="#fbbf24" />
        ))}
      </div>

      {/* Explanation */}
      {hasIssues && (
        <div style={{ padding: '0 18px 10px' }}>
          <div style={{
            padding: '6px 10px', background: '#1c0a0a11', border: '1px solid #991b1b22',
            borderRadius: 4, color: '#a3a3a3', fontSize: 10,
          }}>
            These rows have storage_deleted_at set (S3 object was physically deleted) but status is still '{Object.keys(d.by_current_status || {})[0] || 'confirmed'}'.
            Normalizing sets status = 'deleted' to match physical reality.
          </div>
        </div>
      )}

      {/* Sample rows */}
      {d.samples && d.samples.length > 0 && (
        <div style={{ padding: '0 18px 12px' }}>
          <button onClick={() => setShowSamples(!showSamples)} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', color: '#fb923c', fontSize: 11,
            cursor: 'pointer', padding: 0,
          }}>
            {showSamples ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            Sample rows ({d.samples.length}{d.inconsistent_count > d.samples.length ? ` of ${d.inconsistent_count}` : ''})
          </button>

          {showSamples && (
            <div style={{ marginTop: 8, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                <thead>
                  <tr style={{ color: '#525252' }}>
                    <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #1f1f1f' }}>Asset ID</th>
                    <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #1f1f1f' }}>Kind</th>
                    <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #1f1f1f' }}>Current Status</th>
                    <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #1f1f1f' }}>New Status</th>
                    <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #1f1f1f' }}>Size</th>
                    <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #1f1f1f' }}>Deleted At</th>
                    <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #1f1f1f' }}>Storage Key</th>
                  </tr>
                </thead>
                <tbody>
                  {d.samples.map((s, i) => (
                    <tr key={i}>
                      <td style={{ padding: '3px 6px', color: '#a3a3a3', fontFamily: "'IBM Plex Mono', monospace" }}>{s.asset_id}</td>
                      <td style={{ padding: '3px 6px', color: '#a3a3a3' }}>{s.kind || '—'}</td>
                      <td style={{ padding: '3px 6px', color: '#fbbf24' }}>{s.current_status}</td>
                      <td style={{ padding: '3px 6px', color: '#4ade80' }}>{s.new_status}</td>
                      <td style={{ padding: '3px 6px', color: '#737373' }}>{s.size_bytes ? `${(s.size_bytes / 1024).toFixed(1)} KB` : '—'}</td>
                      <td style={{ padding: '3px 6px', color: '#737373', fontSize: 9 }}>{s.storage_deleted_at || '—'}</td>
                      <td style={{ padding: '3px 6px', color: '#525252', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 9 }}>{s.storage_key || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Apply section */}
      {hasIssues && (
        <div style={{
          margin: '0 18px 14px', padding: 14,
          background: '#0c0806', borderRadius: 8, border: '1px solid #7c2d1244',
        }}>
          <div style={{ fontSize: 11, color: '#fb923c', fontWeight: 600, marginBottom: 8 }}>
            Apply Status Normalization
          </div>
          <div style={{ fontSize: 10, color: '#525252', marginBottom: 8 }}>
            Sets status = 'deleted' for {d.inconsistent_count} row(s) where storage_deleted_at IS NOT NULL. No S3 operations. No storage_key changes.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              placeholder={CONFIRM_STRING}
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              style={{
                flex: 1, padding: '6px 10px', background: '#0a0a0a', borderRadius: 4,
                border: '1px solid #1f1f1f', color: '#d4d4d4', fontSize: 11,
                fontFamily: "'IBM Plex Mono', monospace",
              }}
            />
            <button
              onClick={runApply}
              disabled={confirmText !== CONFIRM_STRING || applyLoading}
              style={{
                padding: '6px 16px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                cursor: confirmText === CONFIRM_STRING ? 'pointer' : 'not-allowed',
                background: confirmText === CONFIRM_STRING ? '#431407' : '#0a0a0a',
                border: `1px solid ${confirmText === CONFIRM_STRING ? '#7c2d12' : '#1f1f1f'}`,
                color: confirmText === CONFIRM_STRING ? '#fb923c' : '#333',
                opacity: applyLoading ? 0.5 : 1,
              }}
            >
              {applyLoading ? 'Applying...' : 'Apply'}
            </button>
          </div>

          {/* Apply result */}
          {applyResult && (
            <div style={{
              marginTop: 8, padding: '6px 10px', borderRadius: 4, fontSize: 10,
              background: applyResult.error ? '#1c0a0a' : '#052e16',
              border: `1px solid ${applyResult.error ? '#991b1b' : '#166534'}`,
              color: applyResult.error ? '#f87171' : '#4ade80',
            }}>
              {applyResult.error
                ? `Error: ${applyResult.error}`
                : `✓ Applied — ${applyResult.inconsistent_count} rows normalized to status='deleted'`
              }
            </div>
          )}
        </div>
      )}
    </div>
  );
}



// ── Lifecycle Foundation Card (PR 1) ─────────────────────────────────

function LifecycleFoundationCard({ headers }) {
  const [tab, setTab] = useState('summary');
  const [summary, setSummary] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const [preview, setPreview] = useState(null);
  const [execResult, setExecResult] = useState(null);
  const [events, setEvents] = useState(null);
  const [evtLimit, setEvtLimit] = useState(20);
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Repair state
  const [repairPreview, setRepairPreview] = useState(null);
  const [repairResult, setRepairResult] = useState(null);
  const [repairConfirm, setRepairConfirm] = useState('');

  const call = async (path, opts) => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}${path}`, { headers, ...opts });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || res.status); }
      return await res.json();
    } catch (e) { setError(e.message); return null; } finally { setLoading(false); }
  };

  const loadSummary = async () => { const d = await call('/api/admin/lifecycle/summary'); if (d) setSummary(d); };
  const loadConflicts = async () => { const d = await call('/api/admin/lifecycle/conflicts'); if (d) setConflicts(d); };
  const loadPreview = async () => { const d = await call('/api/admin/lifecycle/backfill-preview'); if (d) setPreview(d); };
  const loadEvents = async (lim) => { const d = await call(`/api/admin/lifecycle/events?limit=${lim || evtLimit}`); if (d) setEvents(d); };
  const doExecute = async () => {
    if (confirm !== 'BACKFILL LIFECYCLE') return;
    const d = await call(`/api/admin/lifecycle/backfill-execute?confirmation=${encodeURIComponent(confirm)}`, { method: 'POST' });
    if (d) { setExecResult(d); setPreview(null); setConfirm(''); loadSummary(); }
  };
  // Repair functions
  const loadRepairPreview = async () => { const d = await call('/api/admin/lifecycle/repair/stale-active-orphaned/preview'); if (d) setRepairPreview(d); };
  const doRepair = async () => {
    if (repairConfirm !== 'REPAIR ORPHANED LIFECYCLE') return;
    const d = await call(`/api/admin/lifecycle/repair/stale-active-orphaned/execute?confirmation=${encodeURIComponent(repairConfirm)}`, { method: 'POST' });
    if (d) { setRepairResult(d); setRepairPreview(null); setRepairConfirm(''); loadSummary(); loadConflicts(); }
  };

  const TABS = [
    { id: 'summary', label: 'Summary' },
    { id: 'conflicts', label: 'Conflicts' },
    { id: 'backfill', label: 'Backfill' },
    { id: 'repair', label: 'Repair' },
    { id: 'events', label: 'Events' },
  ];

  const Pill = ({ children }) => (
    <span style={{ padding: '2px 8px', background: '#1e1b4b11', borderRadius: 4, border: '1px solid #4338ca44', color: '#a78bfa', fontSize: 9, fontWeight: 600 }}>{children}</span>
  );

  const Stat = ({ label, value, color = '#e5e5e5' }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
      <span style={{ color: '#71717a', fontSize: 11 }}>{label}</span>
      <span style={{ color, fontSize: 12, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>{value ?? '—'}</span>
    </div>
  );

  return (
    <div style={{ marginTop: 12, background: '#0a0a14', border: '1px solid #1e1b4b44', borderRadius: 8, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #1e1b4b33' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Database size={14} color="#a78bfa" />
          <span style={{ color: '#a78bfa', fontSize: 13, fontWeight: 700 }}>Media Lifecycle Foundation</span>
          <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: '#1e1b4b', border: '1px solid #4338ca66', color: '#c4b5fd' }}>PR 1</span>
        </div>
      </div>

      {/* Safety pills */}
      <div style={{ padding: '10px 18px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {['Lifecycle metadata only', 'No S3 operations', 'No file deletion', 'Diagnostics remain safety source'].map(w => (
          <Pill key={w}>{w}</Pill>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ padding: '0 18px 8px', display: 'flex', gap: 6 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '5px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            border: tab === t.id ? '1px solid #7c3aed' : '1px solid #27272a',
            background: tab === t.id ? '#2e1065' : '#0a0a0a',
            color: tab === t.id ? '#c4b5fd' : '#525252',
          }}>{t.label}</button>
        ))}
      </div>

      {error && <div style={{ margin: '0 18px 8px', padding: '6px 10px', background: '#1c0a0a', border: '1px solid #991b1b44', borderRadius: 4, color: '#f87171', fontSize: 11 }}>{error}</div>}

      <div style={{ padding: '4px 18px 14px' }}>

        {/* ── Summary Tab ── */}
        {tab === 'summary' && (
          <div>
            <button onClick={loadSummary} disabled={loading} style={lcBtn}>{loading ? 'Loading…' : 'Load Summary'}</button>
            {summary && (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <div style={lcBox}>
                    <div style={lcBoxTitle}>All Assets</div>
                    {summary.all_assets && Object.entries(summary.all_assets.distribution || {}).map(([k, v]) => (
                      <Stat key={k} label={k} value={v} color={k === 'missing' ? '#f59e0b' : k === 'active' ? '#4ade80' : k === 'deleted' ? '#f87171' : '#e5e5e5'} />
                    ))}
                    <Stat label="Total" value={summary.all_assets?.total} color="#a78bfa" />
                  </div>
                  <div style={lcBox}>
                    <div style={lcBoxTitle}>Confirmed Only</div>
                    {summary.confirmed_only && Object.entries(summary.confirmed_only.distribution || {}).map(([k, v]) => (
                      <Stat key={k} label={k} value={v} color={k === 'missing' ? '#f59e0b' : k === 'active' ? '#4ade80' : '#e5e5e5'} />
                    ))}
                    <Stat label="Total" value={summary.confirmed_only?.total} color="#a78bfa" />
                  </div>
                  <div style={lcBox}>
                    <div style={lcBoxTitle}>By Status</div>
                    {summary.by_status && Object.entries(summary.by_status).map(([k, v]) => (
                      <Stat key={k} label={k} value={v} />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Conflicts Tab ── */}
        {tab === 'conflicts' && (
          <div>
            <button onClick={loadConflicts} disabled={loading} style={lcBtn}>{loading ? 'Loading…' : 'Load Conflicts'}</button>
            {conflicts && (
              <div style={{ marginTop: 10 }}>
                <Stat label="Total Conflicts" value={conflicts.conflict_count} color={conflicts.conflict_count > 0 ? '#f87171' : '#4ade80'} />
                {conflicts.conflicts && conflicts.conflicts.length > 0 ? (
                  <div style={{ marginTop: 8, maxHeight: 300, overflowY: 'auto' }}>
                    {conflicts.conflicts.map((c, i) => (
                      <div key={i} style={{ padding: '6px 10px', borderBottom: '1px solid #1f1f23', fontSize: 11 }}>
                        <span style={{ color: '#f59e0b', fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }}>{c.conflict_type}</span>
                        <span style={{ color: '#525252', marginLeft: 8 }}>{c.asset_id?.slice(0, 16)}…</span>
                        <div style={{ color: '#3f3f46', fontSize: 10, marginTop: 2 }}>{c.details}</div>
                      </div>
                    ))}
                  </div>
                ) : conflicts.conflict_count === 0 && (
                  <div style={{ color: '#4ade80', fontSize: 11, marginTop: 6 }}>No conflicts detected.</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Backfill Tab ── */}
        {tab === 'backfill' && (
          <div>
            <button onClick={loadPreview} disabled={loading} style={lcBtn}>{loading ? 'Loading…' : 'Run Backfill Preview (dry-run)'}</button>
            <div style={{ color: '#525252', fontSize: 10, marginTop: 4 }}>Preview only — no database writes.</div>

            {preview && (
              <div style={{ marginTop: 10 }}>
                <div style={lcBox}>
                  <Stat label="Total Assets" value={preview.total_assets} />
                  <Stat label="Already Set" value={preview.already_set} color="#4ade80" />
                  <Stat label="→ deleted" value={preview.set_deleted} color="#f87171" />
                  <Stat label="→ active (referenced)" value={preview.set_active_referenced} color="#4ade80" />
                  <Stat label="→ active (preset)" value={preview.set_active_preset} color="#4ade80" />
                  <Stat label="→ unused (orphaned)" value={preview.set_unused_orphaned} color="#fbbf24" />
                  <Stat label="→ unused (unreferenced)" value={preview.set_unused_unreferenced} color="#fbbf24" />
                  <Stat label="Needs Review" value={preview.needs_review} color="#f59e0b" />
                  <Stat label="Errors" value={preview.errors} color={preview.errors > 0 ? '#f87171' : '#525252'} />
                  {preview.dry_run && <div style={{ color: '#a78bfa', fontSize: 10, fontWeight: 600, marginTop: 6 }}>DRY RUN — no writes performed</div>}
                </div>

                {preview.needs_review_samples && preview.needs_review_samples.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ color: '#71717a', fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Needs Review Samples:</div>
                    {preview.needs_review_samples.map((s, i) => (
                      <div key={i} style={{ color: '#525252', fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                        {s.asset_id?.slice(0, 20)}… status={s.status} user={s.user_id || '?'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Execute */}
            <div style={{ marginTop: 14, padding: '12px', background: '#0a0a0a', border: '1px solid #27272a', borderRadius: 6 }}>
              <div style={{ color: '#71717a', fontSize: 11, marginBottom: 6 }}>
                Type <code style={{ color: '#c4b5fd', background: '#1e1b4b', padding: '1px 5px', borderRadius: 3 }}>BACKFILL LIFECYCLE</code> to execute:
              </div>
              <input value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Type confirmation…"
                style={{ width: '100%', padding: '8px 10px', background: '#111113', border: `1px solid ${confirm === 'BACKFILL LIFECYCLE' ? '#22c55e' : '#27272a'}`, borderRadius: 4, color: '#e5e5e5', fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", marginBottom: 8, boxSizing: 'border-box' }} />
              <button onClick={doExecute} disabled={confirm !== 'BACKFILL LIFECYCLE' || loading} style={{
                padding: '6px 16px', borderRadius: 4, border: 'none', fontSize: 12, fontWeight: 700, cursor: confirm === 'BACKFILL LIFECYCLE' ? 'pointer' : 'not-allowed',
                background: confirm === 'BACKFILL LIFECYCLE' ? '#7c3aed' : '#1a1a1a', color: confirm === 'BACKFILL LIFECYCLE' ? '#fff' : '#3f3f46',
              }}>{loading ? 'Executing…' : 'Execute Backfill'}</button>
            </div>

            {execResult && (
              <div style={{ marginTop: 10, padding: '10px 12px', background: '#052e16', border: '1px solid #16653466', borderRadius: 6 }}>
                <div style={{ color: '#4ade80', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Backfill Complete</div>
                <Stat label="Set deleted" value={execResult.set_deleted} />
                <Stat label="Set active (ref)" value={execResult.set_active_referenced} />
                <Stat label="Set active (preset)" value={execResult.set_active_preset} />
                <Stat label="Set unused (orphaned)" value={execResult.set_unused_orphaned} />
                <Stat label="Set unused (unref)" value={execResult.set_unused_unreferenced} />
                <Stat label="Already set" value={execResult.already_set} color="#4ade80" />
                <Stat label="Needs review" value={execResult.needs_review} color="#f59e0b" />
                <Stat label="Errors" value={execResult.errors} color={execResult.errors > 0 ? '#f87171' : '#525252'} />
              </div>
            )}
          </div>
        )}

        {/* ── Repair Tab ── */}
        {tab === 'repair' && (
          <div>
            <button onClick={loadRepairPreview} disabled={loading} style={lcBtn}>{loading ? 'Loading…' : 'Preview Stale Active-Orphaned'}</button>
            <div style={{ color: '#525252', fontSize: 10, marginTop: 4 }}>Find confirmed assets marked active by legacy backfill that are orphaned with no live references.</div>

            {repairPreview && (
              <div style={{ marginTop: 10 }}>
                <div style={lcBox}>
                  <Stat label="Total Candidates" value={repairPreview.total_candidates} color={repairPreview.total_candidates > 0 ? '#f59e0b' : '#4ade80'} />
                  {repairPreview.dry_run && <div style={{ color: '#a78bfa', fontSize: 10, fontWeight: 600, marginTop: 6 }}>DRY RUN — no writes performed</div>}
                </div>

                {repairPreview.candidates && repairPreview.candidates.length > 0 && (
                  <div style={{ marginTop: 8, maxHeight: 300, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #27272a' }}>
                          {['Asset ID', 'Kind', 'Current', 'Proposed', 'Orphaned At'].map(h => (
                            <th key={h} style={{ padding: '4px 6px', color: '#525252', fontWeight: 600, textAlign: 'left', textTransform: 'uppercase', fontSize: 9 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {repairPreview.candidates.map((c, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #1f1f23' }}>
                            <td style={evtTd}><span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{c.asset_id?.slice(0, 14)}…</span></td>
                            <td style={evtTd}>{c.kind}</td>
                            <td style={evtTd}><span style={{ color: '#4ade80' }}>active</span><span style={{ color: '#3f3f46' }}> / {c.lifecycle_reason}</span></td>
                            <td style={evtTd}><span style={{ color: '#fbbf24' }}>{c.proposed?.lifecycle_state}</span><span style={{ color: '#3f3f46' }}> / {c.proposed?.lifecycle_reason}</span></td>
                            <td style={evtTd}>{c.orphaned_at?.slice(0, 10)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {repairPreview.total_candidates === 0 && (
                  <div style={{ color: '#4ade80', fontSize: 11, marginTop: 8 }}>No stale active-orphaned candidates found.</div>
                )}
              </div>
            )}

            {/* Execute repair */}
            {repairPreview && repairPreview.total_candidates > 0 && (
              <div style={{ marginTop: 14, padding: '12px', background: '#0a0a0a', border: '1px solid #27272a', borderRadius: 6 }}>
                <div style={{ color: '#71717a', fontSize: 11, marginBottom: 6 }}>
                  Type <code style={{ color: '#c4b5fd', background: '#1e1b4b', padding: '1px 5px', borderRadius: 3 }}>REPAIR ORPHANED LIFECYCLE</code> to execute:
                </div>
                <div style={{ color: '#525252', fontSize: 10, marginBottom: 8 }}>Each candidate is revalidated before repair. No S3 operations. No status change. Only lifecycle metadata + audit event.</div>
                <input value={repairConfirm} onChange={e => setRepairConfirm(e.target.value)} placeholder="Type confirmation…"
                  style={{ width: '100%', padding: '8px 10px', background: '#111113', border: `1px solid ${repairConfirm === 'REPAIR ORPHANED LIFECYCLE' ? '#22c55e' : '#27272a'}`, borderRadius: 4, color: '#e5e5e5', fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", marginBottom: 8, boxSizing: 'border-box' }} />
                <button onClick={doRepair} disabled={repairConfirm !== 'REPAIR ORPHANED LIFECYCLE' || loading} style={{
                  padding: '6px 16px', borderRadius: 4, border: 'none', fontSize: 12, fontWeight: 700, cursor: repairConfirm === 'REPAIR ORPHANED LIFECYCLE' ? 'pointer' : 'not-allowed',
                  background: repairConfirm === 'REPAIR ORPHANED LIFECYCLE' ? '#7c3aed' : '#1a1a1a', color: repairConfirm === 'REPAIR ORPHANED LIFECYCLE' ? '#fff' : '#3f3f46',
                }}>{loading ? 'Repairing…' : 'Execute Repair'}</button>
              </div>
            )}

            {repairResult && (
              <div style={{ marginTop: 10, padding: '10px 12px', background: repairResult.errors > 0 ? '#1c0a0a' : '#052e16', border: `1px solid ${repairResult.errors > 0 ? '#991b1b44' : '#16653466'}`, borderRadius: 6 }}>
                <div style={{ color: repairResult.errors > 0 ? '#f87171' : '#4ade80', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Repair Complete</div>
                <Stat label="Repaired" value={repairResult.repaired} color="#4ade80" />
                <Stat label="Skipped" value={repairResult.skipped} color="#fbbf24" />
                <Stat label="Errors" value={repairResult.errors} color={repairResult.errors > 0 ? '#f87171' : '#525252'} />
                {repairResult.skipped_details && repairResult.skipped_details.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ color: '#71717a', fontSize: 10, fontWeight: 600 }}>Skipped:</div>
                    {repairResult.skipped_details.map((s, i) => (
                      <div key={i} style={{ color: '#525252', fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
                        {s.asset_id?.slice(0, 14)}… — {s.reason}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Events Tab ── */}
        {tab === 'events' && (
          <div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={() => loadEvents(evtLimit)} disabled={loading} style={lcBtn}>{loading ? 'Loading…' : 'Load Events'}</button>
              <select value={evtLimit} onChange={e => setEvtLimit(Number(e.target.value))} style={{ padding: '4px 8px', background: '#0a0a0a', border: '1px solid #27272a', borderRadius: 4, color: '#a3a3a3', fontSize: 11 }}>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
            {events && (
              <div style={{ marginTop: 10 }}>
                <Stat label="Events Loaded" value={events.event_count} />
                {events.events && events.events.length > 0 ? (
                  <div style={{ marginTop: 8, maxHeight: 350, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #27272a' }}>
                          {['Asset', 'From', 'To', 'Reason', 'Source', 'Time'].map(h => (
                            <th key={h} style={{ padding: '4px 6px', color: '#525252', fontWeight: 600, textAlign: 'left', textTransform: 'uppercase', fontSize: 9 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {events.events.map((e, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #1f1f23' }}>
                            <td style={evtTd}><span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{e.asset_id?.slice(0, 14)}…</span></td>
                            <td style={evtTd}><span style={{ color: '#71717a' }}>{e.from_state || '—'}</span></td>
                            <td style={evtTd}><span style={{ color: e.to_state === 'active' ? '#4ade80' : e.to_state === 'deleted' ? '#f87171' : '#fbbf24' }}>{e.to_state}</span></td>
                            <td style={evtTd}>{e.reason}</td>
                            <td style={evtTd}>{e.source}</td>
                            <td style={evtTd}>{e.created_at?.slice(0, 19)?.replace('T', ' ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ color: '#525252', fontSize: 11, marginTop: 6 }}>No lifecycle events recorded yet.</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const lcBtn = { padding: '5px 14px', background: '#1e1b4b', border: '1px solid #4338ca66', borderRadius: 4, color: '#c4b5fd', fontSize: 11, fontWeight: 600, cursor: 'pointer' };
const lcBox = { background: '#0a0a0a', border: '1px solid #1f1f23', borderRadius: 6, padding: '10px 12px' };
const lcBoxTitle = { color: '#a78bfa', fontSize: 11, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' };
const evtTd = { padding: '5px 6px', color: '#a3a3a3' };


// ── Styles ──────────────────────────────────────────────────────────────

const styles = {
  loginContainer: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', background: '#0a0a0a',
  },
  loginCard: {
    background: '#141415', border: '1px solid #262626', borderRadius: 12,
    padding: 32, textAlign: 'center', width: 340,
  },
  loginTitle: { color: '#e5e5e5', fontSize: 18, margin: '12px 0 20px' },
  loginInput: {
    width: '100%', padding: '10px 12px', background: '#0a0a0a',
    border: '1px solid #333', borderRadius: 6, color: '#e5e5e5',
    fontSize: 14, marginBottom: 12, outline: 'none', boxSizing: 'border-box',
  },
  loginButton: {
    width: '100%', padding: '10px 0', background: '#f59e0b',
    border: 'none', borderRadius: 6, color: '#000', fontWeight: 600,
    fontSize: 14, cursor: 'pointer',
  },

  page: { maxWidth: 1400 },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 16,
  },
  title: { color: '#e5e5e5', fontSize: 22, fontWeight: 700, margin: 0 },
  subtitle: { color: '#525252', fontSize: 12, fontFamily: "'IBM Plex Mono', monospace" },
  refreshBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', background: '#1a1a1b', border: '1px solid #333',
    borderRadius: 6, color: '#a3a3a3', fontSize: 12, cursor: 'pointer',
    fontFamily: "'IBM Plex Mono', monospace",
  },

  readOnlyBanner: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 16px', marginBottom: 20,
    background: '#1c1917', border: '1px solid #854d0e44',
    borderRadius: 8, color: '#d4a574', fontSize: 13,
  },

  errorBanner: {
    background: '#ef4444' + '22', border: '1px solid #ef444466', borderRadius: 6,
    padding: '10px 14px', color: '#fca5a5', fontSize: 13, marginBottom: 16,
    wordBreak: 'break-word', lineHeight: 1.5,
  },

  totalsRow: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12, marginBottom: 20,
  },
  totalCard: {
    background: '#141415', border: '1px solid #262626', borderRadius: 8,
    padding: '14px 18px',
  },
  totalLabel: { color: '#737373', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 },
  totalValue: { color: '#e5e5e5', fontSize: 24, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", marginTop: 4 },

  cardGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 12, marginBottom: 20,
  },
  summaryCard: {
    background: '#141415', border: '1px solid #262626', borderRadius: 8,
    padding: '14px 16px', textAlign: 'left',
    fontFamily: 'Inter, system-ui, sans-serif',
  },

  diagnosticRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 0', marginBottom: 12,
  },

  tableCard: {
    background: '#141415', border: '1px solid #262626', borderRadius: 8,
    padding: 16, marginBottom: 16,
  },

  tableContainer: { overflowX: 'auto' },
  table: {
    width: '100%', borderCollapse: 'collapse', fontSize: 12,
    fontFamily: "'IBM Plex Mono', monospace",
  },
  th: {
    textAlign: 'left', padding: '8px 10px', color: '#737373',
    borderBottom: '1px solid #262626', fontSize: 10, textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  td: { padding: '7px 10px', color: '#a3a3a3', borderBottom: '1px solid #1a1a1b', fontSize: 11 },
  tdKey: {
    padding: '7px 10px', color: '#a3a3a3', borderBottom: '1px solid #1a1a1b',
    maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    fontSize: 10,
  },
  trEven: { background: '#0f0f10' },

  keyText: {
    fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
    color: '#737373', maxWidth: 300, overflow: 'hidden',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block',
  },
  previewBtn: {
    background: 'none', border: '1px solid #262626', borderRadius: 4,
    cursor: 'pointer', color: '#525252', padding: '2px 4px',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },

  dupeGroup: {
    background: '#0f0f10', border: '1px solid #1a1a1b', borderRadius: 8,
    marginBottom: 8, overflow: 'hidden',
  },
  dupeHeader: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 12px', width: '100%',
    background: 'none', border: 'none', cursor: 'pointer',
    textAlign: 'left', fontFamily: 'Inter, system-ui, sans-serif',
  },

  configCard: {
    background: '#141415', border: '1px solid #1a1a1b', borderRadius: 8,
    padding: 16, marginTop: 20,
  },
  configItem: {
    display: 'flex', gap: 8, fontSize: 11,
    fontFamily: "'IBM Plex Mono', monospace",
    padding: '4px 10px', background: '#0a0a0a',
    borderRadius: 4, border: '1px solid #1f1f1f',
  },

  // PR 8A.6a: Tabs + Confirmation Modal
  tabBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', background: '#1a1a1b', border: '1px solid #333',
    borderRadius: 6, color: '#a3a3a3', fontSize: 12, cursor: 'pointer',
    fontFamily: "'IBM Plex Mono', monospace", whiteSpace: 'nowrap',
  },
  tabBtnActive: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', background: '#f59e0b' + '22', border: '1px solid #f59e0b' + '66',
    borderRadius: 6, color: '#f59e0b', fontSize: 12, cursor: 'pointer',
    fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, whiteSpace: 'nowrap',
  },
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  confirmModalCard: {
    background: '#141415', border: '1px solid #262626', borderRadius: 12,
    padding: 28, width: 460, maxHeight: '80vh', overflow: 'auto', position: 'relative',
    textAlign: 'center',
  },
  modalCard: {
    background: '#141415', border: '1px solid #262626', borderRadius: 12,
    padding: 24, width: 520, maxHeight: '80vh', overflow: 'auto', position: 'relative',
  },
  modalTitle: {
    color: '#e5e5e5', fontSize: 15, fontWeight: 700, margin: '0 0 12px',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  modalInput: {
    width: '100%', padding: '8px 12px', background: '#0a0a0a',
    color: '#e5e5e5', border: '1px solid #404040', borderRadius: 6,
    fontSize: 12, boxSizing: 'border-box', fontFamily: "'IBM Plex Mono', monospace",
  },
  modalCancelBtn: {
    padding: '7px 14px', background: '#262626', color: '#d4d4d4',
    border: '1px solid #404040', borderRadius: 6, cursor: 'pointer', fontSize: 12,
  },
  modalConfirmBtn: {
    padding: '7px 14px', background: '#0c4a6e', color: '#7dd3fc',
    border: '1px solid #075985', borderRadius: 6, fontSize: 12, fontWeight: 600,
  },
};
